//purchase
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from '../../Component/Layout';
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import CharacteristicsModal from "../../Component/CharacteristicsModal ";
import { useFieldPermissions } from "../../utils/useFieldPermissions"; // ⚠️ ADJUST — match your actual hooks path
import { authHeaders } from "../../utils/authHeaders";
import MaskedValue from "../../Component/MaskedValue"; // ⚠️ ADJUST — match your actual MaskedValue path
import { FileText } from 'lucide-react';

// ── Row tint by posting state (same light-tint style as the expired rows) ──
// green = fully posted, yellow = partly posted, red = not posted
const POSTING_ROW_BG = {
  POSTED:     "#dcfce7",
  PARTIAL:    "#fef9c3",
  NOT_POSTED: "#fde8e8",
};


// react-select compact style used throughout
const rsCompact = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px", position: "absolute" }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

// Rendered outside the modal's scroll container and flips above the field
// automatically when there isn't room below (e.g. near the bottom of a modal).
const rsMenuProps = {
  menuPlacement: "auto",
  menuPosition: "fixed",
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
};

const rsFilter = {
  ...rsCompact,
  control: (base) => ({
    ...rsCompact.control(base),
    height: "30px",
    minHeight: "30px",
    fontSize: "12px",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "12px", position: "absolute" }),
};

// Wraps a field so hovering ANYWHERE over it (not just directly over the
// truncated text) shows the full value in a styled tooltip below it.
// Pass tooltip="" / undefined to render the field with no tooltip behavior.
const FieldWithTooltip = ({ tooltip, children }) => (
  <div
    className="po-field-tooltip"
    data-tooltip={tooltip || ""}
    style={{ position: "relative" }}
  >
    {children}
  </div>
);


// ─────────────────────────────────────────────────────────────
// DateInputDDMMYYYY — always displays/picks dates as dd/mm/yyyy,
// regardless of the browser/OS locale (native <input type="date">
// cannot be forced to a fixed format, which is why it looked
// different on different laptops). Stores/returns "YYYY-MM-DD",
// same as native type="date", so it's a drop-in replacement.
// ─────────────────────────────────────────────────────────────
const ddmmyyyy_pad2 = (n) => String(n).padStart(2, "0");

const ddmmyyyy_isoToDisplay = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
};

const ddmmyyyy_isoToParts = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) };
};

const DDMMYYYY_DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DDMMYYYY_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DateInputDDMMYYYY = ({
  value,
  onChange,
  disabled = false,
  className = "form-control",
  style = {},
  placeholder = "dd/mm/yyyy",
  min, // optional "YYYY-MM-DD"
  max, // optional "YYYY-MM-DD"
}) => {
  const [open, setOpen] = useState(false);
  const parts = ddmmyyyy_isoToParts(value);
  const today = new Date();
  const [viewYear, setViewYear] = useState(parts ? parts.year : today.getFullYear());
  const [viewMonth, setViewMonth] = useState(parts ? parts.month : today.getMonth());
  const wrapRef = useRef(null);
  const popupRef = useRef(null);
  const CALENDAR_WIDTH = 220;
  const CALENDAR_HEIGHT = 250; // approx rendered height, used to decide flip-above
  const [coords, setCoords] = useState(null); // { top, left, width, placement }

  useEffect(() => {
    const p = ddmmyyyy_isoToParts(value);
    if (p) {
      setViewYear(p.year);
      setViewMonth(p.month);
    }
  }, [value]);

  // Compute a fixed-position anchor for the portalled calendar, flipping
  // above the field when there isn't enough room below (e.g. near the
  // bottom of a modal), so it's never clipped by the modal's own
  // overflow/scroll container or stuck behind other tab content.
  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < CALENDAR_HEIGHT && rect.top > CALENDAR_HEIGHT;
    setCoords({
      top: placeAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      placement: placeAbove ? "above" : "below",
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScrollOrResize = () => reposition();
    // capture:true so this also fires for scroll on the modal's inner
    // scrollable container, not just the window
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        popupRef.current && !popupRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const selectedParts = ddmmyyyy_isoToParts(value);

  const pickDay = (day) => {
    const iso = `${viewYear}-${ddmmyyyy_pad2(viewMonth + 1)}-${ddmmyyyy_pad2(day)}`;
    if (min && iso < min) return;
    if (max && iso > max) return;
    onChange && onChange(iso);
    setOpen(false);
  };

  const goPrevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };

  const goNextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange && onChange("");
    setOpen(false);
  };

  // Build calendar grid cells
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startWeekday = firstOfMonth.getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const calendarPopup = open && !disabled && coords && createPortal(
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        top: coords.placement === "above" ? undefined : coords.top,
        bottom: coords.placement === "above" ? (window.innerHeight - coords.top) : undefined,
        left: coords.left,
        zIndex: 100000,
        background: "#fff",
        border: "1px solid #ced4da",
        borderRadius: 6,
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        padding: 10,
        width: CALENDAR_WIDTH,
        fontSize: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button
          type="button"
          onClick={goPrevMonth}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}
        >
          ‹
        </button>
        <strong style={{ fontSize: 12 }}>
          {DDMMYYYY_MONTH_NAMES[viewMonth]} {viewYear}
        </strong>
        <button
          type="button"
          onClick={goNextMonth}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}
        >
          ›
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center", marginBottom: 4 }}>
        {DDMMYYYY_DAY_NAMES.map((dn) => (
          <div key={dn} style={{ color: "#64748b", fontWeight: 600, padding: "2px 0" }}>
            {dn}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center", rowGap: 2 }}>
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} />;
          const iso = `${viewYear}-${ddmmyyyy_pad2(viewMonth + 1)}-${ddmmyyyy_pad2(day)}`;
          const isSelected =
            selectedParts &&
            selectedParts.year === viewYear &&
            selectedParts.month === viewMonth &&
            selectedParts.day === day;
          const isDisabledDay = (min && iso < min) || (max && iso > max);
          return (
            <div
              key={idx}
              onClick={() => !isDisabledDay && pickDay(day)}
              style={{
                padding: "4px 0",
                borderRadius: 4,
                cursor: isDisabledDay ? "not-allowed" : "pointer",
                color: isDisabledDay ? "#cbd5e1" : isSelected ? "#fff" : "#1e293b",
                background: isSelected ? "#0d6efd" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!isSelected && !isDisabledDay) e.currentTarget.style.background = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.background = "transparent";
              }}
            >
              {day}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <button
          type="button"
          onClick={() => {
            const now = new Date();
            const iso = `${now.getFullYear()}-${ddmmyyyy_pad2(now.getMonth() + 1)}-${ddmmyyyy_pad2(now.getDate())}`;
            if ((min && iso < min) || (max && iso > max)) return;
            onChange && onChange(iso);
            setOpen(false);
          }}
          style={{
            border: "1px solid #ced4da",
            background: "#fff",
            borderRadius: 4,
            fontSize: 11,
            padding: "3px 8px",
            cursor: "pointer",
          }}
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            border: "1px solid #ced4da",
            background: "#fff",
            borderRadius: 4,
            fontSize: 11,
            padding: "3px 8px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          readOnly
          disabled={disabled}
          className={className}
          style={{ ...style, cursor: disabled ? "not-allowed" : "pointer", background: disabled ? "#e9ecef" : (style.background || "#fff") }}
          value={ddmmyyyy_isoToDisplay(value)}
          placeholder={placeholder}
          onClick={() => !disabled && setOpen((o) => !o)}
        />
        {!disabled && value && (
          <span
            onClick={clearDate}
            title="Clear date"
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#94a3b8",
              fontSize: 13,
              lineHeight: 1,
            }}
          >
            ✕
          </span>
        )}
      </div>

      {calendarPopup}
    </div>
  );
};

const USER_MANUAL_SCOPE = {
    moduleName: "Procurement",
    process: "Purchase",
    subProcess: "Purchase Order"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;


function getCurrentUser() {
  // ⚠️ FIX: this used to return `parsedUser.email` from *inside* the
  // `if (userObj)` block the moment userId/fullName weren't found on the
  // parsed "user" object. That meant the separate localStorage "userId" key
  // below was only ever consulted when "user" was completely missing or
  // failed to parse — i.e. never, in practice, since "user" is set on every
  // login. So even a valid standalone userId key was being ignored and the
  // function fell straight through to email. Fixed by gathering every
  // candidate first and then picking by priority, so the standalone userId
  // key is checked BEFORE any name/email fallback, regardless of what's on
  // the "user" object.
  let parsedUser = null;
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      parsedUser = JSON.parse(userObj);
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
  }

  // Prefer the full name (first + last) for Created By / Approved By display.
  // The backend stamps the real name on save, so this only affects what is
  // shown on a brand-new PO before it is saved.
  const fullName = `${parsedUser?.firstName || ''} ${parsedUser?.lastName || ''}`.trim();
  if (fullName) return fullName;

  if (parsedUser?.userId) return parsedUser.userId;

  const storedUserId = localStorage.getItem("userId");
  if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;

  const storedName = localStorage.getItem("userName");
  if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;

  // Last resort only — no userId or name available anywhere.
  if (parsedUser?.email) return parsedUser.email;
  return "";
}


// 🆕 IMPORT PO — LANDED COST — 5 top-level tabs (Material / Logistics /
// Customs / Port-Clearing / Bank), same row as Basic/Product/Confirmation.
// `key` maps 1:1 to the `landed_${key}` column added on purchase_orders
// (see ensurePoLandedCostColumns in purchaseOrder.service.js) and to the
// same-named field sent in productDetails on save.
const LANDED_COST_GROUPS = [
  {
    group: "Customs",
    tabId: "landed_customs",
    perLineItem: false, // 🆕 single entry for the whole PO — not repeated per product line item, and no Product Code column
    fields: [],
  },
  {
    group: "Port/Clearing",
    tabId: "landed_port_clearing",
    perLineItem: false, // 🆕 single entry for the whole PO — not repeated per product line item, and no Product Code column
    fields: [
      { key: "cha_cf_charges", label: "CHA/C&F Charges" },
      { key: "port_charges", label: "Port Charges" },
      { key: "documentation_charges", label: "Documentation Charges" },
      { key: "demurrage", label: "Demurrage" },
      { key: "detention", label: "Detention" },
    ],
  },
  {
    group: "Bank",
    tabId: "landed_bank",
    perLineItem: false, // 🆕 single entry for the whole PO — not repeated per product line item, and no Product Code column
    fields: [
      { key: "lc_charges", label: "LC Charges" },
      { key: "bank_charges", label: "Bank Charges" },
      { key: "forex_charges", label: "Forex Charges" },
    ],
  },
];

const LANDED_COST_TAB_IDS = LANDED_COST_GROUPS.map(g => g.tabId);

// Unit Cost is always kept to 3 decimal places.
const round2 = (v) => Math.round((Number(v || 0) + Number.EPSILON) * 1000) / 1000; // 3 dp (name kept to avoid touching call sites)

const PurchaseOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Role-based field/module permissions for this module ──────────────
  // DISPLAY-role users get per-field masking (isFieldVisible); everyone
  // else (admin, no-role-assigned, CREATE/CHANGE/REVIEW/CONVERT) sees real
  // values per useFieldPermissions' own rules. hasModuleAccess / permission
  // helpers below gate which actions/tabs render at all.
  const {
    loading: permLoading,
    isAdmin,
    hasRole,
    permissionTypes: poPermissionTypes,
    hasModuleAccess,
    isReviewOnly,
    isConvertOnly,
    isFieldVisible
  } = useFieldPermissions("PURCHASE_ORDER");

  // Convenience flags mirroring the CREATE/CHANGE meaning documented in
  // authController.js / Role.js for this module's permission types.
  const canCreatePO = isAdmin || !hasRole || (poPermissionTypes || []).includes("CREATE");
  const canEditPO = isAdmin || !hasRole || (poPermissionTypes || []).includes("CHANGE");

  const [poList, setPoList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [createActiveTab, setCreateActiveTab] = useState("basic");
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendors, setVendors] = useState([]);
  // 🆕 Supplier-conflict resolution modal — replaces the old non-blocking
  // toast.warning() when products with different default (PIR) vendors are
  // mixed into one PO. Shape: { options: [{ name, code }, ...] } | null.
  // "options" is the deduped list of conflicting vendors to choose from;
  // the modal also lets the user pick any OTHER supplier from the full
  // `vendors` list instead of one of the conflicting ones.
  const [vendorConflictModal, setVendorConflictModal] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [calculatedAmounts, setCalculatedAmounts] = useState({});
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState('');
  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Driven by the Navbar alert bell: clicking "Purchase Order Validity
  // Expired" navigates here with { showExpiredOnly: true }. expiredKeys
  // holds the purchase_order_id of every PO /po/validity/expired currently
  // flags — used both to filter the list down to just those rows and to
  // highlight them below. Same pattern as PurchaseRequsition.js / SalesOrder.js.
  const [showExpiredOnly, setShowExpiredOnly] = useState(!!location.state?.showExpiredOnly);
  const [expiredKeys, setExpiredKeys] = useState(new Set());

  // Re-check location.state on every navigation (not just mount) so
  // clicking the bell while already sitting on this page still works —
  // same reasoning as PurchaseRequsition.js's equivalent effect. Keyed off
  // location.key rather than location.state itself, same as Home.js.
  useEffect(() => {
    if (location.state?.showExpiredOnly) {
      setShowExpiredOnly(true);
      // Consume the navigation flag so it doesn't live on in browser
      // history — otherwise a plain refresh replays this same state and
      // re-applies the expired-only filter forever, with no way to land
      // back on a normal, unfiltered page load. Same fix as Home.js.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Backs the Navbar alert bell (and the "Purchase Order Validity Expired"
  // highlight/filter below) — GET /po/validity/expired, keyed by
  // purchase_order_id. Fetched once on mount, same as Purchase Requisition.
  useEffect(() => {
    const fetchExpiredValidity = async () => {
      try {
        const res = await axios.get(`${PURCHASE_API}/po/validity/expired`, { headers: authHeaders() });
        const rows = res.data || [];
        setExpiredKeys(new Set(rows.map(r => r.purchase_order_id)));
      } catch (err) {
        console.error("Failed to load expired validity list:", err);
      }
    };
    fetchExpiredValidity();
  }, []);

  // eslint-disable-next-line no-unused-vars
  const [paymentTerms, setPaymentTerms] = useState('');

  // 🆕 Terms & Conditions — a per-PO override, unlike Payment Terms this is
  // NEVER auto-filled from the vendor/business partner; the user types it
  // directly. Persisted separately via GET/PUT /po/:poId/terms (backed by
  // po_terms_conditions, see purchaseOrder.service.js), not part of the
  // main PO header save, since that table/endpoint already exists for this
  // purpose and purchase_orders has no column for it.
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [modeOfTransport, setModeOfTransport] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [freightTerms, setFreightTerms] = useState("");
  const [insuranceTerms, setInsuranceTerms] = useState("");
  const [transporter, setTransporter] = useState("");
  const [poHeaderLotType, setPoHeaderLotType] = useState("");
  const [productDetails, setProductDetails] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [discounts, setDiscounts] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [comment, setComment] = useState("");
  const [actionType, setActionType] = useState("");
  const [commentModal, setCommentModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [confirmationData, setConfirmationData] = useState(modalData);
  const [editableProducts, setEditableProducts] = useState([]);
  const [selectedPurchaseOrganization, setSelectedPurchaseOrganization] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [uploadedDocs, setUploadedDocs] = useState([]);
  // PO document upload size limit — configurable via
  // Configuration → File Size Config (Module = "Purchase Order").
  // Falls back to 50 KB (the old implicit/no-limit default) if it can't be fetched.
  const DEFAULT_MAX_PO_DOC_SIZE_KB = 50;
  const [maxPoDocSizeKb, setMaxPoDocSizeKb] = useState(DEFAULT_MAX_PO_DOC_SIZE_KB);
  const [filterVendor, setFilterVendor] = useState("");
  const [selectedPoStatus, setSelectedPoStatus] = useState("");
  // Product Type filter for the list-page filter bar (separate from
  // poProductType, which is the Product Type picked inside the Create/Edit PO modal).
  const [selectedProductType, setSelectedProductType] = useState(null);

  // List-page Category / Sub Category / Product Code filters (same as the
  // Purchase Requisition list). Named list* so they don't collide with
  // filterCategory/filterSubCategory, which belong to the Create/Edit modal.
  const [listCategory, setListCategory] = useState(null);
  const [listSubCategory, setListSubCategory] = useState(null);
  const [listProductCode, setListProductCode] = useState(null);

  // ── Cable attribute filters (mirrors Stock Report / Purchase Requisition:
  // Construction, Code, Cable Type, Category, Cores, Color, Cross Section,
  // Std. Ref, Voltage) ──
  const [selectedConstruction, setSelectedConstruction] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedCableType, setSelectedCableType] = useState(null);
  const [selectedCableCategory, setSelectedCableCategory] = useState(null);
  const [selectedCores, setSelectedCores] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedCrossSection, setSelectedCrossSection] = useState(null);
  const [selectedStdRef, setSelectedStdRef] = useState(null);
  const [selectedVoltage, setSelectedVoltage] = useState(null);

  // Shared product master list (Construction..Voltage) — same endpoint
  // Stock Report / BOM use, so these filter options are computed from it
  // client-side instead of needing separate backend endpoints per field.
  const [cableAttrProducts, setCableAttrProducts] = useState([]);

  const [isPoSaved, setIsPoSaved] = useState(false);
  const [poLocation, setPoLocation] = useState("")
  const [locationOptions, setLocationOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  // ── Product filter state ──────────────────────────────
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubCategory, setFilterSubCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [poCreatedDate, setPoCreatedDate] = useState("");
const [poApprovedDate, setPoApprovedDate] = useState("");

  const [purchaseOrganizations,
    setPurchaseOrganizations] =
    useState([]);
  //eslint-disable-next-line no-unused-vars
  const [createPurchasePlanner, setCreatePurchasePlanner] = useState("");
  //eslint-disable-next-line no-unused-vars
  const [createPurchaseOrganization, setCreatePurchaseOrganization] = useState("");

  const [purchasePlanners,
    setPurchasePlanners] =
    useState([]);

  const [modeOfTransportOptions, setModeOfTransportOptions] = useState([]);
  const [deliveryTypeOptions, setDeliveryTypeOptions] = useState([]);
  const [freightTermsOptions, setFreightTermsOptions] = useState([]);
  // Payment Terms dropdown — options come from Configuration > Payment Terms
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [insuranceTermsOptions, setInsuranceTermsOptions] = useState([]);
  const [transporterOptions, setTransporterOptions] = useState([]);
  const [poHeaderLotTypeOptions, setPoHeaderLotTypeOptions] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [poProductType, setPoProductType] = useState("");

  // 🆕 ORDER TYPE — Domestic PO / Import PO. When Import PO is selected,
  // an extra "Import Details" header section (and extra item columns on
  // the Product tab) become visible.
  const [poOrderType, setPoOrderType] = useState("domestic");
  const orderTypeOptions = [
    { value: "domestic", label: "Domestic PO" },
    { value: "import", label: "Import PO" },
  ];

  // 🆕 IMPORT PO — header-level fields (only relevant when poOrderType === "import")
  const [importSupplierCountry, setImportSupplierCountry] = useState("");

  // 🆕 Dynamic tax column headings for the Product tab's 3 tax columns.
  // Domestic POs always show CGST/SGST/IGST (the historical behavior).
  // Import POs replace these with whatever tax_type(s) were actually
  // configured on the GST Configuration screen for this HSN + Supplier
  // Country (e.g. just "IGST", or a custom type like "BCD"), so the
  // heading the user sees always matches what's really being charged
  // instead of an assumed CGST/SGST/IGST split. Reset back to the
  // defaults whenever GST can't resolve (no country/HSN match yet).
  const DEFAULT_GST_COLUMN_LABELS = { col1: "CGST", col2: "SGST", col3: "IGST" };
  const [gstColumnLabels, setGstColumnLabels] = useState(DEFAULT_GST_COLUMN_LABELS);

  // Given the raw `taxes: [{ type, percent }]` array gst_configuration
  // returns for a product, produce { col1, col2, col3 } labels — used only
  // for Import POs. Falls back to defaults when there's nothing to show.
  const deriveGstColumnLabels = (taxesByProduct) => {
    for (const code of Object.keys(taxesByProduct || {})) {
      const taxes = taxesByProduct[code]?.taxes;
      if (taxes && taxes.length > 0) {
        return {
          col1: taxes[0]?.type || DEFAULT_GST_COLUMN_LABELS.col1,
          col2: taxes[1]?.type || "",
          col3: taxes[2]?.type || "",
        };
      }
    }
    return DEFAULT_GST_COLUMN_LABELS;
  };

  const [importCurrency, setImportCurrency] = useState("");
  const [importExchangeRate, setImportExchangeRate] = useState("");

  // 🆕 Currency master (Configuration > Currency) — supplies the symbol that
  // goes with each currency, so amounts are no longer hardcoded to ₹.
  const [currencyMaster, setCurrencyMaster] = useState([]);
  useEffect(() => {
    axios
      .get(`${MASTER_API}/configuration/dynamic/currency`, { headers: authHeaders() })
      .then((res) => setCurrencyMaster(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Currency master error:", err));
  }, []);

  // Match an Import PO's currency (whatever the Exchange Rate Config / user
  // gave us — name like "Yuan" or code like "USD") against the master.
  // No match -> show the currency text itself rather than a wrong ₹.
  const resolveCurrencySymbol = (cur) => {
    const key = String(cur || "").trim().toLowerCase() || "inr";
    const row = currencyMaster.find(
      (r) =>
        String(r.name || "").trim().toLowerCase() === key ||
        String(r.currency_code || "").trim().toLowerCase() === key
    );
    // Symbol if configured, else whatever is in the master's Currency Code
    // column (some setups keep ₹ / $ / € there).
    const cfg = row?.currency_symbol || row?.currency_code;
    if (cfg) return cfg;
    if (key === "inr" || key === "rupee" || key === "indian rupee") return "₹";
    return String(cur).trim();
  };

  // 🆕 Per-line Currency dropdown options — straight from Configuration >
  // Currency. Value = currency_code (falls back to name). A row's current
  // currency is always kept as an option even if it's missing from the
  // master, so opening an old PO never blanks the cell.
  const currencyOptions = (current) => {
    const opts = [];
    const seen = new Set();
    currencyMaster.forEach((r) => {
      const v = String(r.currency_code || r.name || "").trim();
      if (!v || seen.has(v.toLowerCase())) return;
      seen.add(v.toLowerCase());
      opts.push(v);
    });
    const cur = String(current || "").trim();
    if (cur && !seen.has(cur.toLowerCase())) opts.unshift(cur);
    return opts;
  };

  const handleRowCurrencyChange = (idx, value) => {
    setModalData((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], currency: value };
      return next;
    });
    // 💤 Exchange rate NOT applied to Unit Cost / Price on currency change (for now).
    // When enabled later, convert editableProducts[idx].product_cost here.
  };

  // Domestic PO -> home currency (INR). Import PO -> the selected Currency.
  const currencySymbol =
    poOrderType === "import" && importCurrency
      ? resolveCurrencySymbol(importCurrency)
      : resolveCurrencySymbol("INR");
  const [importIncoterm, setImportIncoterm] = useState("");
  const [importPortOfLoading, setImportPortOfLoading] = useState("");
  const [importPortOfDischarge, setImportPortOfDischarge] = useState("");
  const [importFinalDestination, setImportFinalDestination] = useState("");
  const [importExpectedShipmentDate, setImportExpectedShipmentDate] = useState("");
  const [importExpectedArrivalDate, setImportExpectedArrivalDate] = useState("");
  const [importLcRequired, setImportLcRequired] = useState("No");
  const [importInsuranceRequired, setImportInsuranceRequired] = useState("No");

  //eslint-disable-next-line
  const [poCategory, setPoCategory] = useState("");
  //eslint-disable-next-line
  const [poSubCategory, setPoSubCategory] = useState("");
  const [poCreatedBy, setPoCreatedBy] = useState(getCurrentUser());
  // Logged-in user's "First Last", fetched from the backend (the cached
  // localStorage "user" object often has no firstName/lastName, which is why
  // the userId used to show up here).
  const [currentUserName, setCurrentUserName] = useState(getCurrentUser());
  useEffect(() => {
    axios
      .get(`${PURCHASE_API}/po/current-user`, { headers: authHeaders() })
      .then((r) => {
        const n = r.data?.name;
        if (!n) return;
        setCurrentUserName(n);
        // Only replace the placeholder shown on a fresh form — never a
        // created_by loaded from an existing PO.
        setPoCreatedBy((prev) => (!prev || prev === getCurrentUser() ? n : prev));
      })
      .catch(() => {});
  }, []);
  // 🆕 Optional manual PO Number typed in on Create — left blank, the
  // backend auto-generates one as before (see purchaseOrder_service.js).
  const [poManualNumber, setPoManualNumber] = useState("");

  // 🆕 PO Revision No. — shown beside PO Number, saved with the PO and
  // printed on the PO form. Starts at 0 for a new PO.
  const [poRevisionNo, setPoRevisionNo] = useState("0");

  // 🆕 Contact Details (mandatory) — Name is picked from Configuration >
  // Business Partner Responsible; Email and Contact No. fill in from it.
  const [poContactOptions, setPoContactOptions] = useState([]);
  const [poContactName, setPoContactName] = useState("");
  const [poContactEmail, setPoContactEmail] = useState("");
  const [poContactPhone, setPoContactPhone] = useState("");

  // 🆕 Rename PO Number (for an ALREADY-SAVED PO — e.g. one that got its
  // number auto-generated on PR conversion). Kept separate from
  // poManualNumber/the main Save-Update flow, since purchase_order_id is
  // the lookup key that flow uses to find the PO, not a field it patches.
  const [renamedPoNumber, setRenamedPoNumber] = useState("");
  const [savingPoNumber, setSavingPoNumber] = useState(false);
  const [poApprovedBy, setPoApprovedBy] = useState("");

  const [changeLogs, setChangeLogs] = useState([]);
  const [advancePayments, setAdvancePayments] = useState([]);
  const [paymentHistoryTabType, setPaymentHistoryTabType] = useState("Advance");
  const [paymentHistoryTabAmount, setPaymentHistoryTabAmount] = useState("");
  const [paymentHistoryTabDescription, setPaymentHistoryTabDescription] = useState("");
  const [paymentHistoryTabReference, setPaymentHistoryTabReference] = useState("");
  const [paymentHistoryTabDate, setPaymentHistoryTabDate] = useState(
    (() => {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    })()
  );
  const [editingPaymentId, setEditingPaymentId] = useState(null); // 🆕 id of the advance-payment row currently being edited (null = adding a new payment)

  // 🆕 ACKNOWLEDGEMENT tab
  const [acknowledgements, setAcknowledgements] = useState([]);
  const [ackNoInput, setAckNoInput] = useState("");
  const [ackDateInput, setAckDateInput] = useState(
    (() => {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    })()
  );
  const [editingAckId, setEditingAckId] = useState(null); // id of the acknowledgement row currently being edited (null = adding new)
  const [paymentTermNumber, setPaymentTermNumber] = useState("");
  //eslint-disable-next-line no-unused-vars
  const [paymentTermUnit, setPaymentTermUnit] = useState("days");
  const [quoteNo, setQuoteNo] = useState("");
  const [quoteDate, setQuoteDate] = useState("");
  const [isEditMode, setIsEditMode] = useState(false); // false = view mode, true = edit mode
  const [isSubmittingPO, setIsSubmittingPO] = useState(false); // guards against double-click save+submit
  const [listTab, setListTab] = useState("active"); // "active" | "closed" | "short_closed"

  // Which tab a PO row belongs to:
  //  - Short Closed: po_status is SHORT_CLOSED
  //  - Closed: GRN completely done (posting_status POSTED) and not short closed
  //  - Active: everything else
  const matchesListTab = (row) => {
    const ps = (row.po_status || row.status || "").toUpperCase();
    const isShortClosed = ps === "SHORT_CLOSED";
    const isClosed = !isShortClosed && row.posting_status === "POSTED";
    if (listTab === "short_closed") return isShortClosed;
    if (listTab === "closed") return isClosed;
    return !isShortClosed && !isClosed;
  };
  const [deletedLineItemIds, setDeletedLineItemIds] = useState([]); // track deleted row DB ids for save

  // 🐛 FIX: remember which saved PO is open, independent of row order.
  // Save used to decide create-vs-update from modalData[0] alone, so once
  // the first row was deleted (or replaced by a newly-added row with no
  // purchase_order_id) the save went to create-purchase-order and produced
  // a brand-new PO, leaving notes/terms/etc. behind on the old one.
  const activePoIdRef = useRef("");
  useEffect(() => {
    const id = (modalData || []).find(r => r && r.purchase_order_id)?.purchase_order_id;
    if (id) activePoIdRef.current = id;
  }, [modalData]);
  const [showPODocInModal, setShowPODocInModal] = useState(false);

  // ── Characteristics modal state ──────────────────────────────
  const [modalVisible, setModalVisible] = useState(false);
  // In-app Short Close confirmation dialog (replaces window.confirm)
  const [showShortCloseConfirm, setShowShortCloseConfirm] = useState(false);
  const [charModalRowIdx, setCharModalRowIdx] = useState(null);
  //eslint-disable-next-line
  const [charModalSource, setCharModalSource] = useState(null); // "product"
  const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
  // class_code -> Set of characteristic names configured under that class
  // (Product Class vs Purchase Class each have their own characteristic
  // list; this lets the ▶ icons show only the characteristics that
  // actually belong to the class they're next to, instead of the full
  // merged list coming back from the PIR / PO characteristics endpoint).
  const [classCharacteristicMap, setClassCharacteristicMap] = useState({});

  useEffect(() => {
    axios
      .get(`${MASTER_API}/product-config/getProductConfigs`)
      .then((res) => {
        const map = {};
        (res.data || []).forEach((cfg) => {
          map[cfg.class_code] = new Set(
            (cfg.characteristics || []).map((c) =>
              (c.characteristic || "").trim().toLowerCase()
            )
          );
        });
        setClassCharacteristicMap(map);
      })
      .catch(() => setClassCharacteristicMap({}));
  }, []);



  // Fixed dd/mm/yyyy hh:mm:ss formatter, always in Indian Standard Time
  // (Asia/Kolkata), regardless of the viewer's device timezone/locale or the
  // UTC value sent by the server. Used for Change Log / Docs "Date & Time".
  const formatChangeLogDateTime = (dateInput) => {
    if (!dateInput) return "-";
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(d.getTime())) return "-";
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const hh = parts.hour === '24' ? '00' : parts.hour;
    return `${parts.day}/${parts.month}/${parts.year} ${hh}:${parts.minute}:${parts.second}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // If already in YYYY-MM-DD format, return as-is to avoid UTC timezone shift
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // For ISO strings like "2024-05-15T00:00:00.000Z", extract the date part directly
    // without constructing a Date object (which would shift due to UTC vs local timezone)
    if (typeof dateStr === "string" && dateStr.includes("T")) {
      return dateStr.split("T")[0];
    }
    // Fallback: use local date methods (not UTC) to avoid -1 day in IST (UTC+5:30)
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Converts a YYYY-MM-DD string to DD/MM/YYYY for display purposes only.
  // Leaves the underlying state (used for saving/API calls) untouched.
  const formatDateDMY = (dateStr) => {
    if (!dateStr) return "";
    const isoDate = formatDate(dateStr); // normalize to YYYY-MM-DD first
    const parts = isoDate.split("-");
    if (parts.length !== 3) return isoDate;
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  };

  // Returns today's date as YYYY-MM-DD in LOCAL timezone (not UTC)
  const getTodayLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [confirmationLines, setConfirmationLines] = useState({});

  // 🆕 IMPORT PO — Landed Cost tab. Keyed by rowKey (row.id for saved
  // lines, `idx_${idx}` for lines not yet saved) -> { [fieldKey]: value }.
  // Same keying pattern as confirmationLines above.
  const [landedCosts, setLandedCosts] = useState({});
  const [confirmQtys, setConfirmQtys] = useState(modalData?.map(row => row.confirm_qty || '') || []);
  const [confirmDates, setConfirmDates] = useState(modalData?.map(row => formatDate(row.confirm_date)) || []);
  const [deliverQtys, setDeliverQtys] = useState(modalData?.map(row => row.deliver_qty || '') || []);
  const [deliverDates, setDeliverDates] = useState(modalData?.map(row => formatDate(row.deliver_date)) || []);
  const [receivingDates, setReceivingDates] = useState(modalData?.map(row => formatDate(row.receiving_date)) || []);
  const [transportationDates, setTransportationDates] = useState(modalData?.map(row => formatDate(row.transportation_date)) || []);
  const [packingDates, setPackingDates] = useState(modalData?.map(row => formatDate(row.packing_date)) || []);
  const [pickingDates, setPickingDates] = useState(modalData?.map(row => formatDate(row.picking_date)) || []);
  const [madDates, setMadDates] = useState(modalData?.map(row => formatDate(row.mad)) || []);




  const fetchLogs = async (poId) => {
    try {
      const res = await axios.get(`${PURCHASE_API}/po/logs/${poId}`, { headers: authHeaders() });
      if (res.data.success) {
        setChangeLogs(res.data.logs);
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
    }
  };

 const fetchAdvancePayments = async (poId) => {
  try {
    const res = await axios.get(`${PURCHASE_API}/po/advance-payments/${poId}`, { headers: authHeaders() });

    console.log(res.data.advancePayments);

    if (res.data.success) {
      setAdvancePayments(res.data.advancePayments);
    }
  } catch (err) {
    console.error("Error fetching advance payments:", err);
  }
};

// 🆕 Load saved acknowledgement rows for this PO, and populate the
// Acknowledgement No / Date box with the most recent saved row so the
// box always reflects the exact saved value instead of sitting empty.
const fetchAcknowledgements = async (poId) => {
  try {
    const res = await axios.get(`${PURCHASE_API}/po/acknowledgements/${poId}`, { headers: authHeaders() });

    if (res.data.success) {
      const rows = res.data.acknowledgements || [];
      setAcknowledgements(rows);

      if (rows.length > 0) {
        // Don't assume API ordering — pick the row with the newest date,
        // falling back to the last row in the array if dates are missing/equal.
        const latest = rows.reduce((best, row) => {
          if (!best) return row;
          const bestTime = new Date(best.acknowledgement_date || 0).getTime();
          const rowTime = new Date(row.acknowledgement_date || 0).getTime();
          return rowTime >= bestTime ? row : best;
        }, null);
        setAckNoInput(latest.acknowledgement_no || "");
        setAckDateInput(
          latest.acknowledgement_date ? String(latest.acknowledgement_date).slice(0, 10) : ackDateInput
        );
        // 🆕 Track which saved row this is so Save updates it (PUT) instead
        // of creating a duplicate (POST) — the row-level edit button is gone
        // now that the history table has been removed.
        setEditingAckId(latest.id);
      } else {
        // 🆕 This PO has no saved acknowledgement rows — clear the box
        // instead of leaving the previous PO's values sitting there.
        setAckNoInput("");
        setAckDateInput("");
        setEditingAckId(null);
      }
    }
  } catch (err) {
    console.error("Error fetching acknowledgements:", err);
  }
};

  const [navigatedFromGR, setNavigatedFromGR] = useState(false);

  // Auto-open modal when navigated from Goods Receipt page with a PO number in state
  useEffect(() => {
    if (location.state?.openPoNo) {
      setNavigatedFromGR(true);
      viewPO(location.state.openPoNo);
      // Clear router state so a page refresh doesn't re-open the modal
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (modalData?.length) {
      setEditableProducts(prev => {
        // Only initialize rows that don't exist yet — never overwrite existing edits
        const next = modalData.map((row, i) => {
          if (prev[i]) return prev[i]; // preserve existing edits
          return {
            product_cost: row.product_cost,
            base_cost_inr: row.base_cost_inr ?? row.product_cost, // 🆕 for exchange-rate re-conversion
            requirement_qty: row.requirement_qty,
            delivery_date: formatDate(row.delivery_date),
          };
        });
        return next;
      });
    }
  }, [modalData]);

  useEffect(() => {
    fetch(`${MASTER_API}/configuration/file-size-limit/${encodeURIComponent("Purchase Order")}`, { headers: authHeaders() })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        if (data && data.maxSizeKb) setMaxPoDocSizeKb(data.maxSizeKb);
      })
      .catch(() => { /* keep default limit if config fetch fails */ });
  }, []);

  // Fetch persisted docs list from the server whenever the Docs tab is
  // opened for a saved PO (has a real purchase_order_id).
  useEffect(() => {
    const poId = modalData?.[0]?.purchase_order_id || "";
    if (createActiveTab === "docs" && poId) {
      fetchUploadedDocs(poId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createActiveTab, modalData]);

  // 🔧 Terms & Conditions is now a plain column on purchase_orders
  // (terms_and_conditions), saved together with the rest of the PO header
  // exactly like Payment Terms — no separate po_terms_conditions table or
  // /po/:id/terms endpoint needed. Loading it happens in the main PO-load
  // effect alongside payment_terms (see setTermsAndConditions there);
  // saving happens via the same "Save" button that saves the whole PO.

  // Splits a FileList/array into files within the limit and files that exceed it,
  // and toasts a warning listing any rejected files. Same pattern as
  // SalesOrder.js's filterFilesWithinSizeLimit — keep both in sync.
  const filterFilesWithinSizeLimit = (files) => {
    const maxBytes = maxPoDocSizeKb * 1024;
    const okFiles = [];
    const rejected = [];
    files.forEach(f => {
      if (f.size > maxBytes) {
        rejected.push(f.name);
      } else {
        okFiles.push(f);
      }
    });
    if (rejected.length) {
      toast.error(
        `${rejected.length} file(s) exceed the ${maxPoDocSizeKb} KB upload limit and were skipped: ${rejected.join(', ')}`
      );
    }
    return okFiles;
  };

  const [docsUploading, setDocsUploading] = useState(false);

  const fetchUploadedDocs = (poId) => {
    if (!poId) { setUploadedDocs([]); return; }
    axios.get(`${PURCHASE_API}/po/list-docs/${encodeURIComponent(poId)}`, { headers: authHeaders() })
      .then(res => setUploadedDocs(res.data?.files || []))
      .catch(() => setUploadedDocs([]));
  };

  const handleDocUpload = async (e) => {
    const rawFiles = Array.from(e.target.files);
    e.target.value = ""; // allow re-selecting the same file name later

    const poId = modalData?.[0]?.purchase_order_id || "";
    if (!poId) {
      toast.error("Save the PO before attaching documents.");
      return;
    }

    const okFiles = filterFilesWithinSizeLimit(rawFiles);
    if (!okFiles.length) return;

    const formData = new FormData();
    okFiles.forEach((f) => formData.append("docs", f));

    setDocsUploading(true);
    try {
      await axios.post(
        `${PURCHASE_API}/po/upload-docs/${encodeURIComponent(poId)}`,
        formData,
        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
      );
      fetchUploadedDocs(poId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to upload document(s)");
    } finally {
      setDocsUploading(false);
    }
  };

  const handleDocDownload = async (doc) => {
    const poId = modalData?.[0]?.purchase_order_id || "";
    if (!poId || !doc.filename) return;
    try {
      const res = await fetch(
        `${PURCHASE_API}/po/download-doc/${encodeURIComponent(poId)}/${encodeURIComponent(doc.filename)}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.originalname || doc.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Failed to download file");
    }
  };

  useEffect(() => {

    // PURCHASE ORDERS

    axios
      .get(`${PURCHASE_API}/po/purchase-orders`, { headers: authHeaders() })
      .then((res) => {

        if (res.data.success) {

          setAllPurchaseOrders(
            res.data.purchaseOrders
          );

          setPoList(
            res.data.purchaseOrders
          );

          setPurchaseOrders(
            res.data.purchaseOrders
          );
        }

      })
      .catch((err) =>
        console.error(
          "Error fetching purchase orders:",
          err
        )
      );

    // VENDORS

    axios
      .get(`${PURCHASE_API}/po/vendors`, { headers: authHeaders() })
      .then((res) => {
        // API returns [{name, id}] directly
        const list = Array.isArray(res.data) ? res.data : [];
        setVendors(list);
      })
      .catch((err) =>
        console.error(
          "Error fetching vendors:",
          err
        )
      );

    // PURCHASE ORGANIZATION

    axios
      .get(
        `${MASTER_API}/configuration/purchase-orgs`
      )
      .then((res) => {
        // Handle {success, data:[]} or plain array
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        setPurchaseOrganizations(list);
      })
      .catch((err) =>
        console.error(
          "Purchase Org Error:",
          err
        )
      );

    // PURCHASE PLANNER

    axios
      .get(
        `${MASTER_API}/configuration/purchase-planners`
      )
      .then((res) => {
        // Handle {success, data:[]} or plain array
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        setPurchasePlanners(list);
      })
      .catch((err) =>
        console.error(
          "Purchase Planner Error:",
          err
        )
      );

    // LOCATION

    axios
      .get(
        `${MASTER_API}/configuration/location`
      )
      .then((res) => {

        // Handle both plain array and {success, data:[]} shape
        const locRaw = res.data;
        const locList = Array.isArray(locRaw) ? locRaw
          : Array.isArray(locRaw?.data) ? locRaw.data : [];
        setLocationOptions(locList);

      })
      .catch((err) =>
        console.error(
          "Location Error:",
          err
        )
      );

    // COUNTRY — same master used by the GST Configuration screen's
    // Country dropdown, so Supplier Country here can never drift from
    // what gst_configuration rows are actually keyed to.
    axios
      .get(
        `${MASTER_API}/configuration/country`
      )
      .then((res) => {
        const countryRaw = res.data;
        const countryList = Array.isArray(countryRaw) ? countryRaw
          : Array.isArray(countryRaw?.data) ? countryRaw.data : [];
        setCountryOptions(countryList);
      })
      .catch((err) =>
        console.error(
          "Country Error:",
          err
        )
      );




    axios.get(`${MASTER_API}/configuration/dynamic/mode_of_transport`, { headers: authHeaders() })
      .then(res => setModeOfTransportOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/delivery_type`, { headers: authHeaders() })
      .then(res => setDeliveryTypeOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/freight_terms`, { headers: authHeaders() })
      .then(res => setFreightTermsOptions(res.data || []));

    // Payment Terms master (Configuration > Payment Terms) — same list the
    // Quotation screen uses.
    axios.get(`${MASTER_API}/configuration/dynamic/payment-terms`, { headers: authHeaders() })
      .then(res => setPaymentTermsOptions(res.data || []))
      .catch((err) => console.error("Payment Terms Error:", err));

    axios.get(`${MASTER_API}/configuration/dynamic/insurance_terms`, { headers: authHeaders() })
      .then(res => setInsuranceTermsOptions(res.data || []));

    // Contact Details dropdown — Configuration > Business Partner Responsible
    // (returns id, name, phone_number, email).
    axios.get(`${MASTER_API}/configuration/dynamic/business-partner-responsible`, { headers: authHeaders() })
      .then(res => setPoContactOptions(res.data || []))
      .catch((err) => console.error("Contact Details master error:", err));

    axios.get(`${MASTER_API}/configuration/dynamic/transporter`, { headers: authHeaders() })
      .then(res => setTransporterOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/po_header_lot_type`, { headers: authHeaders() })
      .then(res => setPoHeaderLotTypeOptions(res.data || []));


  }, []);



  useEffect(() => {
    axios
      .get(
        `${PURCHASE_API}/po/product-details`,
        { headers: authHeaders() }
      )
      .then((res) => {

        setAllProducts(
          res.data || []
        );

      })
      .catch((err) =>
        console.error(err)
      );

    // Fetch category options from configuration
    axios
      .get(`${MASTER_API}/configuration/category`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setCategoryOptions(data);
      })
      .catch((err) => console.error("Category options error:", err));

    // Fetch sub-category options from configuration
    axios
      .get(`${MASTER_API}/configuration/sub-category`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setSubCategoryOptions(data);
      })
      .catch((err) => console.error("Sub-category options error:", err));

    // Fetch product type options
    // Purchase Order is the "Internal" side (same as Purchase Requisition) —
    // only Product Types tagged Internal in Product Master should appear
    // here. Previously called with no usage filter, so External-tagged
    // types (Semi Finished, Finished Goods) showed up here too.
    axios
      .get(`${MASTER_API}/configuration/product-types?usage=Internal`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setProductTypeOptions(data.map(i => ({ value: i.name || i, label: i.name || i })));
      })
      .catch((err) => console.error("Product type options error:", err));

    // ── Shared product master list (Construction..Voltage) — same endpoint
    // Stock Report / BOM use, so the cable-attribute filter options below are
    // computed from it client-side instead of needing separate endpoints. ──
    axios.get(`${MASTER_API}/bom/products`)
      .then(res => setCableAttrProducts(res.data.data || []))
      .catch(err => console.error("Failed loading products for cable attribute filters:", err.response?.data || err.message));

  }, []);



  // Normalizes a PO's raw status into the same display label shown in the
  // Status column, so the Status filter matches exactly what the user sees
  // in the table. Shared between fetchPOs (filtering) and the table cell.
  const getPoStatusLabel = (po) => {
    const s = (po.status || "").trim();
    const su = s.toUpperCase();
    if (su === "CREATED") return "Created";
    if (su === "APPROVED") return "Approved";
    if (su === "REJECTED") return "Rejected";
    if (s === "Partially Approved") return "Partially Approved";
    if (s === "Partially Rejected") return "Partially Rejected";
    if (su === "QUERY") return "Query";
    return "Pending for Approval";
  };

  const poStatusFilterOptions = [
    "Created",
    "Pending for Approval",
    "Approved",
    "Rejected",
    "Partially Approved",
    "Partially Rejected",
    "Query",
  ];

  // ── Cable attribute filter options (Construction..Voltage), computed off
  // the shared cableAttrProducts list — mirrors Stock Report/BatchList's
  // getCascadingOptions, but without cross-field cascading here since the PO
  // list filters at the summary-row level, not a full product table. ──
  const getCableAttrOptions = React.useCallback((key) => {
    const uniqueVals = Array.from(
      new Set(
        cableAttrProducts
          .map(p => p[key])
          .filter(v => v !== undefined && v !== null && v !== "")
          .map(v => String(v).trim())
          .filter(v => v !== "")
      )
    );
    return uniqueVals
      .map(v => ({ value: v, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cableAttrProducts]);

  const constructionOptions = React.useMemo(() => getCableAttrOptions("construction"), [getCableAttrOptions]);
  const codeOptions = React.useMemo(() => getCableAttrOptions("code"), [getCableAttrOptions]);
  const cableTypeOptions = React.useMemo(() => getCableAttrOptions("cableType"), [getCableAttrOptions]);
  const cableCategoryOptions = React.useMemo(() => getCableAttrOptions("category"), [getCableAttrOptions]);
  const coresOptions = React.useMemo(() => getCableAttrOptions("noOfCores"), [getCableAttrOptions]);
  const colorOptions = React.useMemo(() => getCableAttrOptions("color"), [getCableAttrOptions]);
  const crossSectionOptions = React.useMemo(() => getCableAttrOptions("nominalCrossSection"), [getCableAttrOptions]);
  const stdRefOptions = React.useMemo(() => getCableAttrOptions("referenceOfStandard"), [getCableAttrOptions]);
  const voltageOptions = React.useMemo(() => getCableAttrOptions("voltage"), [getCableAttrOptions]);

  // ── Category / Sub Category / Product Code options for the list filter
  // bar. Built from the POs themselves (so only values that actually exist
  // on a PO are offered) and scoped to the selected Product Type, exactly
  // like the Purchase Requisition list. ──
  const normType = (v) => (v || "").toString().trim().toLowerCase();

  const typeScopedPOs = React.useMemo(
    () => selectedProductType
      ? allPurchaseOrders.filter(po => normType(po.productType) === normType(selectedProductType.value))
      : allPurchaseOrders,
    [allPurchaseOrders, selectedProductType]
  );

  // Cable attributes only exist on cable products. For Raw Material and
  // other types show Category / Sub Category instead of nine empty dropdowns.
  const poHasCableAttrs = React.useMemo(
    () => typeScopedPOs.some(po =>
      ["construction", "code", "cableType", "noOfCores", "color",
       "nominalCrossSection", "referenceOfStandard", "voltage"]
        .some(k => po[k] !== undefined && po[k] !== null && String(po[k]).trim() !== "")
    ),
    [typeScopedPOs]
  );
  const showCableFilters = selectedProductType?.value !== "Asset" && poHasCableAttrs;

  const listCategoryOptions = React.useMemo(() => {
    const names = [...new Set(typeScopedPOs.flatMap(po => po.categories || []))];
    return names.sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [typeScopedPOs]);

  const listSubCategoryOptions = React.useMemo(() => {
    const names = [...new Set(
      typeScopedPOs
        .filter(po => !listCategory || (po.categories || []).includes(listCategory.value))
        .flatMap(po => po.sub_categories || [])
    )];
    return names.sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [typeScopedPOs, listCategory]);

  const listProductCodeOptions = React.useMemo(() => {
    const names = [...new Set(typeScopedPOs.flatMap(po => po.product_codes || []))];
    return names.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map(v => ({ value: v, label: v }));
  }, [typeScopedPOs]);

  const fetchPOs = () => {
    const filteredData = allPurchaseOrders.filter((po) => {
      const matchesPurchaseOrderId =
        selectedPurchaseOrderId ? po.purchase_order_id === selectedPurchaseOrderId : true;

      // Fix: compare against po.purchase_organization, not po.purchase_order_id
      const matchesPurchaseOrganization =
        selectedPurchaseOrganization
          ? (po.purchase_organization || "")
            .toLowerCase()
            .includes(selectedPurchaseOrganization.toLowerCase())
          : true;

      const matchesVendor = filterVendor
        ? po.vendor_name === filterVendor
        : true;

      const matchesStatus = selectedPoStatus
        ? getPoStatusLabel(po) === selectedPoStatus
        : true;

      const matchesProductType = selectedProductType
        ? po.productType === selectedProductType.value
        : true;

      const matchesListCategory = listCategory
        ? (po.categories || []).includes(listCategory.value)
        : true;

      const matchesListSubCategory = listSubCategory
        ? (po.sub_categories || []).includes(listSubCategory.value)
        : true;

      const matchesListProductCode = listProductCode
        ? (po.product_codes || []).includes(listProductCode.value)
        : true;

      // ── Cable attribute filters (Construction..Voltage) ──
      const matchesConstruction = selectedConstruction
        ? po.construction === selectedConstruction.value
        : true;

      const matchesCode = selectedCode
        ? po.code === selectedCode.value
        : true;

      const matchesCableType = selectedCableType
        ? po.cableType === selectedCableType.value
        : true;

      const matchesCableCategory = selectedCableCategory
        ? po.category === selectedCableCategory.value
        : true;

      const matchesCores = selectedCores
        ? String(po.noOfCores) === String(selectedCores.value)
        : true;

      const matchesColor = selectedColor
        ? po.color === selectedColor.value
        : true;

      const matchesCrossSection = selectedCrossSection
        ? String(po.nominalCrossSection) === String(selectedCrossSection.value)
        : true;

      const matchesStdRef = selectedStdRef
        ? po.referenceOfStandard === selectedStdRef.value
        : true;

      const matchesVoltage = selectedVoltage
        ? po.voltage === selectedVoltage.value
        : true;

      const matchesExpired = !showExpiredOnly || expiredKeys.has(po.purchase_order_id);

      return (
        matchesExpired &&
        matchesPurchaseOrderId &&
        matchesPurchaseOrganization &&
        matchesVendor &&
        matchesStatus &&
        matchesProductType &&
        matchesListCategory &&
        matchesListSubCategory &&
        matchesListProductCode &&
        matchesConstruction &&
        matchesCode &&
        matchesCableType &&
        matchesCableCategory &&
        matchesCores &&
        matchesColor &&
        matchesCrossSection &&
        matchesStdRef &&
        matchesVoltage
      );
    });

    setPoList(filteredData);
  };



  useEffect(() => {
    if (showModal && modalData?.length && !selectedVendor) {
      const vendor = vendors.find(
        v => v.name === modalData?.[0].vendor_name
      );
      if (vendor) setSelectedVendor(vendor.id);
    }
  }, [showModal, modalData, vendors, selectedVendor]);

  useEffect(() => {
    fetchPOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allPurchaseOrders,
    selectedPurchaseOrderId,
    selectedPurchaseOrganization,
    filterVendor,
    selectedPoStatus,
    selectedProductType,
    listCategory,
    listSubCategory,
    listProductCode,
    selectedConstruction,
    selectedCode,
    selectedCableType,
    selectedCableCategory,
    selectedCores,
    selectedColor,
    selectedCrossSection,
    selectedStdRef,
    selectedVoltage,
    showExpiredOnly,
    expiredKeys
  ]);

  const viewPO = async (poNo) => {
    try {
      const res = await axios.get(`${PURCHASE_API}/po/${poNo}`, { headers: authHeaders() });

      if (res.data && res.data.length > 0) {
        let poData = res.data;

        try {
          const pCodes = poData.map(item => item.product_code);
          const checkRes = await axios.post(`${PURCHASE_API}/po/check-default-vendor`, { productCodes: pCodes }, { headers: authHeaders() });

          if (checkRes.data && checkRes.data.commonVendorCode) {
            const { commonVendorCode, commonVendorName } = checkRes.data;

            const bpRes = await axios.get(`${PURCHASE_API}/po/business-partner/${commonVendorCode}`, { headers: authHeaders() });
            const vendorData = bpRes.data;

            poData = poData.map(row => {
              // 🐛 FIX: updatedCosts comes from purchase_info_records.productCost
              // — the vendor's CURRENT default/master cost, not what this PO line
              // actually has saved. This used to auto-refresh Domestic rows'
              // product_cost with that master value on every single view/reload,
              // which meant any manual Unit Cost edit (however carefully saved,
              // and correctly logged in the Change Log) got silently reverted
              // back to the vendor's default price the next time the PO was
              // opened — the PO would appear to "not remember" cost edits.
              // A saved line item's cost must always reflect what's actually in
              // the DB for that PO, never the vendor's live master price.
              // Import PO rows were already fixed to do this (see the
              // base_cost_inr comment below) — Domestic rows now match that
              // same behavior: row.product_cost, unconditionally, for every
              // existing saved line.
              const isImportRow = (row.order_type || "").toLowerCase() === "import";
              const rate = Number(row.exchange_rate) || 0;
              return {
                ...row,
                vendor_name: commonVendorName,
                product_cost: row.product_cost,
                // Back-derive the original INR cost from what's actually
                // stored (convertedCost / rate) so later exchange-rate edits
                // in this modal re-convert from the right base instead of
                // treating the foreign-currency figure as if it were INR.
                base_cost_inr: isImportRow && rate
                  ? Number(row.product_cost) / rate
                  : row.product_cost,
                contact_person: vendorData.contact_person_name || null,
                business_type: vendorData.business_type || null,
                phone: vendorData.phone || null,
                gstin: vendorData.gstin || null,
                address: vendorData.registered_address || null,
                payment_terms:
                  row.payment_terms ||
                  vendorData.terms_and_conditions ||
                  vendorData.payment_terms ||
                  null
              };
            });
          }
        } catch (err) {
          console.error("Auto Vendor check failed:", err);
        }

        setModalData(poData);
        setEditableProducts(poData.map(row => ({
          product_cost: row.product_cost,
          requirement_qty: row.requirement_qty,
          delivery_date: formatDate(row.delivery_date),
        })));

        // 🆕 Dynamic tax columns from what's ACTUALLY stored in
        // purchase_order_tax (poData[*].taxes, added by getPurchaseOrderById).
        // Previously View/Edit only ever read cgst_percent/sgst_percent/
        // igst_percent, which are always 0 for Import POs (their taxes are
        // named things like "Basic Customs Duty", not CGST/SGST/IGST), so
        // the modal showed CGST/SGST/IGST = 0.00 while the DB actually had
        // 3 real tax rows sitting unused. Deriving straight from row.taxes
        // means the grid always matches the DB, for both domestic and
        // import POs, the moment the modal opens — before the async
        // line-item-calc refresh below even runs.
        setGstColumnLabels(
          deriveGstColumnLabels(
            Object.fromEntries(poData.map(row => [row.id, { taxes: row.taxes || [] }]))
          )
        );
        setCalculatedAmounts(
          Object.fromEntries(poData.map((row, idx) => {
            const t = row.taxes || [];
            return [idx, {
              totalCost: row.total_cost,
              netPriceAfterDiscount: row.net_price_after_discount,
              cgst: Number(t[0]?.percent ?? row.cgst_percent ?? 0),
              sgst: Number(t[1]?.percent ?? row.sgst_percent ?? 0),
              igst: Number(t[2]?.percent ?? row.igst_percent ?? 0),
              cgstAmount: Number(t[0]?.value ?? row.cgst_amount ?? 0),
              sgstAmount: Number(t[1]?.value ?? row.sgst_amount ?? 0),
              igstAmount: Number(t[2]?.value ?? row.igst_amount ?? 0),
              totalGST: row.total_gst_amount,
              finalPriceAfterGST: row.final_price_after_gst,
              hsnCode: row.hsn_code,
              taxes: t,
            }];
          }))
        );

        // 🆕 Landed Cost tab — hydrate per-line-item groups (e.g. Customs)
        // from the landed_* columns on each saved line item, and PO-level
        // groups (Port/Clearing, Bank) once from the first row into the
        // "po_level" pseudo-key.
        const loadedLandedCosts = {};
        const perLineItemGroups = LANDED_COST_GROUPS.filter(g => g.perLineItem !== false);
        const poLevelGroups = LANDED_COST_GROUPS.filter(g => g.perLineItem === false);

        poData.forEach((row, idx) => {
          const rowKey = row.id || `idx_${idx}`;
          const entry = {};
          perLineItemGroups.forEach(g => g.fields.forEach(f => {
            const dbVal = row[`landed_${f.key}`];
            entry[f.key] = (dbVal === null || dbVal === undefined) ? "" : dbVal;
          }));
          loadedLandedCosts[rowKey] = entry;
        });

        if (poData.length > 0) {
          const poLevelEntry = {};
          poLevelGroups.forEach(g => g.fields.forEach(f => {
            const dbVal = poData[0][`landed_${f.key}`];
            poLevelEntry[f.key] = (dbVal === null || dbVal === undefined) ? "" : dbVal;
          }));
          loadedLandedCosts["po_level"] = poLevelEntry;
        }
        setLandedCosts(loadedLandedCosts);
        // NOTE: calculatedAmounts was already populated above from
        // poData[*].taxes (the actual stored DB values) — don't reset it
        // to {} here, or the grid would flash to 0.00 before the async
        // line-item-calc refresh below completes.
        setShowModal(true);

        // 🆕 Resolve GST for every saved line item right away, as its own
        // self-contained async block — NOT awaited inline with the rest of
        // viewPO(). Previously this same fetch lived at the very end of
        // this function; if anything earlier in viewPO() threw (a bad
        // field, a flaky request, etc.), the whole try block would abort
        // before ever reaching it, leaving CGST/SGST/IGST stuck at 0 even
        // though every other field loaded fine — matching exactly what
        // "View" (which calls the same endpoint on its own) could still
        // fix manually per row. Running it here, unawaited, means it
        // starts immediately and completes independently of anything else
        // in this function.
        (async () => {
          const newCalculatedAmounts = {};
          await Promise.all(poData.map(async (row, idx) => {
            const lineItemId = row.id;
            const discount = row.discount || 0;
            const currentCost = row.product_cost;

            if (!lineItemId) return;

            try {
              const prodRes = await fetch(
                `${PURCHASE_API}/po/line-item-calc/${lineItemId}?discount=${discount}&cost=${currentCost}`
              , { headers: authHeaders() });

              if (prodRes.ok) {
                const data = await prodRes.json();

                // 🛠 FIX: the column HEADERS (gstColumnLabels) are always
                // derived positionally from taxes[0]/[1]/[2] — that's what
                // makes them dynamic (e.g. a single "IGST" column when only
                // IGST applies). The AMOUNTS must be filled into the exact
                // same positions, or the header and the number under it
                // stop describing the same tax. Previously, whenever
                // taxes[0].type happened to be literally "CGST"/"SGST"/
                // "IGST" (true for every Domestic row), the amounts were
                // instead pulled from the NAMED data.cgst/sgst/igst fields
                // — which don't necessarily line up with taxes[] order. For
                // an interstate Domestic row (only IGST applicable),
                // taxes[0] = IGST => header col1 = "IGST", but
                // data.cgst (=0) got written into slot 1 and data.igst
                // (the real amount) got written into slot 3, under a blank
                // header. Always mapping positionally keeps header and
                // value locked together, for both Domestic and Import.
                const t = data.taxes || [];

                newCalculatedAmounts[idx] = {
                  totalCost: data.totalCost, netPriceAfterDiscount: data.netPriceAfterDiscount,
                  cgst: Number(t[0]?.percent ?? 0),
                  sgst: Number(t[1]?.percent ?? 0),
                  igst: Number(t[2]?.percent ?? 0),
                  cgstAmount: Number(t[0]?.value ?? (data.netPriceAfterDiscount * (t[0]?.percent ?? 0) / 100).toFixed(2)),
                  sgstAmount: Number(t[1]?.value ?? (data.netPriceAfterDiscount * (t[1]?.percent ?? 0) / 100).toFixed(2)),
                  igstAmount: Number(t[2]?.value ?? (data.netPriceAfterDiscount * (t[2]?.percent ?? 0) / 100).toFixed(2)),
                  totalGST: data.totalGST, finalPriceAfterGST: data.finalPriceAfterGST, hsnCode: data.hsnCode,
                  taxes: t,
                };

                setGstColumnLabels({
                  col1: t[0]?.type || DEFAULT_GST_COLUMN_LABELS.col1,
                  col2: t[1]?.type || "",
                  col3: t[2]?.type || "",
                });
              } else {
                console.error(`GST calc request failed for line item ${lineItemId}: HTTP ${prodRes.status}`);
              }
            } catch (err) {
              console.error(`Failed to fetch GST details for Item ID ${lineItemId}`, err);
            }
          }));

          setCalculatedAmounts(prev => ({ ...prev, ...newCalculatedAmounts }));
        })();

        const poStatus = (poData[0].status || "").toUpperCase();
        // Auto-edit for new converted POs (PENDING) and for POs still in
        // CREATED status — these open directly editable since there's no
        // separate Edit step before Submit for them.
        setIsEditMode(poStatus === "PENDING" || poStatus === "CREATED");
        setDeletedLineItemIds([]); // reset on every open

        const isSaved = !!poData[0].vendor_name;
        setIsPoSaved(isSaved);
        fetchLogs(poData[0].purchase_order_id);
        fetchAdvancePayments(poData[0].purchase_order_id);
        fetchAcknowledgements(poData[0].purchase_order_id);

        // 🔧 FIX: Payment Terms is a plain manual field saved on the PO's
        // own payment_terms column — it must never be auto-filled from
        // business_partner.terms_and_conditions (that's Terms & Conditions
        // text, not a payment term). No business-partner fallback here
        // anymore; that value now feeds Terms & Conditions below instead.
        const pTerms = poData[0].payment_terms || "";
        setPaymentTermNumber(pTerms);
        setPaymentTerms(pTerms);
        setPaymentTermUnit("");

        // Terms & Conditions: read the PO's own saved value (plain column,
        // saved/loaded exactly like payment_terms). If this PO has never
        // had one saved, fall back to the vendor's
        // business_partner.terms_and_conditions so the field isn't blank —
        // it gets written to the db for real the next time the PO is saved.
        const tConditions = poData[0].terms_and_conditions || "";
        setTermsAndConditions(tConditions);
        if (!tConditions) {
          try {
            const vendorCode = poData[0].vendor_code || poData[0].vendor;
            const vendorName = poData[0].vendor_name || "";
            const lookupId = vendorCode || vendors.find(v =>
              v.name?.toLowerCase() === vendorName?.toLowerCase()
            )?.id;
            if (lookupId) {
              const bpRes = await axios.get(`${PURCHASE_API}/po/business-partner/${lookupId}`, { headers: authHeaders() });
              const bpTerms = bpRes.data?.terms_and_conditions || "";
              if (bpTerms) setTermsAndConditions(bpTerms);
            }
          } catch (e) { /* ignore — user can still type it manually */ }
        }
        setPoLocation(poData[0].location || poData[0].country_of_origin || "");
        setModeOfTransport(poData[0].mode_of_transport || "");
        setDeliveryType(poData[0].delivery_type || "");
        setFreightTerms(poData[0].freight_terms || "");
        setInsuranceTerms(poData[0].insurance_terms || "");
        setTransporter(poData[0].transporter || "");
        setPoHeaderLotType(poData[0].po_header_lot_type || "");

        setVendorDetails({
          business_partner_name: poData[0].vendor_name,
          vendor_name: poData[0].vendor_name,
          contact_person_name: poData[0].contact_person,
          business_type: poData[0].business_type,
          phone: poData[0].phone,
          gstin: poData[0].gstin,
          registered_address: poData[0].address,
        });



        setQuoteNo(poData[0].quote_no || "");
        setPoRevisionNo(
          poData[0].po_revision_no !== null && poData[0].po_revision_no !== undefined && poData[0].po_revision_no !== ""
            ? String(poData[0].po_revision_no)
            : "0"
        );
        setPoContactName(poData[0].contact_name || "");
        setPoContactEmail(poData[0].contact_email || "");
        setPoContactPhone(poData[0].contact_phone || "");

        setQuoteDate(
          poData[0].quote_date
            ? formatDate(poData[0].quote_date)
            : ""
        );

        setPoProductType(poData[0].product_type || poData[0].productType || "");

        // 🆕 Order Type + Import PO header fields
        setPoOrderType(poData[0].order_type || poData[0].orderType || "domestic");
        setImportSupplierCountry(poData[0].supplier_country || "");
        setImportCurrency(poData[0].import_currency || "");
        setImportExchangeRate(poData[0].exchange_rate || "");
        setImportIncoterm(poData[0].incoterm || "");
        setImportPortOfLoading(poData[0].port_of_loading || "");
        setImportPortOfDischarge(poData[0].port_of_discharge || "");
        setImportFinalDestination(poData[0].final_destination || "");
        setImportExpectedShipmentDate(
          poData[0].expected_shipment_date ? formatDate(poData[0].expected_shipment_date) : ""
        );
        setImportExpectedArrivalDate(
          poData[0].expected_arrival_date ? formatDate(poData[0].expected_arrival_date) : ""
        );
        setImportLcRequired(poData[0].lc_required || "No");
        setImportInsuranceRequired(poData[0].insurance_required || "No");

        setPoCategory(poData[0].category || poData[0].productCategory || "");
        setPoSubCategory(poData[0].sub_category || poData[0].subCategory || "");
        setPoCreatedBy(poData[0].created_by || getCurrentUser());
setPoCreatedDate(
  poData[0].created_date
    ? formatDate(poData[0].created_date)
    : getTodayLocal()
);
const poStatusForApproval = (poData[0].status || "").toUpperCase();
setPoApprovedBy(
  (poStatusForApproval === "APPROVED")
    ? (poData[0].approved_by || poData[0].approver_id || "")
    : ""
);
setPoApprovedDate(
  (poStatusForApproval === "APPROVED")
    ? (poData[0].approved_date ? formatDate(poData[0].approved_date) : "")
    : ""
);

        // Init confirmation lines from schedule_line JSON
        const initLines = {};
        poData.forEach((row, rowIndex) => {
          const key = row.id || `idx_${rowIndex}`;
          let lines = [];
          try {
            lines = row.schedule_line ? JSON.parse(row.schedule_line) : [];
          } catch (e) { lines = []; }
          if (!lines.length) {
            lines = [{ schedule_line: "", confirm_qty: row.confirm_qty || "", confirm_date: formatDate(row.confirm_date) || "", deliver_qty: row.deliver_qty || "", deliver_date: formatDate(row.deliver_date) || "" }];
          }
          initLines[key] = lines;
        });
        setConfirmationLines(initLines);

        // Set selectedVendor: use vendor_code (from po.vendor column), else match by name
        const vendorIdOnPO = poData[0].vendor_code || poData[0].vendor || "";
        let resolvedVendorId = vendorIdOnPO;
        if (vendorIdOnPO) {
          setSelectedVendor(vendorIdOnPO);
        } else {
          const matchedVendor = vendors.find(v =>
            v.name === poData[0].vendor_name ||
            v.name?.toLowerCase() === poData[0].vendor_name?.toLowerCase()
          );
          resolvedVendorId = matchedVendor ? matchedVendor.id : "";
          setSelectedVendor(resolvedVendorId);
        }

        // 🆕 If this PO was saved before GST was correctly resolved (e.g.
        // cgst_percent/sgst_percent/igst_percent — or the taxes[] rows —
        // are all 0 in the DB), the calculatedAmounts hydration above just
        // carried that stale 0 straight into the grid. calculateLineTotals()
        // has nothing better to fall back to (row.cgst_percent is also 0),
        // so the Product tab shows ₹0 CGST/SGST for the entire edit session
        // — right up until Submit, when the backend independently
        // recalculates GST from gst_configuration and the correct amount
        // finally shows up on reload. Re-run that same resolution here,
        // once, on load, so the preview matches what Submit is about to
        // save instead of only refreshing when the user re-touches the
        // vendor/product dropdowns.
        const loadedOrgForGst = poData[0].purchase_organization || "";
        const allZeroOnLoad = poData.every(row => {
          const t = row.taxes || [];
          return (Number(t[0]?.percent ?? row.cgst_percent ?? 0) === 0) &&
                 (Number(t[1]?.percent ?? row.sgst_percent ?? 0) === 0) &&
                 (Number(t[2]?.percent ?? row.igst_percent ?? 0) === 0);
        });
        if (resolvedVendorId && allZeroOnLoad) {
          await refetchLineGst(resolvedVendorId, loadedOrgForGst);
        }

        const initialDiscounts = {};
        poData.forEach((row, idx) => {
          initialDiscounts[idx] = row.discount || 0;
        });
        setDiscounts(initialDiscounts);
      }
    } catch (error) {
      console.error("Error viewing PO:", error);
      toast.error("Failed to load Purchase Order details");
    }
  };


  const closeModal = () => {
    setShowModal(false);
    setModalData(null);
    activePoIdRef.current = "";
    setRenamedPoNumber("");
    setCalculatedAmounts({});
    setIsPoSaved(false);
    setSelectedVendor("");
    setVendorDetails(null);
    setPoLocation("");

    setPaymentTermNumber("");
    setModeOfTransport("");
    setDeliveryType("");
    setFreightTerms("");
    setInsuranceTerms("");
    setTransporter("");
    setPoHeaderLotType("");

    setPaymentTermUnit("days");
    setCreateActiveTab("basic");
    setIsEditMode(false);
    setFilterCategory("");
    setFilterSubCategory("");
    setEditableProducts([]);
    setLandedCosts({}); // 🆕 reset Landed Cost tab entries
    setDeletedLineItemIds([]); // reset deleted rows tracker
    // If we arrived here from Goods Receipt, go back
    if (navigatedFromGR) {
      setNavigatedFromGR(false);
      navigate(-1);
    }
  };

  // 🆕 Shared by handleVendorSelect AND the Purchase Organization field's
  // onChange below — either one can complete the (vendor, purchaseOrg)
  // pair needed to resolve GST, so both need to trigger this refetch.
  // Extracted so the two triggers can't drift out of sync with each other.
  const refetchLineGst = async (vendorId, purchaseOrganization) => {
    if (!modalData || modalData.length === 0) return;
    const pCodes = modalData.map(item => item.product_code);

    const isImport = poOrderType === "import";

    // 🆕 Purchase Organization no longer gates GST resolution — the
    // gst_configuration lookup is keyed by movement_type + hsn_code only
    // now. For Domestic POs the vendor (needed to resolve Intra/Inter
    // state) is required; for Import POs the Supplier Country is required
    // instead (vendor is irrelevant to the Import gst_configuration
    // lookup, which matches on country, not state).
    const canResolve = isImport ? !!importSupplierCountry : !!vendorId;

    if (canResolve) {
      try {
        const gstRes = await axios.post(`${PURCHASE_API}/po/get-product-gst`, {
          productCodes: pCodes,
          vendorCode: vendorId,
          purchaseOrganization,
          orderType: poOrderType,
          supplierCountry: isImport ? importSupplierCountry : undefined
        }, { headers: authHeaders() });
        const newGst = gstRes.data; // { [productCode]: { hsnCode, cgst, sgst, igst, taxes } }

        // 🛠 FIX: labels must always be derived the same way the amounts
        // below are assigned (positionally from taxes[]), for both
        // Domestic and Import — otherwise a Domestic row with only IGST
        // applicable shows an "IGST" header over a 0 while the real amount
        // sits under a blank header. See matching fix in the line-item-calc
        // refresh block above.
        setGstColumnLabels(deriveGstColumnLabels(newGst));

        setCalculatedAmounts(prev => {
          const updated = { ...prev };
          modalData.forEach((row, idx) => {
            const g = newGst[row.product_code];
            if (!g) return;

            // 🆕 If gst_configuration has no matching HSN+movement row for
            // this product/vendor combo, get-product-gst legitimately comes
            // back all-zero. Previously that zero got written straight into
            // the table, even though the product's own master
            // cgst_percent/sgst_percent/igst_percent (already sitting on
            // this row, baked in when the product was first picked) has the
            // real rate — the "View" popup uses exactly that fallback for
            // unsaved rows, which is why View showed the correct % while
            // the table stayed at 0. Apply the same fallback here so both
            // stay in sync regardless of what triggered this refetch
            // (vendor re-select, Purchase Organization change, etc).
            const isAllZero =
              (Number(g.cgst) || 0) === 0 &&
              (Number(g.sgst) || 0) === 0 &&
              (Number(g.igst) || 0) === 0;

            const fallback = {
              cgst: Number(row.cgst_percent ?? 0),
              sgst: Number(row.sgst_percent ?? 0),
              igst: Number(row.igst_percent ?? 0),
            };

            // 🛠 FIX: gst_configuration's tax rows may not literally be
            // named CGST/SGST/IGST, and even when they are (Domestic,
            // interstate-only-IGST etc.) their order in taxes[] is what the
            // column headers (gstColumnLabels, set above) are keyed to. So
            // for BOTH Domestic and Import we drop them into the 3 tax
            // slots positionally: taxes[0] -> slot 1 (col1), taxes[1] ->
            // slot 2 (col2), taxes[2] -> slot 3 (col3). This keeps whatever
            // header is showing locked to the number underneath it.
            const resolved = (g.taxes && g.taxes.length > 0)
              ? {
                  cgst: Number(g.taxes?.[0]?.percent ?? 0),
                  sgst: Number(g.taxes?.[1]?.percent ?? 0),
                  igst: Number(g.taxes?.[2]?.percent ?? 0),
                }
              : (isAllZero ? fallback : { cgst: g.cgst, sgst: g.sgst, igst: g.igst });

            updated[idx] = {
              ...updated[idx],
              cgst: resolved.cgst,
              sgst: resolved.sgst,
              igst: resolved.igst,
              hsnCode: g.hsnCode || updated[idx]?.hsnCode || row.hsn_code
            };
          });
          return updated;
        });
      } catch (e) {
        console.error("Error fetching product GST:", e);
      }
    } else {
      // Nothing to resolve GST against yet (no vendor for Domestic, no
      // Supplier Country for Import) -> force cgst/sgst/igst to 0 for
      // EVERY row — unconditionally, even for rows with no prior
      // calculatedAmounts entry — otherwise calculateLineTotals() falls
      // through to stale row.cgst_percent/sgst_percent/igst_percent from
      // whenever the product was first added, and the table keeps showing
      // numbers that no longer mean anything.
      setGstColumnLabels(DEFAULT_GST_COLUMN_LABELS);
      setCalculatedAmounts(prev => {
        const updated = { ...prev };
        modalData.forEach((row, idx) => {
          updated[idx] = { ...updated[idx], cgst: 0, sgst: 0, igst: 0 };
        });
        return updated;
      });
    }
  };

  // 🆕 Import-PO counterpart to refetchLineGst — takes the country directly
  // (rather than reading importSupplierCountry state) since this is called
  // from the Supplier Country input's onBlur, before/without waiting for a
  // state update to land.
  const refetchLineGstForCountry = async (country) => {
    if (!modalData || modalData.length === 0) return;
    if (!country) {
      setGstColumnLabels(DEFAULT_GST_COLUMN_LABELS);
      setCalculatedAmounts(prev => {
        const updated = { ...prev };
        modalData.forEach((row, idx) => {
          updated[idx] = { ...updated[idx], cgst: 0, sgst: 0, igst: 0 };
        });
        return updated;
      });
      return;
    }

    const pCodes = modalData.map(item => item.product_code);
    try {
      const gstRes = await axios.post(`${PURCHASE_API}/po/get-product-gst`, {
        productCodes: pCodes,
        purchaseOrganization: modalData?.[0]?.purchase_organization || "",
        orderType: "import",
        supplierCountry: country
      }, { headers: authHeaders() });
      const newGst = gstRes.data; // { [productCode]: { hsnCode, taxes } }

      setGstColumnLabels(deriveGstColumnLabels(newGst));

      setCalculatedAmounts(prev => {
        const updated = { ...prev };
        modalData.forEach((row, idx) => {
          const g = newGst[row.product_code];
          if (!g) return;
          updated[idx] = {
            ...updated[idx],
            cgst: Number(g.taxes?.[0]?.percent ?? 0),
            sgst: Number(g.taxes?.[1]?.percent ?? 0),
            igst: Number(g.taxes?.[2]?.percent ?? 0),
            hsnCode: g.hsnCode || updated[idx]?.hsnCode || row.hsn_code
          };
        });
        return updated;
      });
    } catch (e) {
      console.error("Error fetching product GST for import supplier country:", e);
    }
  };

  // 🆕 Auto-fill Currency + Exchange Rate on the Customs tab the moment a
  // Supplier Country is picked, using the Exchange Rate Configuration table
  // (Exchangerateconfig.service.js -> exchange_rate_configuration). We look
  // up the row for our home country -> the selected supplier country.
  //
  // We do NOT filter by the current month/year — Exchange Rate
  // Configuration is usually updated periodically (not guaranteed to have
  // a row for the exact month the PO happens to be created in), so we
  // fetch every India -> <country> row and take the latest one
  // (getExchangeRateList already sorts year DESC, month DESC, id DESC, so
  // rows[0] is always the most recent).
  //
  // ⚠️ ASSUMED "from_country" = "India" (our own company's country). There's
  // no company-country setting elsewhere in this file to read from — this
  // mirrors the isIndia check used for Order Type auto-derivation above
  // (search "vendorCountry.toLowerCase()"). Confirm this matches how
  // exchange_rate_configuration rows are actually entered (India -> X),
  // and adjust if your company/base country differs.
  const EXCHANGE_RATE_CONFIG_URL = `${PURCHASE_API}/exchange-rate-config`;
  const HOME_COUNTRY = "India";

  const fetchExchangeRateForCountry = async (supplierCountry) => {
    if (!supplierCountry) {
      // Reverted to no country (or back to Domestic) — clear and revert costs to base (INR).
      setImportCurrency("");
      setImportExchangeRate("");
      applyExchangeRateToLineItems("", "");
      return;
    }

    try {
      const res = await axios.get(EXCHANGE_RATE_CONFIG_URL, {
        params: {
          from_country: HOME_COUNTRY,
          to_country: supplierCountry
          // 🆕 no month/year here on purpose — see note above
        },
        headers: authHeaders()
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      if (rows.length === 0) {
        toast.error(
          `No exchange rate configured for ${HOME_COUNTRY} -> ${supplierCountry}. ` +
          `Please add one in Exchange Rate Configuration, or enter Currency / Rate manually.`
        );
        setImportCurrency("");
        setImportExchangeRate("");
        return;
      }

      const match = rows[0]; // latest available (list is sorted year/month DESC)
      const rate = Number(match.exchange_rate) || 0;
      const currency = match.to_currency || "";

      setImportCurrency(currency);
      setImportExchangeRate(rate || "");

      applyExchangeRateToLineItems(currency, rate);
    } catch (e) {
      console.error("Error fetching exchange rate config:", e);
      toast.error("Failed to fetch exchange rate for the selected Supplier Country.");
    }
  };

  // 🆕 Push the new Currency + converted Unit Cost into every Product-tab
  // line item. Each row keeps its ORIGINAL cost (as fetched from the
  // product master, in INR) in `base_cost_inr` — captured once when the
  // product is added (see the product-select onChange, and the
  // editableProducts-init useEffect near the top of the component, both
  // updated to stash it) — so this can be re-applied any number of times
  // (country changed again, or reverted to Domestic) without compounding
  // conversions on top of a previously-converted value.
  //
  // NOTE: the grid's Currency cell reads from `modalData[idx].currency`,
  // but the editable Unit Cost input reads from
  // `editableProducts[idx].product_cost` (see the useEffect that inits
  // editableProducts from modalData, and handleProductFieldChange) — these
  // are two separate pieces of state that happen to start out in sync, so
  // both must be updated here together or the grid and the save payload
  // will disagree.
  //
  // ✅ CONFIRMED DIRECTION (from Exchange Rate Configuration screen) —
  // exchange_rate is stored as "1 unit of HOME currency (INR) = <rate>
  // units of the foreign currency" (e.g. the India -> China row shows
  // "1 INR = 0.071 Yuan"). So converting an INR cost INTO the foreign
  // currency MULTIPLIES by the rate.
  const applyExchangeRateToLineItems = (currency, rate) => {
    if (!modalData || modalData.length === 0) return;

    setModalData(prev =>
      prev.map(row => {
        const baseCostInr = Number(row.base_cost_inr ?? row.product_cost ?? 0);
        // 🆕 native_currency = the currency THIS product's cost is actually
        // quoted in (product master / PIR — see getPIRInfo's
        // COALESCE(pd.currency, ad.currency)). Captured once at product-add
        // time and never overwritten, since `currency` below gets reused to
        // show the PO's *import* currency in the grid.
        const nativeCurrency = row.native_currency ?? row.currency ?? "INR";
        return {
          ...row,
          base_cost_inr: baseCostInr, // preserve for future re-conversion
          native_currency: nativeCurrency
          // 💤 COMMENTED OUT (for now): the PO-level Currency / Exchange Rate must
          // not overwrite each line's own currency anymore — Currency is now a
          // per-row dropdown (Configuration > Currency) the user can change.
          // currency: currency || "INR"
        };
      })
    );

    setEditableProducts(prev =>
      modalData.map((row, i) => {
        const existing = prev[i] || {};
        // 🆕 editableProducts (existing) reflects the user's latest manual
        // edit — see handleProductFieldChange — so it must win over
        // modalData's base_cost_inr, which is only set once at
        // product-add time and never touched by manual edits.
        const baseCostInr = Number(
          existing.base_cost_inr ?? row.base_cost_inr ?? row.product_cost ?? 0
        );
        // 🐛 FIX: this used to multiply EVERY row's cost by the rate
        // whenever a foreign Currency + Exchange Rate went active, no
        // matter what currency that row's cost was already in — which
        // (a) double-converted products whose master/PIR cost is already
        // quoted in a foreign currency, and (b) clobbered a Unit Cost the
        // user had just typed but not yet blurred, since base_cost_inr was
        // still the stale pre-edit figure. Only convert when the product's
        // OWN currency is INR — that's the only case "multiply by rate"
        // means anything.
        const nativeCurrency = (
          existing.native_currency ?? row.native_currency ?? row.currency ?? "INR"
        ).toString().trim().toUpperCase();
        const isNativeInr = nativeCurrency === "" || nativeCurrency === "INR";
        // 💤 COMMENTED OUT (for now): exchange rate is NOT applied to Unit Cost.
        // const convertedCost = round2(isNativeInr && currency && rate ? baseCostInr * rate : baseCostInr);
        const convertedCost = round2(baseCostInr);
        return {
          ...existing,
          base_cost_inr: baseCostInr,
          native_currency: nativeCurrency,
          product_cost: convertedCost
        };
      })
    );
  };

  const handleVendorSelect = async (vendorId) => {
    setSelectedVendor(vendorId);

    if (!vendorId) {
      setVendorDetails(null);
      setPaymentTerms("");
      setPaymentTermNumber("");
      setPaymentTermUnit("days");
      // Supplier cleared -> also re-resolve GST (will zero it out, since
      // there's no vendor half of the pair anymore).
      await refetchLineGst("", modalData?.[0]?.purchase_organization || "");
      return;
    }

    try {
      const response = await axios.get(`${PURCHASE_API}/po/business-partner/${vendorId}`, { headers: authHeaders() });
      setVendorDetails(response.data);

      // 🔧 FIX: business_partner.terms_and_conditions is Terms & Conditions
      // text, not a Payment Term — no longer pushed into Payment Terms.
      // Prefill the Terms & Conditions field instead (only when the user
      // hasn't already typed something for this PO); it's saved to db the
      // same way payment_terms is, via the normal PO Save button.
      const vTerms = response.data.terms_and_conditions || "";
      if (vTerms && !termsAndConditions) {
        setTermsAndConditions(vTerms);
      }

      // 🆕 Auto-derive Order Type from the supplier's country — India ->
      // Domestic PO, anything else -> Import PO (with Supplier Country
      // pre-filled from the business partner record). User can still
      // override the Order Type / Supplier Country manually afterwards.
      // 🔧 FIX: was reading billing_country / shipping_country, but those
      // belong to the Billing/Shipping address sets, which aren't used
      // for Supplier partners — Suppliers only have the single "Address"
      // tab (address_line1..address_country, see businessPartner_service.js
      // and getBusinessPartner's registered_address fix). Read
      // address_country instead so this actually gets populated.
      const vendorCountry = (
        response.data.address_country ||
        ""
      ).trim();

      if (vendorCountry) {
        const isIndia = vendorCountry.toLowerCase() === "india";
        setPoOrderType(isIndia ? "domestic" : "import");
        setImportSupplierCountry(isIndia ? "" : vendorCountry);
      }

    // ✅ When supplier is changed manually, prefer that supplier's own PIR
    // cost (purchase_info_records) per product; fall back to the generic
    // productdetails/assetdetails master cost for any product that has no
    // PIR record with this specific vendor.
if (modalData && modalData.length > 0) {
  const pCodes = modalData.map(item => item.product_code);
  try {
    const [vendorCostRes, masterCostRes] = await Promise.all([
      axios.post(`${PURCHASE_API}/po/get-vendor-costs`, {
        vendorCode: vendorId,
        productCodes: pCodes
      }, { headers: authHeaders() }).catch(e => {
        console.error("Error fetching vendor-specific PIR costs:", e);
        return { data: {} };
      }),
      axios.post(`${PURCHASE_API}/po/get-product-costs`, {
        productCodes: pCodes
      }, { headers: authHeaders() })
    ]);
    const vendorCosts = vendorCostRes.data || {}; // { [productCode]: cost } — this vendor's PIR cost, INR
    const newCosts = masterCostRes.data; // { [productCode]: cost } — generic master cost, INR (fallback)

    // 🆕 This cost comes straight from the backend, not from anything the
    // user typed, so the onBlur-based conversion never fires for it. If a
    // foreign Currency + Exchange Rate is already active, convert right
    // here — same "1 INR = <rate> foreign, so multiply" rule as
    // everywhere else — and keep base_cost_inr as the untouched INR
    // figure the API returned, so re-picking a country later converts
    // from this, not from an already-converted number.
    const rate = Number(importExchangeRate) || 0;

    setEditableProducts(prev => prev.map((item, idx) => {
      const code = modalData[idx].product_code;
      const rawCostInr = vendorCosts[code] !== undefined
        ? Number(vendorCosts[code])
        : (newCosts[code] !== undefined ? Number(newCosts[code]) : Number(item.base_cost_inr ?? item.product_cost ?? 0));
      // 🆕 Only convert when THIS product's own cost is quoted in INR —
      // same native-currency gate as applyExchangeRateToLineItems, so a
      // product whose master/PIR cost is already in a foreign currency
      // doesn't get double-converted here.
      const nativeCurrency = (
        modalData[idx].native_currency ?? modalData[idx].currency ?? "INR"
      ).toString().trim().toUpperCase();
      const isNativeInr = nativeCurrency === "" || nativeCurrency === "INR";
      // 💤 COMMENTED OUT (for now): exchange rate not applied to Unit Cost
      // const shouldConvert = isNativeInr && poOrderType === "import" && importCurrency && rate;
      const shouldConvert = false;
      return {
        ...item,
        base_cost_inr: rawCostInr,
        native_currency: nativeCurrency,
        product_cost: round2(shouldConvert ? rawCostInr * rate : rawCostInr)
      };
    }));
  } catch (e) {
    console.error("Error fetching product costs:", e);
  }

  // 🆕 Supplier change can flip Intra State <-> Inter State (different
  // supplier state -> different movement type), which changes whether tax
  // is split CGST+SGST or IGST. Without this, calculateLineTotals() keeps
  // showing the PREVIOUS supplier's cgst_percent/sgst_percent/igst_percent
  // (it falls back to row.cgst_percent from modalData, which was only set
  // when the product was first added / last saved) until the PO is saved
  // and reloaded. Refetch here so the table reflects the new supplier
  // immediately, same as the cost refetch above.
  await refetchLineGst(vendorId, modalData[0]?.purchase_organization || "");
}

    } catch (error) {
      console.error("Error fetching vendor details or costs:", error);
    }
  };

  // 🆕 Called when the user resolves a supplier conflict from the
  // vendorConflictModal — either by clicking one of the detected
  // conflicting suppliers, or by picking "a different supplier" from the
  // full vendor list. Stamps that supplier onto every product row already
  // in the modal (so the mismatch can't re-trigger) and runs the normal
  // supplier-select side effects (costs, GST, terms, etc.) via
  // handleVendorSelect.
  const resolveVendorConflict = async (vendorCode, vendorName) => {
    setModalData(prev =>
      prev.map(r =>
        r?.product_code
          ? { ...r, _pirVendorName: vendorName || r._pirVendorName, _pirVendorCode: vendorCode || r._pirVendorCode }
          : r
      )
    );
    setVendorConflictModal(null);
    await handleVendorSelect(vendorCode || "");
  };

const handleViewProductDetails = async (lineItemId, idx) => {
  try {
    const discount = discounts[idx] || 0;
    const currentCost =
      editableProducts[idx]?.product_cost ||
      modalData[idx]?.product_cost ||
      0;

    // ✅ NEW / UNSAVED ROW — no DB id yet, so calculate locally instead of hitting the server
    if (!lineItemId) {
      const row = modalData[idx] || {};

      const requirement_qty = Number(
        editableProducts[idx]?.requirement_qty ??
        row.requirement_qty ??
        0
      );

      const hsnCode = row.hsn_code || "";
      const cgst = Number(row.cgst_percent ?? 0);
      const sgst = Number(row.sgst_percent ?? 0);
      const igst = Number(row.igst_percent ?? 0);

      const product_cost = Number(currentCost || 0);
      const totalCost = requirement_qty * product_cost;
      const netPriceAfterDiscount = totalCost - (totalCost * discount) / 100;

      const cgstAmount = (netPriceAfterDiscount * cgst) / 100;
      const sgstAmount = (netPriceAfterDiscount * sgst) / 100;
      const igstAmount = (netPriceAfterDiscount * igst) / 100;
      const totalGST = cgstAmount + sgstAmount + igstAmount;
      const finalPriceAfterGST = netPriceAfterDiscount + totalGST;

      // 🆕 Dynamic tax breakup — no unsaved-row `taxes[]` from the server
      // yet, so fall back to whatever the Product tab's own column headers
      // (gstColumnLabels) currently say for this PO, same labels the grid
      // itself is showing. Keeps this modal from hardcoding CGST/SGST/IGST
      // for an Import row that's actually charging Basic Customs Duty etc.
      const taxesArr = [
        { type: gstColumnLabels.col1 || "CGST", percent: cgst, value: cgstAmount },
        gstColumnLabels.col2 ? { type: gstColumnLabels.col2, percent: sgst, value: sgstAmount } : null,
        gstColumnLabels.col3 ? { type: gstColumnLabels.col3, percent: igst, value: igstAmount } : null,
      ].filter(Boolean);

      setProductDetails({
        product_code: row.product_code,
        discount, product_cost, requirement_qty, hsnCode, cgst, sgst, igst,
        totalCost, netPriceAfterDiscount, cgstAmount, sgstAmount, igstAmount,
        totalGST, finalPriceAfterGST, taxes: taxesArr,
      });

      setCalculatedAmounts(prev => ({
        ...prev,
        [idx]: {
          totalCost, netPriceAfterDiscount, cgst, sgst, igst,
          cgstAmount, sgstAmount, igstAmount, totalGST, finalPriceAfterGST, hsnCode
        }
      }));

      setShowProductModal(true);
      return;
    }

    // ── EXISTING / SAVED ROW — use the server calc as before ──
    const response = await fetch(
      `${PURCHASE_API}/po/line-item-calc/${lineItemId}?discount=${discount}&cost=${currentCost}`
    , { headers: authHeaders() });

    if (response.ok) {
      const data = await response.json();

      const {
        product_cost, requirement_qty, hsnCode, cgst, sgst, igst,
        totalCost, netPriceAfterDiscount, cgstAmount, sgstAmount,
        igstAmount, totalGST, finalPriceAfterGST, taxes
      } = data;

      setProductDetails({
        product_code: modalData[idx].product_code,
        discount, product_cost, requirement_qty, hsnCode, cgst, sgst, igst,
        totalCost, netPriceAfterDiscount, cgstAmount, sgstAmount, igstAmount,
        totalGST, finalPriceAfterGST,
        // 🆕 The real dynamic breakdown straight from the server (same
        // shape as what's stored in purchase_order_tax) — e.g. Basic
        // Customs Duty / Social Welfare Surcharge / Other Customs
        // Charges for an Import line, instead of always CGST/SGST/IGST.
        taxes: taxes && taxes.length
          ? taxes.map(t => ({
              type: t.type,
              percent: Number(t.percent) || 0,
              value: +((netPriceAfterDiscount * (Number(t.percent) || 0)) / 100).toFixed(2),
            }))
          : [
              { type: "CGST", percent: cgst, value: cgstAmount },
              { type: "SGST", percent: sgst, value: sgstAmount },
              { type: "IGST", percent: igst, value: igstAmount },
            ],
      });

      setCalculatedAmounts(prev => ({
        ...prev,
        [idx]: {
          totalCost, netPriceAfterDiscount, cgst, sgst, igst,
          cgstAmount, sgstAmount, igstAmount, totalGST, finalPriceAfterGST, hsnCode
        }
      }));

      setShowProductModal(true);
    } else {
      toast.error("Failed to fetch product details.");
    }
  } catch (error) {
    console.error("Error fetching product details:", error);
  }
};

  const handleDiscountChange = (e, idx) => {
    const newDiscounts = { ...discounts };
    newDiscounts[idx] = parseFloat(e.target.value) || 0;
    setDiscounts(newDiscounts);
  };

  const handleCheckboxChange = (e, idx) => {
    const isChecked = e.target.checked;
    setSelectedRows(prevSelected => {
      if (isChecked) {
        return [...prevSelected, idx];
      } else {
        return prevSelected.filter(i => i !== idx);
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const selectableIndices = modalData
        .map((row, idx) => {
          const s = String(row.review || "").toUpperCase();
          return ["APPROVED", "REJECTED"].includes(s) ? null : idx;
        })
        .filter(idx => idx !== null);
      setSelectedRows(selectableIndices);
    } else {
      setSelectedRows([]);
    }
  };


  // PO is fully finalized when every line has been decided (APPROVED or REJECTED)
  const isPOFinalized =
    Array.isArray(modalData) &&
    modalData.length > 0 &&
    modalData.every(row =>
      ["APPROVED", "REJECTED"].includes(
        String(row.review || "").toUpperCase()
      )
    );


  const handleSubmit = async () => {
    if (!modalData || modalData.length === 0) {
      toast.error("No PO data found");
      return;
    }

    if (isPOFinalized) {
      toast.warning("This Purchase Order is already finalized");
      return;
    }

    if (selectedRows.length === 0) {
      toast.warning("Please select at least one line item to proceed");
      return;
    }

    if (actionType !== "APPROVED" && !comment.trim()) {
      toast.warning("Comment is mandatory");
      return;
    }

    if (
      actionType === "APPROVED" &&
      (!quoteNo || !quoteDate)
    ) {
      toast.warning("Quote No and Quote Date are mandatory");
      return;
    }

    const updatedRows = selectedRows.map((idx) => {
      const row = modalData[idx];

      return {
        id: row.id,
        purchase_order_id: row.purchase_order_id,
        product_code: row.product_code,
        purchase_request_no: row.purchase_request_no,
        status: actionType.toUpperCase(),
        comment,
        reviewed_by: getCurrentUser(),
      };
    });

    try {
      console.log({
        rows: updatedRows,
        quote_no: quoteNo,
        quote_date: quoteDate,
      });

      const response = await fetch(
        `${PURCHASE_API}/po/updateStatus`,
        {
          method: "POST",
          headers: { ...authHeaders(), 
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            rows: updatedRows,
            quote_no: quoteNo || null,
            quote_date: quoteDate || null,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(result.message);

        setCommentModal(false);
        setComment("");

        setSelectedRows([]);

        const poId = modalData[0]?.purchase_order_id;

        if (poId) {
          try {
            const refreshRes = await axios.get(`${PURCHASE_API}/po/${poId}`, { headers: authHeaders() });

            if (
              Array.isArray(refreshRes.data) &&
              refreshRes.data.length > 0
            ) {
              setModalData(refreshRes.data);
            }
          } catch (refreshErr) {
            console.error(
              "Error refreshing PO line items:",
              refreshErr
            );
          }
        }

        await refreshPOList();
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating PO status");
    }
  };

  useEffect(() => {
    if (modalData && Array.isArray(modalData)) {
      setConfirmationData(modalData);
    }
  }, [modalData]);

  useEffect(() => {
    if (modalData && modalData.length > 0) {
      setConfirmQtys(Array(modalData.length).fill(''));
      setConfirmDates(Array(modalData.length).fill(''));
      setDeliverQtys(Array(modalData.length).fill(''));
      setDeliverDates(Array(modalData.length).fill(''));
      setReceivingDates(Array(modalData.length).fill(''));
      setTransportationDates(Array(modalData.length).fill(''));
      setPackingDates(Array(modalData.length).fill(''));
      setPickingDates(Array(modalData.length).fill(''));
      setMadDates(Array(modalData.length).fill(''));
      setConfirmQtys(modalData.map(row => row.confirm_qty || ""));
      setConfirmDates(modalData.map(row => row.confirm_date ? formatDate(row.confirm_date) : ""));
      setDeliverQtys(modalData.map(row => row.deliver_qty || ""));
      setDeliverDates(modalData.map(row => row.deliver_date ? formatDate(row.deliver_date) : ""));
      setReceivingDates(modalData.map(row => row.receiving_date ? formatDate(row.receiving_date) : ""));
      setTransportationDates(modalData.map(row => row.transportation_date ? formatDate(row.transportation_date) : ""));
      setPackingDates(modalData.map(row => row.packing_date ? formatDate(row.packing_date) : ""));
      setPickingDates(modalData.map(row => row.picking_date ? formatDate(row.picking_date) : ""));
      setMadDates(modalData.map(row => row.mad ? formatDate(row.mad) : ""));
    }
  }, [modalData]);
//eslint-disable-next-line
  const handleConfirmQtyChange = (e, idx) => {
    const value = e.target.value;
    const updatedConfirmQtys = [...confirmQtys];
    updatedConfirmQtys[idx] = value;
    setConfirmQtys(updatedConfirmQtys);
  };

  const refreshPOList = async () => {
    try {
      const res = await axios.get(`${PURCHASE_API}/po/purchase-orders`, { headers: authHeaders() });
      if (res.data.success) {
        setAllPurchaseOrders(res.data.purchaseOrders);
        setPoList(res.data.purchaseOrders);
        setPurchaseOrders(res.data.purchaseOrders);
      }
    } catch (err) {
      console.error("Error refreshing PO list:", err);
    }
  };
//eslint-disable-next-line
  const handleConfirmDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedConfirmDates = [...confirmDates];
    updatedConfirmDates[idx] = value;
    setConfirmDates(updatedConfirmDates);
  };
//eslint-disable-next-line
  const handleDeliverQtyChange = (e, idx) => {
    const value = e.target.value;
    const updatedDeliverQtys = [...deliverQtys];
    updatedDeliverQtys[idx] = value;
    setDeliverQtys(updatedDeliverQtys);
  };
//eslint-disable-next-line
  const handleDeliverDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedDeliverDates = [...deliverDates];
    updatedDeliverDates[idx] = value;
    setDeliverDates(updatedDeliverDates);
  };
//eslint-disable-next-line
  const handleReceivinDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedReceivingDates = [...receivingDates];
    updatedReceivingDates[idx] = value;
    setReceivingDates(updatedReceivingDates);
  };
//eslint-disable-next-line
  const handleTransportationDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedTransportationDates = [...transportationDates];
    updatedTransportationDates[idx] = value;
    setTransportationDates(updatedTransportationDates);
  };

  const handleProductFieldChange = (idx, field, value) => {
    const updated = [...editableProducts];
    updated[idx][field] = value;
    // 🐛 FIX: keep base_cost_inr in sync with whatever's on screen as the
    // user types, not just on blur — otherwise picking a Supplier Country
    // (or adding another product) could fire applyExchangeRateToLineItems
    // BEFORE blur ever ran, and it would recompute this row's cost from
    // the STALE pre-edit base_cost_inr, silently discarding what was just
    // typed.
    if (field === "product_cost") {
      updated[idx].base_cost_inr = value;
    }
    setEditableProducts(updated);
  };

  // 🆕 Fires when the Unit Cost box loses focus. Whatever number is
  // sitting in the box at that moment is treated as the amount the user
  // just typed — if a foreign Currency + Exchange Rate is active on the
  // Customs tab AND this product's own cost is quoted in INR
  // (native_currency), it's multiplied by that rate right there in the box
  // (rate is "1 INR = <rate> foreign", e.g. 1 INR = 0.071 Yuan, so
  // typed INR * rate = foreign amount). If the product's own currency is
  // NOT INR, the typed number is left exactly as typed — it's already in
  // that product's currency, so the rate has nothing to convert FROM.
  // base_cost_inr is stored as the pre-conversion (typed) figure so
  // re-picking a country later re-converts from what you actually
  // entered, not a stale value.
  const handleProductCostBlur = (idx) => {
    const rate = Number(importExchangeRate) || 0;
    const typed = Number(editableProducts[idx]?.product_cost) || 0;
    const nativeCurrency = (
      editableProducts[idx]?.native_currency ??
      modalData[idx]?.native_currency ??
      modalData[idx]?.currency ??
      "INR"
    ).toString().trim().toUpperCase();
    const isNativeInr = nativeCurrency === "" || nativeCurrency === "INR";

    // 💤 Exchange rate on Unit Cost is disabled for now — always take the
    // "nothing to convert" path. Original condition kept below for later:
    // if (poOrderType !== "import" || !importCurrency || !rate || !isNativeInr) {
    if (true) {
      // Nothing to convert — still sync base_cost_inr so it's never stale
      // for a later applyExchangeRateToLineItems call.
      const updated = [...editableProducts];
      updated[idx] = { ...updated[idx], base_cost_inr: typed, product_cost: round2(typed) };
      setEditableProducts(updated);
      return;
    }

    // 💤 COMMENTED OUT (for now): exchange rate multiplication on Unit Cost
    // const updated = [...editableProducts];
    // updated[idx] = {
    //   ...updated[idx],
    //   base_cost_inr: typed,
    //   product_cost: round2(typed * rate)
    // };
    // setEditableProducts(updated);
  };

//eslint-disable-next-line
  const handlePackingDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedPackingDates = [...packingDates];
    updatedPackingDates[idx] = value;
    setPackingDates(updatedPackingDates);
  };
//eslint-disable-next-line
  const handlePickingrDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedPickingDates = [...pickingDates];
    updatedPickingDates[idx] = value;
    setPickingDates(updatedPickingDates);
  };
//eslint-disable-next-line
  const handleMadChange = (e, idx) => {
    const value = e.target.value;
    const updatedMadDates = [...madDates];
    updatedMadDates[idx] = value;
    setMadDates(updatedMadDates);
  };

  // ── Characteristics Modal Handlers ─────────────────────────────
  // classSource: "product" filters to the Product Class's own characteristics
  // (e.g. Supplier Description). Falls back to showing
  // everything if we don't have the class's characteristic list yet, so the
  // modal never appears empty while classCharacteristicMap is still loading.
  const handleOpenCharModal = (idx, classSource = "product") => {
    setCharModalRowIdx(idx);
    setCharModalSource(classSource);
    const row = modalData[idx];
    const chars = row.characteristics || [];

    const classCode =
      row.product_class || allProducts.find(p => p.productCode === row.product_code)?.productClass;

    const allowedNames = classCode ? classCharacteristicMap[classCode] : null;

    const filteredChars = allowedNames
      ? chars.filter(c => allowedNames.has((c.characteristic || "").trim().toLowerCase()))
      : chars;

    setSelectedCharacteristics(
      filteredChars.map(c => ({ characteristic: c.characteristic, value: c.value || "" }))
    );
    setModalVisible(true);
  };

  const onModalCharChange = (charIdx, value) => {
    const updated = [...selectedCharacteristics];
    updated[charIdx] = { ...updated[charIdx], value };
    setSelectedCharacteristics(updated);
  };

  const handleSaveCharModal = () => {
    const updated = [...modalData];
    const existingChars = updated[charModalRowIdx]?.characteristics || [];

    // Merge the edited subset back into the row's full characteristics list
    // (rather than overwriting it) so editing Supplier Description here
    // doesn't wipe out the Over Tolerance stored in the same row.
    const editedNames = new Set(
      selectedCharacteristics.map(c => (c.characteristic || "").trim().toLowerCase())
    );
    const untouched = existingChars.filter(
      c => !editedNames.has((c.characteristic || "").trim().toLowerCase())
    );

    updated[charModalRowIdx] = {
      ...updated[charModalRowIdx],
      characteristics: [...untouched, ...selectedCharacteristics]
    };
    setModalData(updated);
    setModalVisible(false);
  };
  // ────────────────────────────────────────────────────────────────

  // Opens the in-app confirm dialog instead of window.confirm
  const handleShortClose = () => setShowShortCloseConfirm(true);

  const confirmShortClose = async () => {
    setShowShortCloseConfirm(false);
    try {
      const res = await axios.post(`${PURCHASE_API}/po/short-close`, {
        purchase_order_id: modalData?.[0]?.purchase_order_id
      }, { headers: authHeaders() });
      if (res.data.success) {
        toast.success("PO Short Closed successfully.");
        closeModal();
        setListTab("short_closed");
        // Refresh PO list
        axios.get(`${PURCHASE_API}/po/purchase-orders`, { headers: authHeaders() }).then(r => {
          if (r.data.success) {
            setAllPurchaseOrders(r.data.purchaseOrders);
            setPoList(r.data.purchaseOrders);
            setPurchaseOrders(r.data.purchaseOrders);
          }
        });
      } else {
        toast.error(res.data.message || "Short Close failed.");
      }
    } catch (err) {
      toast.error("Short Close failed.");
    }
  };

  const handleSaveConfirmation = async () => {
    try {
      const payload = {
        purchase_order_id: modalData?.[0]?.purchase_order_id,
        confirmation: confirmationLines
      };
      const res = await axios.post(`${PURCHASE_API}/po/save-confirmation`, payload, { headers: authHeaders() });
      if (res.data.success) {
        toast.success("Confirmation saved successfully.");
      } else {
        toast.error(res.data.message || "Failed to save confirmation.");
      }
    } catch (err) {
      toast.error("Failed to save confirmation.");
    }
  };

  const handleSubmitPO = async () => {
    const poId = modalData?.[0]?.purchase_order_id;
    if (!poId) {
      toast.error("No Purchase Order found to submit");
      return;
    }

    try {
      const response = await fetch(
        `${PURCHASE_API}/po/submit/${poId}`,
        {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || "Purchase Order submitted for approval");
      } else {
        // Whatever the reason (already submitted by a near-simultaneous
        // click, status changed elsewhere, etc.), the screen we're
        // showing no longer matches the real PO — resync below instead
        // of leaving the stale CREATED-looking form on screen.
        toast.error(result.message || "Failed to submit Purchase Order");
      }
      refreshPOList();
      await viewPO(poId);
    } catch (err) {
      console.error("SUBMIT PO ERROR:", err);
      toast.error("Failed to submit Purchase Order");
    }
  };

  const handleSave = (onSuccess) => {
    if (!modalData || modalData.length === 0) {
      toast.error("No Purchase Order data");
      return;
    }


    if (!selectedVendor && !vendorDetails?.vendor_name) {
      toast.info("Please assign a Vendor before saving the Purchase Order");
      return;
    }

    if (!vendorDetails) {
      toast.error("Vendor details are missing. Please reselect the vendor.");
      return;
    }

    // Contact Details are mandatory
    if (!poContactName) {
      toast.error("Contact Details are mandatory — please select a Name");
      return;
    }

    // =================================================
// DELIVERY DATE MANDATORY VALIDATION
// =================================================
const missingDeliveryDateRow = modalData.findIndex(
  (_, idx) => {
    const deliveryDate = editableProducts[idx]?.delivery_date;
    return !deliveryDate || deliveryDate.trim() === "";
  }
);

if (missingDeliveryDateRow !== -1) {
  toast.error(
    `Delivery Date is mandatory for Product ${
      modalData[missingDeliveryDateRow]?.product_code ||
      `Row ${missingDeliveryDateRow + 1}`
    }`
  );
  return;
}

    // =================================================
    // 🆕 QTY-WITHIN-TOLERANCE-RANGE VALIDATION
    // Block save if the entered Qty falls outside the Under/Over
    // Tolerance range shown in the Range column.
    // =================================================
    for (let idx = 0; idx < modalData.length; idx++) {
      const row = modalData[idx];
      const { rangeMin, rangeMax } = getToleranceRange(row, idx);

      // Only enforce when a range is actually defined for this row
      // (i.e. the Product Class has Over/Under Tolerance % set).
      if (rangeMin === null && rangeMax === null) continue;

      const enteredQty = Number(editableProducts[idx]?.requirement_qty);

      if (
        isNaN(enteredQty) ||
        (rangeMin !== null && enteredQty < rangeMin) ||
        (rangeMax !== null && enteredQty > rangeMax)
      ) {
        // 🆕 Build the message from whichever bound(s) are actually set,
        // instead of assuming both exist (rangeMin/rangeMax can now be
        // null individually — see getToleranceRange).
        const rangeMsg =
          rangeMin !== null && rangeMax !== null
            ? `${rangeMin.toFixed(2)} - ${rangeMax.toFixed(2)}`
            : rangeMax !== null
              ? `≤ ${rangeMax.toFixed(2)}`
              : `≥ ${rangeMin.toFixed(2)}`;

        toast.error(
          `Qty for ${row.product_code || `Row ${idx + 1}`} must be within the allowed range ` +
          `(${rangeMsg}).`
        );
        return;
      }
    }

    /* Block save if products belong to different default vendors — instead
       of just erroring out, surface the same choose-a-supplier modal used
       when the conflict is first detected on product-add, so the user can
       resolve it right here instead of hunting for which row is the odd
       one out. */
    const pirVendorMap = new Map(); // name -> code, de-duped
    modalData.forEach(r => {
      if (r._pirVendorName && !pirVendorMap.has(r._pirVendorName)) {
        pirVendorMap.set(r._pirVendorName, r._pirVendorCode || r._pirVendorName);
      }
    });
    if (pirVendorMap.size > 1) {
      toast.error(
        `Products in this PO have different default suppliers. Choose one below, then click Save again.`
      );
      setVendorConflictModal({
        options: [...pirVendorMap.entries()].map(([name, code]) => ({ name, code }))
      });
      return;
    }

    /* 🆕 All line items must share ONE currency. Same currency on every
       product -> save goes through; mixed currencies -> blocked. */
    const rowCurrencies = modalData.map((r) => String(r.currency || "").trim().toUpperCase());
    const blankCurrencyIdx = rowCurrencies.findIndex((c) => !c);
    if (blankCurrencyIdx !== -1) {
      toast.error(
        `Select a Currency for ${modalData[blankCurrencyIdx]?.product_code || `Row ${blankCurrencyIdx + 1}`}`
      );
      return;
    }
    if (new Set(rowCurrencies).size > 1) {
      toast.error(
        `All products must have the same Currency (found: ${[...new Set(rowCurrencies)].join(", ")}). Please make them the same, then Save.`
      );
      return;
    }

    const calculateGST = ({ qty, unitCost, discount, cgst, sgst, igst }) => {
      const totalCost = qty * unitCost;
      const netAfterDiscount = totalCost - (totalCost * discount) / 100;

      const cgstAmount = (netAfterDiscount * cgst) / 100;
      const sgstAmount = (netAfterDiscount * sgst) / 100;
      const igstAmount = (netAfterDiscount * igst) / 100;

      const totalGST = cgstAmount + sgstAmount + igstAmount;
      const finalPrice = netAfterDiscount + totalGST;

      return {
        totalCost,
        netAfterDiscount,
        cgstAmount,
        sgstAmount,
        igstAmount,
        totalGST,
        finalPrice,
      };
    };

    // Existing PO id: remembered id first, then any row that carries one.
    const existingPoId =
      activePoIdRef.current ||
      modalData?.find(r => r?.purchase_order_id)?.purchase_order_id ||
      "";

    const payload = {

      // 🆕 On Create, send whatever the user typed into PO Number (may be
      // blank — the service auto-generates when it is). Once a PO already
      // has a real purchase_order_id (editing an existing PO), that always
      // wins and poManualNumber is irrelevant.
      purchase_order_id:
        existingPoId || (poManualNumber.trim() || null),
      quote_no: quoteNo || null,
      quote_date: quoteDate || null,
      po_revision_no: poRevisionNo !== "" ? poRevisionNo : null,
      contact_name: poContactName || null,
      contact_email: poContactEmail || null,
      contact_phone: poContactPhone || null,

      // ⚠️ FIX: this used to call getCurrentUser() again here, a fresh call
      // separate from the one that set poCreatedBy at mount (line ~662) /
      // on load (line ~1708). Minutes can pass between opening the form and
      // clicking Save — if anything rewrites localStorage's "user" object
      // in that window (e.g. a token refresh elsewhere in the app) without
      // userId on it, this second call would silently fall back to email
      // even though the field on screen still correctly showed the id.
      // Saving poCreatedBy instead guarantees what's displayed is exactly
      // what gets persisted — no second, possibly-stale lookup.
      modified_by:
        getCurrentUser(),
      created_by: poCreatedBy || getCurrentUser(),
      created_date: poCreatedDate || getTodayLocal(),
      product_type: poProductType || null,

      // 🆕 Order Type + Import PO header fields
      order_type: poOrderType || "domestic",
      supplier_country: poOrderType === "import" ? (importSupplierCountry || null) : null,
      import_currency: poOrderType === "import" ? (importCurrency || null) : null,
      exchange_rate: poOrderType === "import" ? (importExchangeRate || null) : null,
      incoterm: poOrderType === "import" ? (importIncoterm || null) : null,
      port_of_loading: poOrderType === "import" ? (importPortOfLoading || null) : null,
      port_of_discharge: poOrderType === "import" ? (importPortOfDischarge || null) : null,
      final_destination: poOrderType === "import" ? (importFinalDestination || null) : null,
      expected_shipment_date: poOrderType === "import" ? (importExpectedShipmentDate || null) : null,
      expected_arrival_date: poOrderType === "import" ? (importExpectedArrivalDate || null) : null,
      lc_required: poOrderType === "import" ? importLcRequired : null,
      insurance_required: poOrderType === "import" ? importInsuranceRequired : null,


      purchase_organization:
        modalData?.[0]?.purchase_organization || "",

      purchase_planner:
        modalData?.[0]?.purchase_planner || "",

      location:
        poLocation,


      vendor_name:
        vendorDetails?.business_partner_name ||
        vendorDetails?.vendor_name ||
        modalData?.[0]?.vendor_name ||
        null,

      // ✅ Persist the actual vendor/supplier code too — vendor_name alone
      // isn't enough because on reload the PO is re-linked to a vendor
      // using vendor_code first (see load logic around vendorCode/vendor),
      // so without this the supplier change appears to "not save".
      vendor_code:
        selectedVendor || null,

      contact_person_name:
        vendorDetails?.contact_person_name ||
        modalData?.[0]?.contact_person ||
        "",

      business_type:
        vendorDetails?.business_type ||
        modalData?.[0]?.business_type ||
        "",

      phone:
        vendorDetails?.phone ||
        vendorDetails?.contact_details ||
        modalData?.[0]?.phone ||
        "",

      gstin:
        vendorDetails?.gstin ||
        modalData?.[0].gstin ||
        null,

      registered_address:
        vendorDetails?.registered_address ||
        modalData?.[0].address ||
        null,

      payment_terms: paymentTerms || null,
      terms_and_conditions: termsAndConditions || null,

      mode_of_transport: modeOfTransport || null,
      delivery_type: deliveryType || null,
      freight_terms: freightTerms || null,
      insurance_terms: insuranceTerms || null,
      transporter: transporter || null,
      po_header_lot_type: poHeaderLotType || null,
      productDetails: modalData.map((row, idx) => {

        // 🆕 Landed Cost tab — flatten this row's entry into landed_*
        // fields expected by purchaseOrder.service.js. Manual entry only;
        // blank/empty stays null rather than being coerced to 0.
        // Per-line-item groups (Customs) use this row's own entry;
        // PO-level groups (Port/Clearing, Bank) use the single "po_level"
        // entry and are written identically onto every line item, since
        // there is one shared value entered once for the whole PO.
        const landedCostRowKey = row.id || `idx_${idx}`;
        const landedCostEntry = landedCosts[landedCostRowKey] || {};
        const poLevelLandedCostEntry = landedCosts["po_level"] || {};
        const landedCostFields = {};
        LANDED_COST_GROUPS.forEach(g => g.fields.forEach(f => {
          const source = g.perLineItem === false ? poLevelLandedCostEntry : landedCostEntry;
          const val = source[f.key];
          landedCostFields[`landed_${f.key}`] =
            (val === "" || val === null || val === undefined) ? null : Number(val);
        }));

        const qty =
          Number(
            editableProducts[idx]?.requirement_qty || 0
          );

        const unitCost =
          Number(
            editableProducts[idx]?.product_cost || 0
          );

        const discount =
          Number(
            discounts[idx] || 0
          );

        const gstState =
          calculatedAmounts[idx] || {};

        // Fall back to row.cgst_percent / sgst_percent / igst_percent
        // when calculatedAmounts hasn't been populated yet (e.g. on Create
        // before the user clicks "View"). This mirrors calculateLineTotals()
        // so the saved values always match what is shown on screen.
        const cgst =
          Number(gstState.cgst ?? row.cgst_percent ?? 0);

        const sgst =
          Number(gstState.sgst ?? row.sgst_percent ?? 0);

        const igst =
          Number(gstState.igst ?? row.igst_percent ?? 0);

        const calc =
          calculateGST({
            qty,
            unitCost,
            discount,
            cgst,
            sgst,
            igst
          });

        const toleranceRange = getToleranceRange(row, idx);

        return {

          id:
            row.id || null,

          product_code:
            row.product_code,

          product_class:
            row.product_class || null,

          description:
            row.description ||
            row.product_description ||
            null,

          uom:
            row.uom || "",

          currency:
            row.currency || "",

          purchase_request_no:
            row.purchase_request_no,

          product_cost:
            unitCost,

          requirement_qty:
            qty,

          delivery_date:
            editableProducts[idx]?.delivery_date || null,

          discount:
            discount,

          total_cost:
            calc.totalCost,

          net_price_after_discount:
            calc.netAfterDiscount,

          hsn_code:
            gstState.hsnCode || row.hsn_code || null,

          cgst_percent:
            cgst,

          sgst_percent:
            sgst,

          igst_percent:
            igst,

          cgst_amount:
            calc.cgstAmount,

          sgst_amount:
            calc.sgstAmount,

          igst_amount:
            calc.igstAmount,

          total_gst_amount:
            calc.totalGST,

          final_price_after_gst:
            calc.finalPrice,

          confirm_qty:
            confirmQtys[idx] || null,

          confirm_date:
            confirmDates[idx] || null,

          deliver_qty:
            deliverQtys[idx] || null,

          deliver_date:
            deliverDates[idx] || null,

          receiving_date:
            receivingDates[idx] || null,

          transportation_date:
            transportationDates[idx] || null,

          packing_date:
            packingDates[idx] || null,

          picking_date:
            pickingDates[idx] || null,

          mad:
            madDates[idx] || null,

          characteristics:
            row.characteristics || [],

          range_min:
            toleranceRange.rangeMin,

          range_max:
            toleranceRange.rangeMax,

          pir_number:
            row.pir_number || null,

          // 🆕 Import PO — item-level fields (only meaningful when Order
          // Type = Import PO). Country Of Origin always mirrors the PO
          // header's Supplier Country now — no separate per-line value.
          country_of_origin:
            poOrderType === "import" ? (importSupplierCountry || null) : null,

          // 🆕 Import PO — Landed Cost tab (17 fields)
          ...landedCostFields,
        };

      }),

      deletedLineItemIds: deletedLineItemIds.filter(id => id != null),
    };

    const isManualPO = !existingPoId;

    const apiUrl =
      isManualPO
        ? `${PURCHASE_API}/po/create-purchase-order`
        : `${PURCHASE_API}/po/update-purchase-order`;

    return fetch(apiUrl, {
      method: "POST",
      headers: { ...authHeaders(),  "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(async (result) => {
        if (result.success) {
          refreshPOList();
          setIsPoSaved(true);
          const savedPoId =
            result.purchase_order_id ||
            existingPoId;

          // Manually-created PO (not an update) -- calculate & store planned
          // raw material cost for it. Fire-and-forget: doesn't block the save
          // success flow / onSuccess callback, failures are logged only.
          if (isManualPO && savedPoId) {
            fetch(`${PURCHASE_API}/product-costing/calculate-planned-raw-material-cost`, {
              method: "POST",
              headers: { ...authHeaders(), "Content-Type": "application/json" },
              body: JSON.stringify({ purchaseOrderNumber: savedPoId }),
            }).catch((err) => {
              console.error(`Failed to calculate planned cost for PO ${savedPoId}:`, err);
            });
          }

          if (typeof onSuccess === "function") {
            // Caller (e.g. Submit on a CREATED PO) handles its own
            // success messaging / follow-up instead of the plain
            // "saved" toast, and decides whether edit mode stays on.
            await onSuccess(savedPoId);
          } else {
            toast.success("Purchase Order saved successfully");
            setIsEditMode(false);
            if (savedPoId) {
              await viewPO(savedPoId);
            }
          }
        } else {
          alert("Failed to save PO: " + result.message);
        }
      })
      .catch((err) => {
        console.error("SAVE ERROR:", err);
        toast.error("Error while saving Purchase Order");
      });
  };

  const handleApprove = () => {
    setComment("");
    setActionType("APPROVED");
    setCommentModal(true);
  };

  const handleReject = () => {
    setComment("");
    setActionType("REJECTED");
    setCommentModal(true);
  };

  const handleQuery = () => {
    setComment("");
    setActionType("QUERY");
    setCommentModal(true);
  };


 // ── Over Tolerance (%) → Range ──
 // Over Tolerance %  → rangeMax = qty + (qty * overTolerance / 100)
 // The % is typed straight into the "Over Tolerance (%)" column on the line
 // (stored as the "Over Tolerance" characteristic). Purchase Class / Under
 // Tolerance are no longer part of the PO line.
 const normCharName = (n) => (n || "").trim().toLowerCase().replace(/\s+/g, "");

 // Space-insensitive match: some records have "OverTolerance" (no space).
 const getOverTolerance = (row) => {
  const c = (row?.characteristics || []).find(
    (x) => normCharName(x.characteristic) === "overtolerance"
  );
  return c?.value ?? "";
 };

 // Returns a copy of `chars` with the Over Tolerance value set (added if missing).
 const withOverTolerance = (chars, value) => {
  const list = [...(chars || [])];
  const ci = list.findIndex((x) => normCharName(x.characteristic) === "overtolerance");
  if (ci >= 0) list[ci] = { ...list[ci], value };
  else list.push({ characteristic: "Over Tolerance", value });
  return list;
 };

 const handleOverToleranceChange = (idx, rawValue) => {
  // digits + a single decimal point only
  const clean = String(rawValue).replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
  setModalData((prev) => {
    const next = [...prev];
    next[idx] = { ...next[idx], characteristics: withOverTolerance(next[idx].characteristics, clean) };
    return next;
  });
 };

 const getToleranceRange = (row, idx) => {
  // Anchor to the ORIGINAL/base qty (row.requirement_qty, as loaded from the
  // DB) so the range doesn't just follow whatever is being typed — that's
  // what makes the qty-within-range check meaningful on saved / PR-converted
  // lines. For a brand-new line there is no base qty yet, so fall back to the
  // qty currently typed in the Qty box so the Range still appears.
  const baseQty = Number(row.requirement_qty);
  const qty =
    baseQty > 0
      ? baseQty
      : Number(editableProducts[idx]?.requirement_qty) || 0;

  const overRaw = getOverTolerance(row);
  const overPct = Number(overRaw);
  const hasOver = overRaw !== "" && !isNaN(overPct);

  if (!qty || !hasOver) {
    return { rangeMin: null, rangeMax: null, rangeText: "" };
  }

  const rangeMax = qty + (qty * overPct) / 100;

  return {
    rangeMin: null,
    rangeMax,
    rangeText: `≤ ${rangeMax.toFixed(2)}`
  };
 };

 const calculateLineTotals = (row, idx) => {
  const qty = Number(
    editableProducts[idx]?.requirement_qty ??
    row.requirement_qty ??
    0
  );

  const unitCost = Number(
    editableProducts[idx]?.product_cost ??
    row.product_cost ??
    0
  );

  const discount = Number(
    discounts[idx] ??
    row.discount ??
    0
  );

  // 🆕 Only trust calculatedAmounts' GST% if it actually resolved to
  // something non-zero. If cgst/sgst/igst are ALL zero there (whether
  // never set, or set by a server call that legitimately/silently came
  // back empty — no gst_configuration match, vendor mismatch, etc.), fall
  // back to the row's own cgst_percent/sgst_percent/igst_percent instead —
  // the exact same source the "View" popup already uses for unsaved rows.
  // Without this, a nullish-coalesce (`?? `) treats a real 0 from
  // calculatedAmounts as "present" and never falls through, so the table
  // could get stuck showing 0.00 while View (which doesn't depend on that
  // same server round trip) kept showing the correct %.
  const calc = calculatedAmounts[idx];
  const calcHasNonZeroGst =
    calc &&
    ((Number(calc.cgst) || 0) !== 0 ||
      (Number(calc.sgst) || 0) !== 0 ||
      (Number(calc.igst) || 0) !== 0);

  const cgstP = Number(
    calcHasNonZeroGst ? calc.cgst : (row.cgst_percent ?? 0)
  );

  const sgstP = Number(
    calcHasNonZeroGst ? calc.sgst : (row.sgst_percent ?? 0)
  );

  const igstP = Number(
    calcHasNonZeroGst ? calc.igst : (row.igst_percent ?? 0)
  );

  const totalCost = qty * unitCost;

  const netPrice =
    totalCost - (totalCost * discount) / 100;

  const cgstAmt =
    (netPrice * cgstP) / 100;

  const sgstAmt =
    (netPrice * sgstP) / 100;

  const igstAmt =
    (netPrice * igstP) / 100;

  const totalGST =
    cgstAmt + sgstAmt + igstAmt;

  const finalPrice =
    netPrice + totalGST;

  return {
    grossTotal: totalCost,
    netPrice,
    cgstAmt,
    sgstAmt,
    igstAmt,
    totalGST,
    finalPrice
  };
};

  const totals = (modalData && Array.isArray(modalData))
  ? modalData.reduce((acc, row, idx) => {
      const calc = calculateLineTotals(row, idx);

      return {
        gross: acc.gross + calc.grossTotal,
        net: acc.net + calc.netPrice,
        cgst: acc.cgst + calc.cgstAmt,
        sgst: acc.sgst + calc.sgstAmt,
        igst: acc.igst + calc.igstAmt,
        totalGst: acc.totalGst + calc.totalGST,
        grand: acc.grand + calc.finalPrice
      };
    }, {
      gross: 0,
      net: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      totalGst: 0,
      grand: 0
    })
  : {
      gross: 0,
      net: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      totalGst: 0,
      grand: 0
    };

  // 🆕 Port/Clearing + Bank total — both are PO-level (single row, key
  // "po_level" in landedCosts), same shape as the groupSubtotal calc used
  // inside the Customs/Port-Clearing/Bank tabs themselves (search
  // "groupSubtotal"). Summed here so the Product tab's totals row can show
  // it under Grand Total without duplicating that per-tab logic.
  const landedPoLevelEntry = landedCosts["po_level"] || {};
  const portClearingTotal = (LANDED_COST_GROUPS.find(g => g.tabId === "landed_port_clearing")?.fields || [])
    .reduce((sum, f) => sum + (Number(landedPoLevelEntry[f.key]) || 0), 0);
  const bankTotal = (LANDED_COST_GROUPS.find(g => g.tabId === "landed_bank")?.fields || [])
    .reduce((sum, f) => sum + (Number(landedPoLevelEntry[f.key]) || 0), 0);
  const portClearingPlusBankTotal = portClearingTotal + bankTotal;

  const formatCurrency = (value) => {
    const val = Number(value);
    if (isNaN(val)) return "0";
    return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // 🆕 Payment History should only be based on products that have actually
  // been APPROVED at the line level (row.review === "APPROVED") — not the
  // full PO total. Mirrors the same filter used on the Confirmation tab.
  const approvedTotals = (modalData && Array.isArray(modalData))
    ? modalData
        .map((row, idx) => ({ row, idx }))
        .filter(({ row }) => String(row.review || "").toUpperCase() === "APPROVED")
        .reduce((acc, { row, idx }) => {
          const calc = calculateLineTotals(row, idx);
          return {
            gross: acc.gross + calc.grossTotal,
            net: acc.net + calc.netPrice,
            cgst: acc.cgst + calc.cgstAmt,
            sgst: acc.sgst + calc.sgstAmt,
            igst: acc.igst + calc.igstAmt,
            totalGst: acc.totalGst + calc.totalGST,
            grand: acc.grand + calc.finalPrice
          };
        }, { gross: 0, net: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grand: 0 })
    : { gross: 0, net: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grand: 0 };

// 🆕 Populate the form above the table with an existing payment row's
// values so the user can edit it, and switch the form into "edit mode".
const handleEditPaymentClick = (row) => {
  setEditingPaymentId(row.id);
  setPaymentHistoryTabType(row.payment_type || row.paymentType || "Advance");
  setPaymentHistoryTabDate(
    row.payment_date ? String(row.payment_date).slice(0, 10) : paymentHistoryTabDate
  );
  setPaymentHistoryTabAmount(String(row.advance ?? row.amount ?? ""));
  setPaymentHistoryTabDescription(row.payment_description || row.paymentDescription || "");
  setPaymentHistoryTabReference(row.reference_number || row.referenceNumber || "");
};

// 🆕 Leave edit mode without saving changes.
const handleCancelEditPayment = () => {
  setEditingPaymentId(null);
  setPaymentHistoryTabAmount("");
  setPaymentHistoryTabDescription("");
  setPaymentHistoryTabReference("");
};

// 🆕 Rename an already-saved PO's number to a manually-typed value (e.g.
// to override a number that was auto-generated on PR conversion). Fires
// on blur from a plain, always-editable input rather than a separate
// edit toggle — see purchaseOrder.service.js renamePoNumber for the full
// list of related records this also updates (GRN, advance payments,
// notes, terms, logs).
const handleRenamePoNumber = async () => {
  const oldPoId = modalData?.[0]?.purchase_order_id;
  const newPoId = renamedPoNumber.trim();

  if (!oldPoId || !isEditMode) return;

  if (!newPoId) {
    // Don't allow clearing it out to blank — revert the display.
    setRenamedPoNumber(oldPoId);
    return;
  }
  if (newPoId === oldPoId) return;

  setSavingPoNumber(true);
  try {
    const res = await axios.put(
      `${PURCHASE_API}/po/${encodeURIComponent(oldPoId)}/po-number`,
      { newPoNumber: newPoId },
      { headers: authHeaders() }
    );

    if (res.data?.success) {
      setModalData(prev =>
        (prev || []).map(row => ({ ...row, purchase_order_id: newPoId }))
      );
      setPurchaseOrders(prev =>
        prev.map(po =>
          po.purchase_order_id === oldPoId
            ? { ...po, purchase_order_id: newPoId }
            : po
        )
      );
      toast.success(`PO Number changed to ${newPoId}`);
    } else {
      toast.error(res.data?.message || "Could not change PO Number.");
      setRenamedPoNumber(oldPoId); // revert display — nothing actually saved
    }
  } catch (err) {
    console.error("Rename PO Number error:", err);
    toast.error(err.response?.data?.message || "Could not change PO Number.");
    setRenamedPoNumber(oldPoId); // revert display — nothing actually saved
  } finally {
    setSavingPoNumber(false);
  }
};

// Keep the rename field's local value in sync with whichever PO is
// currently open (including switching between POs without closing the
// modal), so it never shows a stale number from a previously viewed PO.
useEffect(() => {
  const currentId = modalData?.[0]?.purchase_order_id;
  if (currentId) {
    setRenamedPoNumber(currentId);
  }
}, [modalData?.[0]?.purchase_order_id]);

// ── Insert a blank product row BELOW row `idx` (the "+" icon on each row) ──
  // Shifts every index-keyed structure so existing rows keep their data.
  const shiftRowKeysUp = (obj, at) => {
    const next = {};
    Object.keys(obj || {}).forEach(k => {
      if (/^\d+$/.test(k)) {
        const ki = parseInt(k, 10);
        next[ki >= at ? ki + 1 : ki] = obj[k];
      } else if (/^idx_\d+$/.test(k)) {            // unsaved-row keys (row.id || `idx_${idx}`)
        const ki = parseInt(k.slice(4), 10);
        next[ki >= at ? `idx_${ki + 1}` : k] = obj[k];
      } else {
        next[k] = obj[k];
      }
    });
    return next;
  };
  const insertPORowAfter = (idx) => {
    const at = idx + 1;
    const blankModal = {
      product_code: "", description: "", uom: "", requirement_qty: "",
      product_cost: "", discount: 0, status: "PENDING",
      ...(activePoIdRef.current ? { purchase_order_id: activePoIdRef.current } : {})
    };
    const blankEditable = { product_cost: "", requirement_qty: "", delivery_date: "" };
    setModalData(prev => [...prev.slice(0, at), blankModal, ...prev.slice(at)]);
    setEditableProducts(prev => [...prev.slice(0, at), blankEditable, ...prev.slice(at)]);
    setDiscounts(prev => shiftRowKeysUp(prev, at));
    setCalculatedAmounts(prev => shiftRowKeysUp(prev, at));
    setLandedCosts(prev => shiftRowKeysUp(prev, at));
    setConfirmationLines(prev => shiftRowKeysUp(prev, at));
  };

const handleAddPaymentRow = async () => {
  const poId = modalData?.[0]?.purchase_order_id;

  if (!poId) {
    console.log("PO ID missing");
    return;
  }

  if (!paymentHistoryTabAmount) return toast.error("Please enter an amount.");

  // 🆕 Use only the APPROVED-line total for payment calculations, not the
  // full PO total (unapproved/rejected lines shouldn't be billed against).
  const totalGrand = approvedTotals.grand;

  // When editing, exclude the row being edited from the "already paid"
  // total so the remaining-balance check reflects the amount being replaced.
  const currentTotalPaid = advancePayments.reduce((sum, p) => {
    if (editingPaymentId && p.id === editingPaymentId) return sum;
    return sum + Number(p.advance || p.amount || 0);
  }, 0);
  const remainingBalance = totalGrand - currentTotalPaid;

  if (remainingBalance <= 0) {
    return toast.error("No balance remaining. Payment is already complete.");
  }
  if (Number(paymentHistoryTabAmount) > remainingBalance) {
    return toast.error(
      `Amount cannot exceed remaining balance of ${currencySymbol}${formatCurrency(remainingBalance)}.`
    );
  }

  try {
    const res = editingPaymentId
      ? await axios.put(`${PURCHASE_API}/po/advance-payments/${poId}/${editingPaymentId}`, {
            total: totalGrand,
            advance: Number(paymentHistoryTabAmount),
            paymentType: paymentHistoryTabType,
            paymentDate: paymentHistoryTabDate,
            paymentDescription: paymentHistoryTabDescription,
            referenceNumber: paymentHistoryTabReference
          }, { headers: authHeaders() })
      : await axios.post(`${PURCHASE_API}/po/advance-payments/${poId}`, {
            total: totalGrand,
            advance: Number(paymentHistoryTabAmount),
            paymentType: paymentHistoryTabType,
            paymentDate: paymentHistoryTabDate,
            paymentDescription: paymentHistoryTabDescription,
            referenceNumber: paymentHistoryTabReference
          }, { headers: authHeaders() });

    if (res.data.success) {
      await fetchAdvancePayments(poId);
      setPaymentHistoryTabAmount("");
      setPaymentHistoryTabDescription("");
      setPaymentHistoryTabReference("");
      toast.success(editingPaymentId ? "Payment updated!" : "Payment recorded!");
      setEditingPaymentId(null);
    }
  } catch (err) {
    console.error("Full Error:", err);
    console.error("Response:", err.response?.data);
    console.error("Status:", err.response?.status);

    toast.error(editingPaymentId ? "Failed to update payment." : "Failed to add payment.");
  }
};
// 🆕 Discard unsaved edits to the box and restore it to the latest saved
// acknowledgement. Keeps editingAckId pointing at that saved row so the
// next Save still updates it (PUT) rather than creating a duplicate (POST).
const handleCancelEditAcknowledgement = () => {
  if (acknowledgements.length > 0) {
    const latest = acknowledgements.reduce((best, row) => {
      if (!best) return row;
      const bestTime = new Date(best.acknowledgement_date || 0).getTime();
      const rowTime = new Date(row.acknowledgement_date || 0).getTime();
      return rowTime >= bestTime ? row : best;
    }, null);
    setAckNoInput(latest.acknowledgement_no || "");
    setAckDateInput(
      latest.acknowledgement_date ? String(latest.acknowledgement_date).slice(0, 10) : ackDateInput
    );
    setEditingAckId(latest.id);
  } else {
    setEditingAckId(null);
    setAckNoInput("");
  }
};

// 🆕 Save (add or update) an acknowledgement row. Once saved, the PO list
// column flips from "Pending" to "Acknowledged" (derived server-side from
// whether any row exists for this PO).
const handleSaveAcknowledgement = async () => {
  const poId = modalData?.[0]?.purchase_order_id;

  if (!poId) {
    console.log("PO ID missing");
    return;
  }

  if (!ackNoInput) return toast.error("Please enter an Acknowledgement No.");

  try {
    const res = editingAckId
      ? await axios.put(`${PURCHASE_API}/po/acknowledgements/${poId}/${editingAckId}`, {
            acknowledgementNo: ackNoInput,
            acknowledgementDate: ackDateInput
          }, { headers: authHeaders() })
      : await axios.post(`${PURCHASE_API}/po/acknowledgements/${poId}`, {
            acknowledgementNo: ackNoInput,
            acknowledgementDate: ackDateInput
          }, { headers: authHeaders() });

    if (res.data.success) {
      const wasUpdate = !!editingAckId;
      await fetchAcknowledgements(poId); // 🆕 re-populates the box and editingAckId with the saved value
      toast.success(wasUpdate ? "Acknowledgement updated!" : "Acknowledgement saved!");
      // Keep the PO list's Acknowledgement column in sync without a full reload
      setPurchaseOrders(prev => prev.map(po =>
        po.purchase_order_id === poId ? { ...po, acknowledgement_status: "Acknowledged" } : po
      ));
      setPoList(prev => prev.map(po =>
        po.purchase_order_id === poId ? { ...po, acknowledgement_status: "Acknowledged" } : po
      ));
      setAllPurchaseOrders(prev => prev.map(po =>
        po.purchase_order_id === poId ? { ...po, acknowledgement_status: "Acknowledged" } : po
      ));
    }
  } catch (err) {
    console.error("Full Error:", err);
    console.error("Response:", err.response?.data);
    console.error("Status:", err.response?.status);

    toast.error(editingAckId ? "Failed to update acknowledgement." : "Failed to save acknowledgement.");
  }
};

//eslint-disable-next-line
  const handleDownloadPDF = async () => {
    if (!modalData || modalData.length === 0) return;
    const poId = modalData?.[0].purchase_order_id;
    const downloadUrl =
      `${PURCHASE_API}/po/download-pdf/${poId}?quote_no=${encodeURIComponent(quoteNo || "")}&quote_date=${quoteDate || ""}&generated_by=${encodeURIComponent(getCurrentUser())}`;

    try {
      toast.info("Generating PDF... Please wait.");
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error("Error downloading PDF", error);
      toast.error("Failed to download PDF.");
    }
  };



  // No role, or a role with no entry for PURCHASE_ORDER at all -> no access
  // to this screen. (Admin / no-role-assigned / any granted permission_type
  // all satisfy hasModuleAccess — see useFieldPermissions.)
  if (!permLoading && !hasModuleAccess) {
    return (
      <Layout>
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
          You don't have access to Purchase Orders.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{`
        .po-field-tooltip[data-tooltip=""]::after,
        .po-field-tooltip:not([data-tooltip])::after {
          content: none;
        }
        .po-field-tooltip::after {
          content: attr(data-tooltip);
          position: absolute;
          left: 0;
          top: calc(100% + 6px);
          background: #1e293b;
          color: #fff;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          line-height: 1.4;
          white-space: normal;
          max-width: 320px;
          width: max-content;
          z-index: 100000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.18);
          opacity: 0;
          visibility: hidden;
          transform: translateY(-4px);
          transition: opacity 0.12s ease, transform 0.12s ease;
          pointer-events: none;
        }
        .po-field-tooltip:hover::after {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        @media (max-width: 768px) {
          .po-filter-card .row > div {
            flex: 0 0 100%;
            max-width: 100%;
          }
          .po-page-title { font-size: 20px !important; }
          .po-modal { padding: 10px !important; }
          .po-tabs { flex-wrap: wrap; }
          .po-tabs button { font-size: 12px !important; padding: 3px 7px !important; }
        }
        @media (max-width: 576px) {
          .po-modal-body { padding: 10px !important; }
          .po-vendor-row > div { flex: 0 0 100% !important; max-width: 100% !important; }
        }
        .po-table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .po-modal-backdrop { overflow-y: auto; }
      `}</style>
      <div style={styles.pageTitleBar}>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >

          <div>
            <h2 style={styles.pageTitle} className="po-page-title">
              Purchase Order
            </h2>

            <div style={styles.pageSubtitle}>
              Manage and review purchase orders
            </div>
          </div>
<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>

          {canCreatePO && (
          <button
            className="btn btn-dark"
            onClick={() => {
              // Reset all carry-over state before opening a fresh Create modal
              setSelectedVendor("");
              setVendorDetails(null);
              setPoLocation("");

              setPaymentTermNumber("");
              setPaymentTermUnit("days");
              setDiscounts({});
              setCalculatedAmounts({});
              setGstColumnLabels(DEFAULT_GST_COLUMN_LABELS);
              setIsPoSaved(false);
              setCreateActiveTab("basic");

              // ✅ ADD THESE TWO LINES — clear quote fields from previous PO
              setQuoteNo("");
              setQuoteDate("");

              // 🆕 Clear any manually-typed PO Number from a previous Create
              setPoManualNumber("");
              setRenamedPoNumber("");
              setPoRevisionNo("0");
              setPoContactName("");
              setPoContactEmail("");
              setPoContactPhone("");

              // ✅ Auto-populate Created By / Created Date for a brand-new PO
              setPoCreatedBy(currentUserName);
              setPoCreatedDate(getTodayLocal());
              setPoApprovedBy("");
              setPoApprovedDate("");
              setPoProductType("");

              setModalData([
                {
                  purchase_organization: createPurchaseOrganization,
                  purchase_planner: createPurchasePlanner,
                  product_code: "",
                  description: "",
                  uom: "",
                  requirement_qty: "",
                  product_cost: "",
                  discount: 0,
                  status: "DRAFT"
                }
              ]);

              setEditableProducts([
                {
                  product_cost: "",
                  requirement_qty: "",
                  delivery_date: ""
                }
              ]);

              setShowModal(true);
              setIsEditMode(true);
            }}
          >
            + Create Purchase Order
          </button>
          )}

            <button
              onClick={() => {
                setManualExists(null);
                setManualFile(null);
                setShowManual(true);
                fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                  .then((res) => res.json())
                  .then((data) => {
                    const norm = (v) => String(v || "").trim().toLowerCase();
                    const match = (data?.data || []).find((m) =>
                      norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                      norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                      norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                    );
                    if (match && match.drive_link) {
                      setManualFile(match);
                      setManualExists(true);
                    } else {
                      setManualExists(false);
                    }
                  })
                  .catch(() => setManualExists(false));
              }}
    title="View User Manual"
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      padding: "10px 14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      color: "#3b82f6",
      fontSize: "14px",
      fontWeight: "600",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
  >
    <FileText size={18} />
    User Manual
  </button>
</div>
        </div>
      </div>

      {showManual && (
  <div
    onClick={() => setShowManual(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100,
    }}
  >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              width: "90vw",
              height: "97vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
            }}
          >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
      }}>
        <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
          User Manual
        </h4>
        <button
          onClick={() => setShowManual(false)}
          style={{
            border: "none", background: "#f1f5f9", borderRadius: "8px",
            width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
            color: "#475569", lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {manualExists === null && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
          Checking for user manual...
        </div>
      )}

      {manualExists === false && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
        }}>
          <FileText size={40} color="#cbd5e1" />
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
            User Manual Not Available
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
            The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
          </div>
        </div>
      )}

            {manualExists === true && manualFile && (() => {
              const MANUAL_SCALE = 1.25;
              return (
                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                  <iframe
                    src={manualFile.drive_link.replace("/view", "/preview")}
                    title={manualFile.file_name || "User Manual"}
                    allow="autoplay"
                    style={{
                      border: "none",
                      width: `${100 / MANUAL_SCALE}%`,
                      height: `${100 / MANUAL_SCALE}%`,
                      transform: `scale(${MANUAL_SCALE})`,
                      transformOrigin: "top left",
                    }}
                  />
                </div>
              );
            })()}
    </div>
  </div>
)}

      {showExpiredOnly && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
          padding: "10px 16px", marginBottom: 16, color: "#991b1b", fontWeight: 500
        }}>
          <span>Showing Purchase Orders with expired validity only</span>
          <button
            onClick={() => setShowExpiredOnly(false)}
            style={{
              background: "transparent", border: "1px solid #991b1b", color: "#991b1b",
              borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 13
            }}
          >
            Clear
          </button>
        </div>
      )}

      {!showExpiredOnly && (
      <div style={styles.filterCard} className="po-filter-card">
        <div className="row g-2">
          {/* Product Type */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Product Type</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={productTypeOptions}
              value={selectedProductType}
              onChange={(opt) => {
                // Options below are scoped to the Product Type, so drop
                // anything picked under the previous type.
                setSelectedProductType(opt);
                setListCategory(null); setListSubCategory(null); setListProductCode(null);
                setSelectedConstruction(null); setSelectedCode(null); setSelectedCableType(null);
                setSelectedCableCategory(null); setSelectedCores(null); setSelectedColor(null);
                setSelectedCrossSection(null); setSelectedStdRef(null); setSelectedVoltage(null);
              }}
            />
          </div>
        </div>

        {/* ── Cable attribute filters — only when the PO list has cable products ── */}
        {showCableFilters && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(9, minmax(80px, 1fr))", gap: 8, marginTop: 12 }}>
          <div>
            <label style={styles.filterLabel}>Construction</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Construction"
              options={constructionOptions} value={selectedConstruction} onChange={setSelectedConstruction} />
          </div>
          <div>
            <label style={styles.filterLabel}>Code</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Code"
              options={codeOptions} value={selectedCode} onChange={setSelectedCode} />
          </div>
          <div>
            <label style={styles.filterLabel}>Cable Type</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Cable Type"
              options={cableTypeOptions} value={selectedCableType} onChange={setSelectedCableType} />
          </div>
          <div>
            <label style={styles.filterLabel}>Category</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Category"
              options={cableCategoryOptions} value={selectedCableCategory} onChange={setSelectedCableCategory} />
          </div>
          <div>
            <label style={styles.filterLabel}>Cores</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Cores"
              options={coresOptions} value={selectedCores} onChange={setSelectedCores} />
          </div>
          <div>
            <label style={styles.filterLabel}>Color</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Color"
              options={colorOptions} value={selectedColor} onChange={setSelectedColor} />
          </div>
          <div>
            <label style={styles.filterLabel}>Cross Section</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Cross Section"
              options={crossSectionOptions} value={selectedCrossSection} onChange={setSelectedCrossSection} />
          </div>
          <div>
            <label style={styles.filterLabel}>Std. Ref</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Std. Ref"
              options={stdRefOptions} value={selectedStdRef} onChange={setSelectedStdRef} />
          </div>
          <div>
            <label style={styles.filterLabel}>Voltage</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Voltage"
              options={voltageOptions} value={selectedVoltage} onChange={setSelectedVoltage} />
          </div>
        </div>
        )}

        {/* ── Raw Material / non-cable types: Category -> Sub Category (cascading) ── */}
        {!showCableFilters && (
        <div className="row g-2 mt-1">
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Category</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Category"
              options={listCategoryOptions} value={listCategory}
              onChange={(opt) => { setListCategory(opt); setListSubCategory(null); }} />
          </div>
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Sub Category</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Sub Category"
              options={listSubCategoryOptions} value={listSubCategory} onChange={setListSubCategory} />
          </div>
        </div>
        )}

        <div className="row g-2 mt-1">

          {/* Product / Asset Code */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>{selectedProductType?.value === "Asset" ? "Asset Code" : "Product Code"}</label>
            <Select {...rsMenuProps} styles={rsFilter} isClearable placeholder="Select"
              options={listProductCodeOptions} value={listProductCode} onChange={setListProductCode} />
          </div>

          {/* Purchase Organization */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Purchase Organization</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={purchaseOrganizations.map((item) => ({
                value: item.name || item.purchase_org || item,
                label: item.name || item.purchase_org || item,
              }))}
              value={selectedPurchaseOrganization
                ? { value: selectedPurchaseOrganization, label: selectedPurchaseOrganization }
                : null}
              onChange={(opt) => setSelectedPurchaseOrganization(opt?.value || "")}
            />
          </div>

          {/* Purchase Order ID */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Purchase Order ID</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={purchaseOrders.map((po) => ({
                value: po.purchase_order_id,
                label: po.purchase_order_id,
              }))}
              value={selectedPurchaseOrderId
                ? { value: selectedPurchaseOrderId, label: selectedPurchaseOrderId }
                : null}
              onChange={(opt) => setSelectedPurchaseOrderId(opt?.value || "")}
            />
          </div>

          {/* Vendor */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Supplier</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={vendors.map((v) => ({ value: v.name, label: v.name }))}
              value={filterVendor ? { value: filterVendor, label: filterVendor } : null}
              onChange={(opt) => setFilterVendor(opt?.value || "")}
            />
          </div>

          {/* Status */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Status</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={poStatusFilterOptions.map((s) => ({ value: s, label: s }))}
              value={selectedPoStatus ? { value: selectedPoStatus, label: selectedPoStatus } : null}
              onChange={(opt) => setSelectedPoStatus(opt?.value || "")}
            />
          </div>
        </div>
      </div>
      )}


      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2 }}>
          <button
            style={{ border: "none", borderRadius: 6, padding: "5px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: listTab === "active" ? "#fff" : "transparent",
              color: listTab === "active" ? "#1e293b" : "#64748b",
              boxShadow: listTab === "active" ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
            }}
            onClick={() => setListTab("active")}
          >Active</button>
          <button
            style={{ border: "none", borderRadius: 6, padding: "5px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: listTab === "closed" ? "#fff" : "transparent",
              color: listTab === "closed" ? "#1e293b" : "#64748b",
              boxShadow: listTab === "closed" ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
            }}
            onClick={() => setListTab("closed")}
          >Closed</button>
          <button
            style={{ border: "none", borderRadius: 6, padding: "5px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: listTab === "short_closed" ? "#fff" : "transparent",
              color: listTab === "short_closed" ? "#1e293b" : "#64748b",
              boxShadow: listTab === "short_closed" ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
            }}
            onClick={() => setListTab("short_closed")}
          >Short Closed</button>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <table className="table" style={styles.erpTable}>
          <thead>
            <tr>
              <th style={{ ...styles.erpTh, width: 200 }}>
                PO No
              </th>

              <th style={{ ...styles.erpTh, width: 220 }}>
                Supplier
              </th>

              <th style={{ ...styles.erpTh, width: 200 }}>
                Purchase Organization
              </th>

              <th style={{ ...styles.erpTh, width: 150 }}>
              Total Value
              </th>
<th style={{ ...styles.erpTh, width: 130 }}>
  Creation Date
</th>
              <th style={{ ...styles.erpTh, width: 120 }}>
                Status
              </th>
              <th style={{ ...styles.erpTh, width: 130 }}>
                Acknowledgement
              </th>
              <th style={{ ...styles.erpTh, width: 100, textAlign: "center" }}>
              </th>
            </tr>
          </thead>

          <tbody>
            {poList.filter(matchesListTab).length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  style={{
                    ...styles.erpTd,
                    textAlign: "center",
                    padding: 20
                  }}
                >
                  No records found
                </td>
              </tr>
            ) : (
              poList.filter(matchesListTab).map((row, idx) => {
                const isExpired = expiredKeys.has(row.purchase_order_id);
                // Bootstrap's .table class paints background-color on each
                // <td>, not the <tr> — so setting it only on <tr> gets
                // painted over (same fix as RFQ/Sales Order/Purchase
                // Requisition list pages). Merge into every cell's style.
                const rowCellStyle = {
                  ...styles.erpTd,
                  backgroundColor: isExpired
                    ? "#fde8e8"
                    : (POSTING_ROW_BG[row.posting_status] || POSTING_ROW_BG.NOT_POSTED)
                };
                return (
                <tr key={idx}>
                  <td style={{ ...rowCellStyle, fontWeight: 600 }}>
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        color: "#1d4ed8",
                        textDecoration: "underline",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                      onClick={() => viewPO(row.purchase_order_id)}
                    >
                      {row.purchase_order_id}
                    </button>
                  </td>

                  <td style={rowCellStyle}>
                    {row.vendor_name}
                  </td>

                  <td style={rowCellStyle}>
                    {row.purchase_organization}
                  </td>

                  <td style={rowCellStyle}>
                    {resolveCurrencySymbol(
                      String(row.order_type || "").toLowerCase() === "import" && row.import_currency
                        ? row.import_currency
                        : "INR"
                    )} {Number(row.final_price || 0).toLocaleString("en-IN")}
                  </td>
                  <td style={{ ...rowCellStyle, whiteSpace: "nowrap" }}>
  {formatDateDMY(row.created_at)}
</td>

                  <td style={rowCellStyle}>
                    {getPoStatusLabel(row)}
                  </td>
                  <td style={rowCellStyle}>
                    {String(row.acknowledgement_status || "").toUpperCase() === "ACKNOWLEDGED"
                      ? "Acknowledged"
                      : "Pending"}
                  </td>
                  <td style={{ ...rowCellStyle, textAlign: "center" }}>
                    <button
                      className="btn btn-outline-dark btn-sm py-0"
                      style={{ fontSize: "12px" }}
                      onClick={() => viewPO(row.purchase_order_id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>



      {showModal && modalData && (
        <div style={styles.modalBackdrop} className="po-modal-backdrop">
          <div
            style={{
              ...styles.modal,
              maxWidth: "1200px",
              width: "90%",
              maxHeight: "90vh",
              padding: "16px",
              fontSize: "14px"   // ⭐ GLOBAL COMPACT FONT (PR STYLE)
            }}
          >
            {/* ================= HEADER ================= */}
            <div style={{ ...styles.modalHeader, padding: "10px 14px" }}>
              <div style={{ fontWeight: 600, fontSize: "18px" }}>
                Purchase Order : {modalData?.[0]?.purchase_order_id || ""}
              </div>
              <button style={styles.iconBtn} onClick={closeModal}>✕</button>
            </div>

            {/* ================= BODY ================= */}
            <div style={{ ...styles.modalBody, padding: "14px" }} className="po-modal-body">

              {/* ===== ROW 1: Product Type / Order Type ===== */}
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Product Type</label>
                  {isFieldVisible("product_type") ? (
                    <Select
                      {...rsMenuProps}
                      styles={rsCompact}
                      options={productTypeOptions}
                      value={productTypeOptions.find(o => o.value === poProductType) || null}
                      onChange={(opt) => setPoProductType(opt?.value || "")}
                      isDisabled={!isEditMode}
                      placeholder="Select"
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* 🆕 Order Type — Domestic PO / Import PO */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Order Type</label>
                  <Select
                    {...rsMenuProps}
                    styles={rsCompact}
                    options={orderTypeOptions}
                    value={orderTypeOptions.find(o => o.value === poOrderType) || orderTypeOptions[0]}
                    onChange={(opt) => setPoOrderType(opt?.value || "domestic")}
                    isDisabled={!isEditMode}
                    placeholder="Select Order Type"
                  />
                </div>

                {/* 🆕 PO Number — optional manual override. Only editable
                    before the PO has been saved (no purchase_order_id yet);
                    once saved, the real ID is shown read-only here and in
                    the modal title, same as Created By / Approved By above. */}
                <div className="col-md-4">
                  <div className="row g-2">
                    <div className="col-8">
                  <label style={styles.compactLabel}>PO Number</label>
                  {!modalData?.[0]?.purchase_order_id ? (
                    <input
                      className="form-control"
                      style={styles.compactInput}
                      value={poManualNumber}
                      onChange={(e) => setPoManualNumber(e.target.value)}
                      disabled={!isEditMode}
                      placeholder="Auto-generated if left blank"
                    />
                  ) : (
                    /* 🆕 Editable like every other field here — changing it
                       and moving focus away (blur / Enter) fires its own
                       save via handleRenamePoNumber, since purchase_order_id
                       is a lookup key the main Save button's flow can't
                       just patch inline. Reverts to the saved value if that
                       call fails. */
                    <input
                      className="form-control"
                      style={styles.compactInput}
                      value={renamedPoNumber}
                      onChange={(e) => setRenamedPoNumber(e.target.value)}
                      onBlur={handleRenamePoNumber}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                      }}
                      disabled={!isEditMode || savingPoNumber}
                    />
                  )}
                    </div>
                    {/* 🆕 PO Revision No. — beside PO Number */}
                    <div className="col-4">
                      <label style={styles.compactLabel}>PO Revision No.</label>
                      <input
                        className="form-control"
                        style={styles.compactInput}
                        value={poRevisionNo}
                        onChange={(e) => setPoRevisionNo(e.target.value.slice(0, 10))}
                        disabled={!isEditMode}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== ROW 2: Category / Sub Category / Created By / Approved By ===== */}
              <div className="row g-2 mb-3">
               
                {/* Created By */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Created By</label>
                  {isFieldVisible("created_by") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={poCreatedBy}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Created Date */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Created Date</label>
                  {isFieldVisible("created_date") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={formatDateDMY(poCreatedDate)}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Approved By */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Approved By</label>
                  {isFieldVisible("approved_by") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={poApprovedBy}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Approved Date */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Approved Date</label>
                  {isFieldVisible("approved_date") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={formatDateDMY(poApprovedDate)}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>
              </div>

              {/* ===== BASIC HEADER INFO ===== */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Purchase Organization</label>
                  {isFieldVisible("purchase_organization") ? (
                    <CreatableSelect
                      {...rsMenuProps}
                      styles={rsCompact}
                      menuShouldScrollIntoView={false}
                      isClearable
                      placeholder="Select or type Purchase Organization"
                      isDisabled={!isEditMode}
                      options={purchaseOrganizations.map((item) => ({
                        value: item.name || item,
                        label: item.name || item,
                      }))}
                      value={modalData?.[0]?.purchase_organization
                        ? { value: modalData?.[0].purchase_organization, label: modalData?.[0].purchase_organization }
                        : null}
                      onChange={(opt) => {
                        const newOrg = opt?.value || "";
                        const updated = modalData.map((row, i) =>
                          i === 0 ? { ...row, purchase_organization: newOrg } : row
                        );
                        setModalData(updated);
                        // 🆕 Purchase Organization is the other half of the
                        // (vendor, purchaseOrg) pair needed to resolve GST —
                        // if a Supplier was already picked before this was
                        // filled in, refetch now instead of leaving CGST/
                        // SGST/IGST stuck at 0 until the Supplier is re-picked.
                        refetchLineGst(selectedVendor, newOrg);
                      }}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                <div className="col-md-4">
                  <label style={styles.compactLabel}>Purchase Planner</label>
                  {isFieldVisible("purchase_planner") ? (
                    <CreatableSelect
                      {...rsMenuProps}
                      styles={rsCompact}
                      menuShouldScrollIntoView={false}
                      isClearable
                      placeholder="Select or type Purchase Planner"
                      isDisabled={!isEditMode}
                      options={purchasePlanners.map((item) => ({
                        value: item.name || item,
                        label: item.name || item,
                      }))}
                      value={modalData?.[0]?.purchase_planner
                        ? { value: modalData?.[0].purchase_planner, label: modalData?.[0].purchase_planner }
                        : null}
                      onChange={(opt) => {
                        const updated = modalData.map((row, i) =>
                          i === 0 ? { ...row, purchase_planner: opt?.value || "" } : row
                        );
                        setModalData(updated);
                      }}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                <div className="col-md-4">
                  <label style={styles.compactLabel}>Location</label>
                  {isFieldVisible("location") ? (
                    <CreatableSelect
                      {...rsMenuProps}
                      styles={rsCompact}
                      menuShouldScrollIntoView={false}
                      isClearable
                      placeholder="Select or type Location"
                      isDisabled={!isEditMode}
                      options={locationOptions.map((loc) => {
                        const v = typeof loc === "string" ? loc : (loc.name || loc.location || loc.value || "");
                        return { value: v, label: v };
                      })}
                      value={poLocation ? { value: poLocation, label: poLocation } : null}
                      onChange={(opt) => setPoLocation(opt?.value || "")}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>
              </div>

              {/* ===== VENDOR ===== */}
              <div className="row g-3 mb-3 po-vendor-row">

                {/* Supplier */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Supplier</label>
                  {isFieldVisible("vendor") ? (
                    <FieldWithTooltip
                      tooltip={
                        selectedVendor
                          ? (vendors.find(v => v.id === selectedVendor)?.name || selectedVendor)
                          : ""
                      }
                    >
                      <CreatableSelect
                        {...rsMenuProps}
                        styles={rsCompact}
                        menuShouldScrollIntoView={false}
                        isClearable
                        placeholder="Select or type Supplier"
                        isDisabled={!isEditMode}
                       options={vendors.map((v) => ({
    value: v.id,
    label: `${v.name} - ${v.id}`
  }))}
                        value={
                          selectedVendor
                            ? {
                              value: selectedVendor,
                              label:
                                vendors.find(v => v.id === selectedVendor)?.name ||
                                selectedVendor
                            }
                            : null
                        }
                        onChange={(opt) =>
                          handleVendorSelect(opt?.value || "")
                        }
                      />
                    </FieldWithTooltip>
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Quote No */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Quote No</label>
                  {isFieldVisible("quote_no") ? (
                    <input
                      type="text"
                      className="form-control"
                      style={styles.compactInput}
                      value={quoteNo}
                      onChange={(e) => setQuoteNo(e.target.value)}
                      disabled={!isEditMode}
                      placeholder="Enter Quote No"
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Quote Date */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Quote Date</label>
                  {isFieldVisible("quote_date") ? (
                    <DateInputDDMMYYYY
                      style={styles.compactInput}
                      value={quoteDate}
                      onChange={(val) => setQuoteDate(val)}
                      disabled={!isEditMode}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

              </div>

              {/* ================= CONTACT DETAILS (mandatory) ================= */}
              <div style={{ fontWeight: 600, fontSize: "13px", margin: "2px 0 4px" }}>
                Contact Details <span style={{ color: "#dc2626" }}>*</span>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Name <span style={{ color: "#dc2626" }}>*</span></label>
                  <Select
                    {...rsMenuProps}
                    styles={rsCompact}
                    isClearable
                    isDisabled={!isEditMode}
                    placeholder="Select Name"
                    options={poContactOptions.map((c) => ({
                      value: c.name,
                      label: c.name,
                      email: c.email || "",
                      phone: c.phone_number || "",
                    }))}
                    value={poContactName ? { value: poContactName, label: poContactName } : null}
                    onChange={(opt) => {
                      setPoContactName(opt?.value || "");
                      setPoContactEmail(opt?.email || "");
                      setPoContactPhone(opt?.phone || "");
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Email Id</label>
                  <input
                    className="form-control"
                    style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                    value={poContactEmail}
                    readOnly
                  />
                </div>
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Contact No.</label>
                  <input
                    className="form-control"
                    style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                    value={poContactPhone}
                    readOnly
                  />
                </div>
              </div>

              {/* Import Details header-level fields have been moved into the Customs tab (see LANDED_COST_TAB_IDS render block below) */}

              {/* ================= TABS ================= */}
              <div style={{ ...styles.tabs, marginBottom: "10px" }} className="po-tabs">
                {["basic", "product", ...LANDED_COST_TAB_IDS, "review", "confirmation",  "payment_history", "acknowledgement", "docs", "change_log" ] // 🆕 Acknowledgement tab, right after Payment History; 🆕 5 Landed Cost category tabs, right after Product (Import PO only)
                  .filter(tab => tab !== "review" || isPoSaved)
                  // 🆕 Not reviewable until Submit moves it out of "Created"
                  // (see submitPurchaseOrder) — hide the Review tab till then.
                  .filter(tab => tab !== "review" || (modalData?.[0]?.status || "").toUpperCase().trim() !== "CREATED")
                  // Landed Cost category tabs are only meaningful for Import POs.
                  .filter(tab => !LANDED_COST_TAB_IDS.includes(tab) || poOrderType === "import")
                  // Review-only role: only the Review tab is reachable — Basic,
                  // Product, Docs, Confirmation, Payment History, Acknowledgement, Change Log hidden.
                  .filter(tab => !isReviewOnly || tab === "review")
                  // Convert-only role: only the Confirmation tab (this module's
                  // convert action) is reachable.
                  .filter(tab => !isConvertOnly || tab === "confirmation")
                  .map(tab => (
                    <button
                      key={tab}
                      className={`btn ${createActiveTab === tab ? "btn-secondary" : "btn-light"}`}
                      style={styles.compactTabBtn}
                      onClick={() => setCreateActiveTab(tab)}
                    >
                      {tab === 'change_log'
                        ? 'Change Log'
                        : tab === 'payment_history'
                          ? 'Payment History'
                          : tab === 'acknowledgement'
                            ? 'Acknowledgement'
                            : LANDED_COST_TAB_IDS.includes(tab)
                              ? LANDED_COST_GROUPS.find(g => g.tabId === tab).group
                              : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
              </div>

              {/* ================= BASIC TAB ================= */}
              {createActiveTab === "basic" && (
                <div className="row g-3">

                  {/* Payment Terms */}
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>
                      Payment Terms
                    </label>

                    {isFieldVisible("payment_terms") ? (
                      <div className="d-flex gap-2">

                        {/* Dropdown fed by Configuration > Payment Terms. A value
                            saved earlier that isn't in the master any more (old
                            free-typed text) still shows as the current selection. */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            placeholder="Select..."
                            isClearable
                            options={paymentTermsOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              paymentTerms
                                ? { value: paymentTerms, label: paymentTerms }
                                : null
                            }
                            onChange={(opt) => setPaymentTerms(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        </div>

                        {/* Long terms get cut off in the dropdown — View shows the
                            full selected text (read-only). */}
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() => setShowPaymentModal(true)}
                          disabled={!paymentTerms}
                        >
                          View
                        </button>

                      </div>
                    ) : (
                      <MaskedValue />
                    )}
                  </div>

                  {/* 🆕 Terms & Conditions — same field+View layout as Payment
                      Terms, but purely typed by the user: no vendor/business
                      partner auto-fill. Disabled until the PO is saved
                      (purchase_order_id exists), since it's persisted via its
                      own per-PO endpoint rather than the main PO save. NOTE:
                      "terms_and_conditions" is a new field-permission key —
                      add it in Configuration > Field Permissions for
                      non-admin roles to see this field. */}
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>
                      Terms &amp; Conditions
                    </label>

                    {isFieldVisible("terms_and_conditions") ? (
                      <div className="d-flex gap-2">

                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={termsAndConditions}
                          onChange={(e) => setTermsAndConditions(e.target.value)}
                          disabled={!isEditMode}
                        />

                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() => setShowTermsModal(true)}
                          disabled={!isEditMode}
                        >
                          View
                        </button>

                      </div>
                    ) : (
                      <MaskedValue />
                    )}
                  </div>

                  {/* Logistics fields — now shown for both Domestic PO and Import PO */}
                  {(
                    <>
                      {/* Mode Of Transport */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Mode Of Transport</label>
                        {isFieldVisible("mode_of_transport") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={modeOfTransportOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              modeOfTransport
                                ? { value: modeOfTransport, label: modeOfTransport }
                                : null
                            }
                            onChange={(opt) => setModeOfTransport(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                      {/* Delivery Type */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Delivery Type</label>
                        {isFieldVisible("delivery_type") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={deliveryTypeOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              deliveryType
                                ? { value: deliveryType, label: deliveryType }
                                : null
                            }
                            onChange={(opt) => setDeliveryType(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                      {/* Freight Terms */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Freight Terms</label>
                        {isFieldVisible("freight_terms") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={freightTermsOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              freightTerms
                                ? { value: freightTerms, label: freightTerms }
                                : null
                            }
                            onChange={(opt) => setFreightTerms(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                      {/* Insurance Terms */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Insurance Terms</label>
                        {isFieldVisible("insurance_terms") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={insuranceTermsOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              insuranceTerms
                                ? { value: insuranceTerms, label: insuranceTerms }
                                : null
                            }
                            onChange={(opt) => setInsuranceTerms(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                    </>
                  )}

                  {/* PO Header Lot Type */}
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>PO Header Lot Type</label>
                    {isFieldVisible("po_header_lot_type") ? (
                      <Select
                        {...rsMenuProps}
                        styles={rsCompact}
                        options={poHeaderLotTypeOptions.map(x => ({
                          value: x.name,
                          label: x.name
                        }))}
                        value={
                          poHeaderLotType
                            ? { value: poHeaderLotType, label: poHeaderLotType }
                            : null
                        }
                        onChange={(opt) => setPoHeaderLotType(opt?.value || "")}
                        isDisabled={!isEditMode}
                      />
                    ) : (
                      <MaskedValue />
                    )}
                  </div>

                </div>
              )}

              {createActiveTab === "product" && (
                <div>

                  {isEditMode && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button
                        className="btn btn-dark btn-sm"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => {
                          setModalData([
                            ...modalData,
                            {
                              product_code: "",
                              description: "",
                              uom: "",
                              requirement_qty: "",
                              product_cost: "",
                              discount: 0,
                              status: "PENDING"
                            }
                          ]);
                          setEditableProducts([
                            ...editableProducts,
                            {
                              product_cost: "",
                              requirement_qty: "",
                              delivery_date: ""
                            }
                          ]);
                        }}
                      >
                        + Add Product
                      </button>
                    </div>
                  )}

                  {isEditMode && String(poProductType).trim().toLowerCase() !== 'asset' && (
                    <div
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '10px 12px',
                        marginBottom: 10
                      }}
                    >

                      {/* Filter Row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                       

                        {/* Category */}
                        <div style={{ minWidth: 160, flex: '1 1 150px', maxWidth: 220 }}>
                          <Select
                            {...rsMenuProps}
                            isClearable
                            isDisabled={!isEditMode}
                            placeholder="All Categories"
                            menuShouldScrollIntoView={false}
                            options={categoryOptions.map(c => ({
                              value: c.name || c,
                              label: c.name || c
                            }))}
                            value={
                              filterCategory
                                ? { value: filterCategory, label: filterCategory }
                                : null
                            }
                            onChange={(opt) => {
                              setFilterCategory(opt?.value || "");
                              setFilterSubCategory("");
                            }}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: 30,
                                fontSize: '12px'
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: '2px 6px'
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                                fontSize: '12px'
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                alignSelf: 'flex-start',
                                paddingTop: 2
                              }),
                              dropdownIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              clearIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: '12px',
                                color: '#94a3b8',
                                fontWeight: 400
                              }),
                              option: (base, state) => ({
                                ...base,
                                fontSize: '12px',
                                fontWeight: 400,
                                color: state.isSelected ? '#fff' : '#334155',
                                backgroundColor: state.isSelected
                                  ? '#475569'
                                  : state.isFocused
                                    ? '#f1f5f9'
                                    : '#fff',
                                padding: '6px 10px'
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999
                              }),
                              menu: (base) => ({
                                ...base,
                                fontSize: '12px'
                              })
                            }}
                          />
                        </div>

                        {/* Sub Category */}
                        <div style={{ minWidth: 160, flex: '1 1 150px', maxWidth: 220 }}>
                          <Select
                            {...rsMenuProps}
                            isClearable
                            isDisabled={!isEditMode}
                            placeholder="All Sub-Categories"
                            menuShouldScrollIntoView={false}
                            options={subCategoryOptions
                              .filter(sc => {
                                const scName = sc.name || sc;
                                if (!filterCategory) return true;
                                return allProducts.some(
                                  p =>
                                    p.productCategory === filterCategory &&
                                    p.subCategory === scName
                                );
                              })
                              .map(sc => ({
                                value: sc.name || sc,
                                label: sc.name || sc
                              }))}
                            value={
                              filterSubCategory
                                ? { value: filterSubCategory, label: filterSubCategory }
                                : null
                            }
                            onChange={(opt) => setFilterSubCategory(opt?.value || "")}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: 30,
                                fontSize: '12px'
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: '2px 6px'
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                                fontSize: '12px'
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                alignSelf: 'flex-start',
                                paddingTop: 2
                              }),
                              dropdownIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              clearIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: '12px',
                                color: '#94a3b8',
                                fontWeight: 400
                              }),
                              option: (base, state) => ({
                                ...base,
                                fontSize: '12px',
                                fontWeight: 400,
                                color: state.isSelected ? '#fff' : '#334155',
                                backgroundColor: state.isSelected
                                  ? '#475569'
                                  : state.isFocused
                                    ? '#f1f5f9'
                                    : '#fff',
                                padding: '6px 10px'
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999
                              }),
                              menu: (base) => ({
                                ...base,
                                fontSize: '12px'
                              })
                            }}
                          />
                        </div>

                        {isEditMode && (filterCategory || filterSubCategory) && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{
                              fontSize: 11,
                              whiteSpace: 'nowrap',
                              height: 30,
                              padding: '0 10px'
                            }}
                            onClick={() => {
                              setFilterCategory("");
                              setFilterSubCategory("");
                            }}
                          >
                            ✕ Clear
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                    <table className="table" style={{ ...styles.erpTable, tableLayout: "auto" }}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.erpTh, width: '40px', textAlign: 'center' }}>Item</th>
                          <th style={{ ...styles.erpTh, width: '200px' }}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Code" : "Product Code"}
                          </th>
                          <th style={{ ...styles.erpTh, width: '100px', textAlign: 'center' }}>Product Class</th>
                          <th style={{ ...styles.erpTh, minWidth: '160px' }}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Description" : "Description"}
                          </th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Qty</th>
                          <th style={{ ...styles.erpTh, width: '110px', textAlign: 'center' }}>Over Tolerance (%)</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '130px' }}>Range</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '60px' }}>UOM</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '80px' }}>Currency</th>
<th style={{ ...styles.erpTh, textAlign: 'center', width: '120px' }}>HSN Code</th>
<th style={{ ...styles.erpTh, textAlign: 'right', width: '90px' }}>Unit Cost</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '70px' }}>Disc (%)</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Total Cost</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Net Price</th>
                          {/* CGST/SGST/IGST are always 0.00 until the PO is submitted
                              (taxes aren't computed yet at creation time), so these
                              columns only render once the PO is out of edit mode. */}
                          {!isEditMode && (
                            <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col1 || "—"}</th>
                          )}
                          {!isEditMode && gstColumnLabels.col2 && <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col2}</th>}
                          {!isEditMode && gstColumnLabels.col3 && <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col3}</th>}
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '110px' }}>Grand Total</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '110px', whiteSpace: 'nowrap' }}>Delivery Date</th>
                          {poOrderType === "import" && (
                            <>
                              <th style={{ ...styles.erpTh, textAlign: 'center', width: '120px' }}>Country Of Origin</th>
                            </>
                          )}
                          <th style={{ ...styles.erpTh, width: '60px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalData.map((row, idx) => {
                          const calc = calculateLineTotals(row, idx);

                          return (
                            <tr key={idx}>
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>{idx + 1}</td>

                              {/* Product Code — dropdown showing "CODE - Description" */}
                              <td style={styles.erpTd}>
                                {!isFieldVisible("product_code") ? (
                                  <MaskedValue height={24} />
                                ) : (
                                <Select
                                  {...rsMenuProps}
                                  styles={{
                                    ...rsCompact,
                                    container: (base) => ({ ...base, minWidth: "180px" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  }}
                                  menuShouldScrollIntoView={false}
                                  placeholder={String(poProductType).trim().toLowerCase() === "asset" ? "Select Asset" : "Select Product"}
                                  isDisabled={!isEditMode}
                                  value={row.product_code
                                    ? {
                                      value: row.product_code,
                                      label: (() => {
                                        const p = allProducts.find(p => p.productCode === row.product_code);
                                        return p
                                          ? `${p.productCode} - ${p.description || p.productDescription || ""}`
                                          : row.product_code;
                                      })()
                                    }
                                    : null}
                                  options={
                                    // Finished Goods / Semi Finished remain valid choices in
                                    // the Product Type dropdown, but they are manufacturing
                                    // outputs, not purchasable items — so the Product Code
                                    // dropdown must stay empty ("No options") for these two
                                    // types no matter what's in the product master.
                                    ['finished goods', 'semi finished'].includes(
                                      String(poProductType || '').trim().toLowerCase()
                                    )
                                      ? []
                                      : allProducts
                                          .filter(p => {
                                            if (
                                              poProductType &&
                                              String(p.productType || "").trim().toLowerCase() !==
                                                String(poProductType).trim().toLowerCase()
                                            ) return false;
                                            if (filterCategory && p.productCategory !== filterCategory) return false;
                                            if (filterSubCategory && p.subCategory !== filterSubCategory) return false;
                                            return true;
                                          })
                                          .map((p) => ({
                                            value: p.productCode,
                                            label: `${p.productCode} - ${p.description || p.productDescription || ""}`,
                                          }))
                                  }
                                  onChange={async (opt) => {
                                    const code = opt?.value || "";
                                    const product = allProducts.find(p => p.productCode === code);

                                    // ✅ Check if this is an existing saved line BEFORE resetting
                                    const isExistingLine = !!modalData[idx]?.id;

                                    const updated = [...modalData];
                                    updated[idx] = {
                                      ...updated[idx],
                                      product_code: product?.productCode || "",
                                      description:
                                        product?.description ||
                                        product?.productDescription ||
                                        product?.product_description || "",
                                      uom: product?.uom || "",
                                      // 🆕 currency/product_cost below are overwritten by
                                      // applyExchangeRateToLineItems() right after setModalData
                                      // (a few lines down) whenever we're on an Import PO with
                                      // a Currency + Exchange Rate already set — base_cost_inr
                                      // is what that conversion is always computed FROM, so it
                                      // must be captured here from the (INR) product master,
                                      // never from an already-converted value.
                                      currency: product?.currency || "",
                                      // 🆕 native_currency — the currency THIS
                                      // product's cost is actually quoted in;
                                      // captured once here and never overwritten,
                                      // so later exchange-rate triggers know
                                      // whether to convert this row at all.
                                      native_currency: product?.currency || "INR",
                                      base_cost_inr: product?.productCost || 0,
                                      product_cost: product?.productCost || 0,
                                      requirement_qty: "",
                                      delivery_date: "",
                                      // ✅ Tax % now comes from the HSN code
                                      // (hsn_code_data table) instead of being
                                      // hardcoded, so CGST/SGST/IGST reflect the
                                      // product's actual HSN code, not qty.
                                      cgst_percent: Number(product?.cgstPercent ?? 0),
                                      sgst_percent: Number(product?.sgstPercent ?? 0),
                                      igst_percent: Number(product?.igstPercent ?? 0),
                                      hsn_code: product?.hsnCode || "",
                                      product_class: product?.productClass || "",
                                      // ✅ Keep existing characteristics for saved lines, clear only for new lines
                                      characteristics: isExistingLine
                                        ? (modalData[idx].characteristics || [])
                                        : [],
                                    };
                                    setModalData(updated);

                                    // 🆕 If this is an Import PO and we already have a
                                    // Currency + Exchange Rate on the Customs tab, convert
                                    // this newly-added row's cost immediately instead of
                                    // leaving it in INR until the next country change.
                                    if (poOrderType === "import" && importCurrency && importExchangeRate) {
                                      applyExchangeRateToLineItems(importCurrency, Number(importExchangeRate));
                                    }

                                    // 🆕 Clear any stale calculatedAmounts for this row —
                                    // otherwise calculateLineTotals() keeps using the OLD
                                    // GST% (from a previous "View" click) instead of the
                                    // fresh cgst_percent/sgst_percent/igst_percent we just
                                    // set above, so CGST/SGST/IGST in the table wouldn't
                                    // update until the user clicked View again.
                                    setCalculatedAmounts(prev => {
                                      const next = { ...prev };
                                      delete next[idx];
                                      return next;
                                    });

                                    // 🆕 Resolve real GST for THIS row immediately, instead of
                                    // waiting for the user to click "View" or re-pick the
                                    // vendor. Previously the row above only got
                                    // cgst_percent/sgst_percent/igst_percent from the product
                                    // master (stale/0 now that GST comes from
                                    // gst_configuration), so any row the user didn't manually
                                    // "View" saved with 0% GST. If a vendor is already known,
                                    // fetch this one product's real GST now and stash it in
                                    // calculatedAmounts — the same place Save() and the table
                                    // read GST from — so it's correct the moment a product is
                                    // picked, no extra click required. Purchase Organization is
                                    // NOT required here — gst_configuration is looked up by
                                    // movement_type + hsn_code only.
                                    const orgForGst = modalData?.[0]?.purchase_organization || "";
                                    // 🆕 Fallback source: the product master's own
                                    // cgstPercent/sgstPercent/igstPercent (same fields
                                    // View's "!lineItemId" branch already uses locally).
                                    // If gst_configuration has no matching HSN+movement
                                    // row yet (or billing_state is missing on the
                                    // supplier), get-product-gst legitimately/silently
                                    // comes back all-zero — previously that zero got
                                    // written into calculatedAmounts as-is, so the table
                                    // showed 0.00 until "View" was opened and recomputed
                                    // from this same fallback field. Applying the same
                                    // fallback here keeps both paths in sync from the
                                    // start, no extra click required.
                                    const fallbackGst = {
                                      cgst: Number(product?.cgstPercent ?? 0),
                                      sgst: Number(product?.sgstPercent ?? 0),
                                      igst: Number(product?.igstPercent ?? 0),
                                    };
                                    const isImportRow = poOrderType === "import";
                                    // Import POs resolve GST off Supplier Country, not the
                                    // vendor — selectedVendor is irrelevant to the Import
                                    // gst_configuration lookup (see getGstForPO on the backend).
                                    const canResolveGst = isImportRow ? !!importSupplierCountry : !!selectedVendor;
                                    if (code && canResolveGst) {
                                      try {
                                        const gstRes = await axios.post(
                                          `${PURCHASE_API}/po/get-product-gst`,
                                          {
                                            productCodes: [code],
                                            vendorCode: selectedVendor,
                                            purchaseOrganization: orgForGst,
                                            orderType: poOrderType,
                                            supplierCountry: isImportRow ? importSupplierCountry : undefined
                                          },
                                          { headers: authHeaders() }
                                        );
                                        const g = gstRes.data?.[code];
                                        const isAllZero =
                                          !g ||
                                          ((Number(g.cgst) || 0) === 0 &&
                                            (Number(g.sgst) || 0) === 0 &&
                                            (Number(g.igst) || 0) === 0);

                                        // 🛠 FIX: same header/value alignment fix as
                                        // refetchLineGst and the line-item-calc
                                        // refresh — map BOTH the labels and the
                                        // amounts positionally from g.taxes[], for
                                        // Domestic and Import alike. Previously the
                                        // labels only updated when isImportRow was
                                        // true, so picking a product on a Domestic
                                        // PO (e.g. an interstate supplier that's
                                        // IGST-only) resolved the correct IGST
                                        // amount but left the header stuck on the
                                        // fixed CGST/SGST/IGST 3-column default —
                                        // exactly backwards from what the backend
                                        // (getGstForPO) actually determined.
                                        const resolved = (g?.taxes && g.taxes.length > 0)
                                          ? {
                                              cgst: Number(g.taxes?.[0]?.percent ?? 0),
                                              sgst: Number(g.taxes?.[1]?.percent ?? 0),
                                              igst: Number(g.taxes?.[2]?.percent ?? 0),
                                            }
                                          : (isAllZero ? fallbackGst : { cgst: g.cgst, sgst: g.sgst, igst: g.igst });

                                        setGstColumnLabels(
                                          g?.taxes?.length
                                            ? {
                                                col1: g.taxes[0]?.type || DEFAULT_GST_COLUMN_LABELS.col1,
                                                col2: g.taxes[1]?.type || "",
                                                col3: g.taxes[2]?.type || "",
                                              }
                                            : DEFAULT_GST_COLUMN_LABELS
                                        );

                                        if (isAllZero && !isImportRow) {
                                          console.warn(
                                            `GST config lookup returned 0% for product ${code} — falling back to product master cgst/sgst/igst. Check gst_configuration / supplier billing_state.`
                                          );
                                        }

                                        setCalculatedAmounts(prev => ({
                                          ...prev,
                                          [idx]: {
                                            ...prev[idx],
                                            cgst: resolved.cgst,
                                            sgst: resolved.sgst,
                                            igst: resolved.igst,
                                            hsnCode: g?.hsnCode || product?.hsnCode || ""
                                          }
                                        }));
                                      } catch (gstErr) {
                                        console.error("Error resolving GST for new row, using product master fallback:", gstErr);
                                        setCalculatedAmounts(prev => ({
                                          ...prev,
                                          [idx]: {
                                            ...prev[idx],
                                            cgst: fallbackGst.cgst,
                                            sgst: fallbackGst.sgst,
                                            igst: fallbackGst.igst,
                                            hsnCode: product?.hsnCode || ""
                                          }
                                        }));
                                      }
                                    } else {
                                      // No vendor (Domestic) / no Supplier Country (Import)
                                      // yet — still seed the table with the product
                                      // master's rate instead of 0, so it's not
                                      // misleadingly blank before that's picked.
                                      // 🛠 Also reset the headers to the fixed
                                      // CGST/SGST/IGST default here — otherwise a
                                      // column layout left over from a previously
                                      // viewed/edited PO (e.g. a single "IGST"
                                      // column) could still be showing here even
                                      // though nothing has been resolved for THIS
                                      // product/vendor pair yet.
                                      setGstColumnLabels(DEFAULT_GST_COLUMN_LABELS);
                                      setCalculatedAmounts(prev => ({
                                        ...prev,
                                        [idx]: {
                                          ...prev[idx],
                                          cgst: fallbackGst.cgst,
                                          sgst: fallbackGst.sgst,
                                          igst: fallbackGst.igst,
                                          hsnCode: product?.hsnCode || ""
                                        }
                                      }));
                                    }

                                    // ✅ Auto-populate header Product Type from the
                                    // selected product, if it hasn't been set yet
                                    if (product?.productType && !poProductType) {
                                      setPoProductType(product.productType);
                                    }

                                    const updatedEditable = [...editableProducts];
                                    const masterCostInr = Number(product?.productCost || 0);
                                    const activeRate = Number(importExchangeRate) || 0;
                                    // 🆕 Only convert when this product's OWN currency (master
                                    // record) is INR — a product already priced in a foreign
                                    // currency shouldn't have the rate applied on top of it.
                                    const productNativeCurrency = (product?.currency || "INR").toString().trim().toUpperCase();
                                    const isProductNativeInr = productNativeCurrency === "" || productNativeCurrency === "INR";
                                    // 💤 COMMENTED OUT (for now): exchange rate not applied to Unit Cost
                                    // const shouldConvertNow = isProductNativeInr && poOrderType === "import" && importCurrency && activeRate;
                                    const shouldConvertNow = false;
                                    updatedEditable[idx] = {
                                      ...updatedEditable[idx],
                                      base_cost_inr: masterCostInr, // 🆕 always the raw INR figure
                                      native_currency: productNativeCurrency || "INR",
                                      product_cost: round2(shouldConvertNow ? masterCostInr * activeRate : masterCostInr),
                                      requirement_qty: "",
                                      delivery_date: "",
                                    };
                                    setEditableProducts(updatedEditable);

                                    /* ── PIR auto-populate — SKIP for existing saved lines ── */
                                    if (code && !isExistingLine) {   // ✅ Only fetch PIR for NEW lines
                                      try {
                                        const pirRes = await axios.get(`${PURCHASE_API}/po/pir-info/${code}`, { headers: authHeaders() });
                                        const pir = pirRes.data;

                                        if (pir && pir.found) {
                                          // Only override with the PIR's cost when it's an actual
                                          // positive price. If the vendor's PIR has no price set
                                          // (0, null, blank), keep the cost already pulled from
                                          // Product Master instead of blanking it out.
                                          const pirCostNum = Number(pir.productCost);
                                          // 🆕 PIR cost is raw INR from the backend ONLY when
                                          // pir.currency (COALESCE(pd.currency, ad.currency) —
                                          // see getPIRInfo) is INR. Convert it the same way as
                                          // the product-master seed above, but gated on that
                                          // currency instead of assuming every PIR cost is INR.
                                          const pirRate = Number(importExchangeRate) || 0;
                                          const pirNativeCurrency = (pir.currency || product?.currency || "INR").toString().trim().toUpperCase();
                                          const pirIsNativeInr = pirNativeCurrency === "" || pirNativeCurrency === "INR";
                                          // 💤 COMMENTED OUT (for now): exchange rate not applied to Unit Cost
                                          // const pirShouldConvert = pirIsNativeInr && poOrderType === "import" && importCurrency && pirRate;
                                          const pirShouldConvert = false;
                                          const pirCostForDisplay =
                                            pirCostNum > 0
                                              ? (pirShouldConvert ? pirCostNum * pirRate : pirCostNum)
                                              : null; // null = "no override, keep existing"

                                          setModalData(prev => {
                                            const next = [...prev];
                                            next[idx] = {
                                              ...next[idx],
                                              base_cost_inr: pirCostNum > 0 ? pirCostNum : next[idx].base_cost_inr,
                                              native_currency: pirCostNum > 0 ? pirNativeCurrency : next[idx].native_currency,
                                              product_cost: pirCostForDisplay !== null ? pirCostForDisplay : next[idx].product_cost,
                                              product_class: pir.productClass || next[idx].product_class,
                                              characteristics: (() => {
                                                const pirChars = pir.characteristics || [];
                                                const typedOver = getOverTolerance(next[idx]);
                                                return typedOver !== ""
                                                  ? withOverTolerance(pirChars, typedOver)
                                                  : pirChars;
                                              })(),
                                              _pirVendorName: pir.vendorName || "",
                                              _pirVendorCode: pir.vendorCode || pir.vendorName || "", // 🆕 needed so the conflict modal can call handleVendorSelect with the right id, not just display the name
                                              pir_number: pir.pir_number || null,
                                            };
                                            return next;
                                          });

                                          setEditableProducts(prev => {
                                            const next = [...prev];
                                            next[idx] = {
                                              ...next[idx],
                                              base_cost_inr: pirCostNum > 0 ? pirCostNum : next[idx].base_cost_inr,
                                              native_currency: pirCostNum > 0 ? pirNativeCurrency : next[idx].native_currency,
                                              product_cost: pirCostForDisplay !== null ? pirCostForDisplay : next[idx].product_cost,
                                            };
                                            return next;
                                          });

                                          /* Vendor conflict check */
                                          const otherVendorRows = modalData
                                            .filter((_, i) => i !== idx && _?.product_code && _?._pirVendorName)
                                            .filter(r => r._pirVendorName !== pir.vendorName);

                                          const hasConflict = otherVendorRows.length > 0;

                                          if (hasConflict) {
                                            // 🆕 Instead of just warning and letting the mismatch
                                            // sit there until Save rejects it, pop the
                                            // choose-a-supplier modal right away: de-duped list of
                                            // every conflicting vendor seen so far (the new
                                            // product's default + whatever the other rows already
                                            // had), plus — inside the modal — the option to pick
                                            // any other supplier entirely.
                                            const seen = new Set();
                                            const conflictOptions = [];
                                            [
                                              { name: pir.vendorName, code: pir.vendorCode || pir.vendorName },
                                              ...otherVendorRows.map(r => ({ name: r._pirVendorName, code: r._pirVendorCode || r._pirVendorName }))
                                            ].forEach(v => {
                                              if (v.name && !seen.has(v.name)) {
                                                seen.add(v.name);
                                                conflictOptions.push(v);
                                              }
                                            });
                                            setVendorConflictModal({ productCode: code, options: conflictOptions });
                                          } else {
                                            if (pir.vendorName && !selectedVendor) {
                                              await handleVendorSelect(pir.vendorCode || pir.vendorName);
                                            }
                                            // 🔧 FIX: was wrongly setting Payment Terms from
                                            // the PIR-resolved vendor's business_partner
                                            // terms_and_conditions. That text belongs in
                                            // Terms & Conditions, not Payment Terms.
                                            if (pir.termsConditions && !termsAndConditions) {
                                              setTermsAndConditions(pir.termsConditions);
                                            }
                                          }
                                        } else if (pir && pir.characteristics) {
                                          // ✅ No PIR exists for this product, but the backend
                                          // still returns blank characteristic fields based on
                                          // the product's class — use those instead of leaving
                                          // the row with an empty characteristics array.
                                          setModalData(prev => {
                                            const next = [...prev];
                                            next[idx] = {
                                              ...next[idx],
                                              characteristics: pir.characteristics || [],
                                            };
                                            return next;
                                          });
                                        }
                                      } catch (pirErr) {
                                        console.error("PIR info fetch failed:", pirErr);
                                      }
                                    }
                                  }}
                                />
                                )}
                              </td>

                              {/* Class — from productdetails.productClass */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px' }}>
                                {!isFieldVisible("product_class") ? <MaskedValue height={20} /> : <>
                                {row.product_class || (allProducts.find(p => p.productCode === row.product_code)?.productClass) || ""}
                                {(row.product_class || allProducts.find(p => p.productCode === row.product_code)?.productClass) && (
                                  <span
                                    style={{ cursor: 'pointer', marginLeft: 6, color: '#1d4ed8', fontWeight: 'bold', fontSize: '11px' }}
                                    title="View / Edit Characteristics"
                                    onClick={() => handleOpenCharModal(idx, "product")}
                                  >
                                    ▶
                                  </span>
                                )}
                                </>}
                              </td>

                              {/* Description — full text, wrapping */}
                              <td
                                style={{
                                  ...styles.erpTd,
                                  minWidth: '160px',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.4'
                                }}
                              >
                                {isFieldVisible("description")
                                  ? (row.description || row.product_description)
                                  : <MaskedValue height={20} />}
                              </td>

                              {/* Qty */}
                              <td style={styles.erpTd}>
                                {isFieldVisible("requirement_qty") ? (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px', minWidth: '80px' }}
                                    value={editableProducts[idx]?.requirement_qty || ""}
                                    onChange={(e) => handleProductFieldChange(idx, "requirement_qty", e.target.value)}
                                    disabled={!isEditMode}
                                  />
                                ) : (
                                  <MaskedValue height={24} />
                                )}
                              </td>

                              {/* Over Tolerance (%) — typed directly on the line (replaces the old
                                  Purchase Class column). Stored as the "Over Tolerance"
                                  characteristic so it saves with the line as before; the
                                  Range column next to it updates as soon as a number is entered.
                                  NOTE: still gated by the existing "purchase_class" field-permission
                                  key so current role/field-masking setups keep working. */}
                              <td style={styles.erpTd}>
                                {!isFieldVisible("purchase_class") ? <MaskedValue height={20} /> : (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px', minWidth: '70px' }}
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={getOverTolerance(row)}
                                    onChange={(e) => handleOverToleranceChange(idx, e.target.value)}
                                    disabled={!isEditMode}
                                  />
                                )}
                              </td>

                              {/* Range — derived from Qty ± Under/Over Tolerance % (set via Characteristics modal) */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {!isFieldVisible("range") ? <MaskedValue height={20} /> : (
                                  getToleranceRange(row, idx).rangeText ? (
                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>
                                      {getToleranceRange(row, idx).rangeText}
                                    </span>
                                  ) : (
                                    "—"
                                  )
                                )}
                              </td>

                              {/* UOM */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {isFieldVisible("uom") ? row.uom : <MaskedValue height={20} />}
                              </td>

                              {/* Currency */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {!isFieldVisible("currency") ? (
                                  <MaskedValue height={20} />
                                ) : isEditMode ? (
                                  <select
                                    className="form-select form-select-sm p-1"
                                    style={{ fontSize: '12px', minWidth: '80px' }}
                                    value={row.currency || ""}
                                    onChange={(e) => handleRowCurrencyChange(idx, e.target.value)}
                                  >
                                    <option value="">Select</option>
                                    {currencyOptions(row.currency).map((c) => (
                                      <option key={c} value={c}>{c}</option>
                                    ))}
                                  </select>
                                ) : (
                                  row.currency || ""
                                )}
                              </td>

                              {/* HSN Code */}
<td style={{ ...styles.erpTd, textAlign: 'center' }}>
  {isFieldVisible("hsn_code") ? (
    row.hsn_code ||
    row.hsnCode ||
    allProducts.find(
      p => p.productCode === row.product_code
    )?.hsnCode ||
    ""
  ) : (
    <MaskedValue height={20} />
  )}
</td>

                              {/* Unit Cost */}
                              <td style={styles.erpTd}>
                                {isFieldVisible("product_cost") ? (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px' }}
                                    value={editableProducts[idx]?.product_cost || ""}
                                    onChange={(e) => handleProductFieldChange(idx, "product_cost", e.target.value)}
                                    onBlur={() => handleProductCostBlur(idx)}
                                    disabled={!isEditMode}
                                  />
                                ) : (
                                  <MaskedValue height={24} />
                                )}
                              </td>

                              {/* Disc (%) */}
                              <td style={styles.erpTd}>
                                {isFieldVisible("discount") ? (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px' }}
                                    value={discounts[idx] || ""}
                                    onChange={(e) => handleDiscountChange(e, idx)}
                                    disabled={!isEditMode}
                                  />
                                ) : (
                                  <MaskedValue height={24} />
                                )}
                              </td>

                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("total_cost") ? formatCurrency(calc.grossTotal) : <MaskedValue height={20} />}</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("net_price") ? formatCurrency(calc.netPrice) : <MaskedValue height={20} />}</td>
                              {/* 🛠 Columns are fully dynamic (gstColumnLabels.col1/2/3
                                  — see header row above): a column only exists
                                  when that tax type actually applies to this PO
                                  (e.g. Inter State Domestic = IGST only, so
                                  col2/col3 don't apply at all). Cell is only
                                  rendered when its matching header exists, so
                                  an inapplicable tax type doesn't show up as a
                                  cell (0.00 or —) at all — same column count as
                                  the header row. */}
                              {!isEditMode && (
                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("cgst_percent") ? formatCurrency(calc.cgstAmt) : <MaskedValue height={20} />}</td>
                              )}
                              {!isEditMode && gstColumnLabels.col2 && (
                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("sgst_percent") ? formatCurrency(calc.sgstAmt) : <MaskedValue height={20} />}</td>
                              )}
                              {!isEditMode && gstColumnLabels.col3 && (
                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>
  {isFieldVisible("igst_percent") ? formatCurrency(calc.igstAmt) : <MaskedValue height={20} />}
</td>
                              )}
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("grand_total") ? formatCurrency(calc.finalPrice) : <MaskedValue height={20} />}</td>

                              {/* Requirement Date */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {!isFieldVisible("delivery_date") ? (
                                  <MaskedValue height={20} />
                                ) : isEditMode ? (
                                  <DateInputDDMMYYYY
                                    className="form-control form-control-sm p-1"
                                    style={{ fontSize: '12px', minWidth: '120px' }}
                                    value={editableProducts[idx]?.delivery_date || ""}
                                    onChange={(val) => {
                                      // Update only this row's date in editableProducts — don't touch modalData
                                      setEditableProducts(prev => {
                                        const next = [...prev];
                                        next[idx] = { ...next[idx], delivery_date: val };
                                        return next;
                                      });
                                    }}
                                  />
                                ) : (
                                  <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    {formatDateDMY(editableProducts[idx]?.delivery_date || row.delivery_date) || "—"}
                                  </span>
                                )}
                              </td>

                              {/* 🆕 Import PO — item-level fields */}
                              {poOrderType === "import" && (
                                <>
                                  <td style={styles.erpTd}>
                                    {/* Country Of Origin always mirrors the header's
                                        Supplier Country — not independently editable
                                        per line, so it can never drift out of sync. */}
                                    <span style={{ fontSize: '12px' }}>
                                      {importSupplierCountry || "—"}
                                    </span>
                                  </td>
                                </>
                              )}

                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                                  <button
                                    className="btn btn-outline-dark btn-sm py-0"
                                    style={{ fontSize: '11px' }}
                                    onClick={() => handleViewProductDetails(row.id, idx)}
                                  >
                                    View
                                  </button>
                                  {isEditMode && (
                                    <button
                                      className="btn btn-sm py-0"
                                      style={{ color: '#16a34a', border: 'none', background: 'transparent', lineHeight: 1, padding: '2px 6px' }}
                                      title="Insert product below"
                                      onClick={() => insertPORowAfter(idx)}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                    </button>
                                  )}
                                  {isEditMode && (
                                    <button
                                      className="btn btn-sm py-0"
                                      style={{ color: '#ef4444', border: 'none', background: 'transparent', lineHeight: 1, padding: '2px 6px' }}
                                      title="Remove row"
                                      onClick={() => {
                                        // Track DB id so backend can delete it
                                        if (row.id) {
                                          setDeletedLineItemIds(prev => [...prev, row.id]);
                                        }
                                        const updatedModal = modalData.filter((_, i) => i !== idx);
                                        const updatedEditable = editableProducts.filter((_, i) => i !== idx);
                                        const updatedDiscounts = {};
                                        Object.keys(discounts).forEach(k => {
                                          const ki = parseInt(k);
                                          if (ki < idx) updatedDiscounts[ki] = discounts[ki];
                                          else if (ki > idx) updatedDiscounts[ki - 1] = discounts[ki];
                                        });
                                        setModalData(updatedModal);
                                        setEditableProducts(updatedEditable);
                                        setDiscounts(updatedDiscounts);
                                      }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}

                        <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>
                          <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>TOTALS:</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.gross)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.net)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.cgst)}</td>
                          {gstColumnLabels.col2 && <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.sgst)}</td>}
                          {gstColumnLabels.col3 && <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.igst)}</td>}
                          <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px', whiteSpace: 'nowrap' }}>{currencySymbol} {formatCurrency(totals.grand)}</td>
                          <td style={styles.erpTd}></td>
                          <td style={styles.erpTd}></td>
                        </tr>

                        {/* 🆕 Port/Clearing total and Bank total — shown as two
                            separate rows under the Grand Total column, each PO-level
                            (single value, not per line item). Only meaningful for
                            Import POs, since both those tabs only render when
                            poOrderType === "import" (see LANDED_COST_TAB_IDS filter
                            above). */}
                        {poOrderType === "import" && (
                          <>
                            {/* Spacer cells before Grand Total must match Total
                                Cost + Net Price + however many GST columns are
                                actually present (1–3), same as the header row,
                                or these rows drift out from under Grand Total. */}
                            <tr style={{ backgroundColor: '#eef2ff', fontWeight: 'bold' }}>
                              <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                Port/Clearing Total:
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              {gstColumnLabels.col2 && <td style={styles.erpTd}></td>}
                              {gstColumnLabels.col3 && <td style={styles.erpTd}></td>}
                              <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                {currencySymbol} {formatCurrency(portClearingTotal)}
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                            </tr>
                            <tr style={{ backgroundColor: '#eef2ff', fontWeight: 'bold' }}>
                              <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                Bank Total:
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              {gstColumnLabels.col2 && <td style={styles.erpTd}></td>}
                              {gstColumnLabels.col3 && <td style={styles.erpTd}></td>}
                              <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                {currencySymbol} {formatCurrency(bankTotal)}
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                            </tr>
                            <tr style={{ backgroundColor: '#dcfce7', fontWeight: 'bold' }}>
                              <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                Final Total:
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              {gstColumnLabels.col2 && <td style={styles.erpTd}></td>}
                              {gstColumnLabels.col3 && <td style={styles.erpTd}></td>}
                              <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                {currencySymbol} {formatCurrency((totals.grand || 0) + (portClearingTotal || 0) + (bankTotal || 0))}
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                            </tr>
                          </>
                        )}


                      </tbody>
                    </table>


                  </div>
                </div>
              )}

              {/* ================= LANDED COST CATEGORY TABS (Import PO only) ================= */}
              {/* Customs renders one row per product line item (with Product Code).
                  Port/Clearing and Bank are PO-level — a single row, no Product Code
                  column, not repeated per line item. */}
              {LANDED_COST_TAB_IDS.includes(createActiveTab) && (() => {
                const activeGroup = LANDED_COST_GROUPS.find(g => g.tabId === createActiveTab);

                // PO-level groups (Customs, Port/Clearing, Bank) — single row,
                // keyed under a fixed pseudo-key. No Product Code column,
                // not repeated per product line item.
                {
                  const rowKey = "po_level";
                  const entry = landedCosts[rowKey] || {};
                  const groupSubtotal = activeGroup.fields
                    .reduce((sum, f) => sum + (Number(entry[f.key]) || 0), 0);

                  return (
                    <div>
                      {/* 🆕 Import details — only on the Customs tab, same fields/order
                          as before, rendered above the manual landed-cost table. */}
                      {createActiveTab === "landed_customs" && (
                        <>
                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Supplier Country</label>
                              <Select
                                {...rsMenuProps}
                                styles={rsCompact}
                                placeholder="Select Supplier Country"
                                options={countryOptions.map(c => ({ value: c.name || c, label: c.name || c }))}
                                value={importSupplierCountry ? { value: importSupplierCountry, label: importSupplierCountry } : null}
                                onChange={(opt) => {
                                  const val = opt?.value || "";
                                  setImportSupplierCountry(val);
                                  // 🆕 Import POs resolve GST off Supplier Country (not
                                  // vendor) — refetch line-item GST/labels the moment a
                                  // country is picked, same as vendor-change does for
                                  // Domestic POs, instead of waiting for a manual "View".
                                  if (poOrderType === "import") {
                                    refetchLineGstForCountry(val);
                                    // 🆕 Auto-fill Currency + Exchange Rate from Exchange
                                    // Rate Configuration, then push the converted Unit Cost
                                    // + Currency into every Product-tab line item.
                                    fetchExchangeRateForCountry(val);
                                  }
                                }}
                                isDisabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Currency</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importCurrency}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setImportCurrency(val);
                                  // 🆕 Manual override — re-apply so Product tab currency
                                  // stays in sync with whatever's typed here.
                                  applyExchangeRateToLineItems(val, Number(importExchangeRate) || 0);
                                }}
                                disabled={!isEditMode}
                                placeholder="e.g. USD"
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Exchange Rate</label>
                              <input
                                type="number"
                                className="form-control"
                                style={styles.compactInput}
                                value={importExchangeRate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setImportExchangeRate(val);
                                  // 🆕 Manual override — recompute every line item's Unit
                                  // Cost against the new rate immediately.
                                  applyExchangeRateToLineItems(importCurrency, Number(val) || 0);
                                }}
                                disabled={!isEditMode}
                                placeholder="e.g. 83.25"
                              />
                            </div>
                          </div>

                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Incoterm</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importIncoterm}
                                onChange={(e) => setImportIncoterm(e.target.value)}
                                disabled={!isEditMode}
                                placeholder="e.g. FOB, CIF, EXW"
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Port Of Loading</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importPortOfLoading}
                                onChange={(e) => setImportPortOfLoading(e.target.value)}
                                disabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Port Of Discharge</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importPortOfDischarge}
                                onChange={(e) => setImportPortOfDischarge(e.target.value)}
                                disabled={!isEditMode}
                              />
                            </div>
                          </div>

                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Final Destination</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importFinalDestination}
                                onChange={(e) => setImportFinalDestination(e.target.value)}
                                disabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Expected Shipment Date</label>
                              <DateInputDDMMYYYY
                                style={styles.compactInput}
                                value={importExpectedShipmentDate}
                                onChange={(val) => setImportExpectedShipmentDate(val)}
                                disabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Expected Arrival Date</label>
                              <DateInputDDMMYYYY
                                style={styles.compactInput}
                                value={importExpectedArrivalDate}
                                onChange={(val) => setImportExpectedArrivalDate(val)}
                                disabled={!isEditMode}
                              />
                            </div>
                          </div>

                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>LC Required</label>
                              <Select
                                {...rsMenuProps}
                                styles={rsCompact}
                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                value={{ value: importLcRequired, label: importLcRequired }}
                                onChange={(opt) => setImportLcRequired(opt?.value || "No")}
                                isDisabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Insurance Required</label>
                              <Select
                                {...rsMenuProps}
                                styles={rsCompact}
                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                value={{ value: importInsuranceRequired, label: importInsuranceRequired }}
                                onChange={(opt) => setImportInsuranceRequired(opt?.value || "No")}
                                isDisabled={!isEditMode}
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {activeGroup.fields.length > 0 && (
                        <>
                          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
                            Manual landed-cost breakup for the whole Purchase Order ({activeGroup.group}). These
                            values are saved together with the rest of the Purchase Order.
                          </div>

                          <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                            <table className="table" style={{ ...styles.erpTable, tableLayout: "auto" }}>
                              <thead>
                                <tr>
                                  {activeGroup.fields.map(f => (
                                    <th key={f.key} style={{ ...styles.erpTh, textAlign: "center" }}>
                                      {f.label}
                                    </th>
                                  ))}
                                  <th style={styles.erpTh}>{activeGroup.group} Subtotal</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {activeGroup.fields.map(f => (
                                    <td key={f.key} style={styles.erpTd}>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm"
                                        style={{ fontSize: "12px", width: "120px", margin: "0 auto" }}
                                        value={entry[f.key] ?? ""}
                                        disabled={!isEditMode}
                                        placeholder="0.00"
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setLandedCosts(prev => ({
                                            ...prev,
                                            [rowKey]: { ...(prev[rowKey] || {}), [f.key]: val }
                                          }));
                                        }}
                                      />
                                    </td>
                                  ))}
                                  <td style={{ ...styles.erpTd, fontWeight: 600, whiteSpace: "nowrap" }}>
                                    {currencySymbol} {formatCurrency(groupSubtotal)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  );
                }
              })()}

              {/* ================= REVIEW TAB ================= */}
              {createActiveTab === "review" && (
                <div>
                  <div className="d-flex justify-content-end gap-2 mb-2">
                    <button
                      className="btn btn-dark btn-sm"
                      onClick={handleApprove}
                      disabled={isPOFinalized}
                    >
                      Approve
                    </button>

                    <button
                      className="btn btn-dark btn-sm"
                      onClick={handleReject}
                      disabled={isPOFinalized}
                    >
                      Reject
                    </button>

                    <button
                      className="btn btn-dark btn-sm"
                      onClick={handleQuery}
                      disabled={isPOFinalized}
                    >
                      Query
                    </button>
                  </div>

                  {/* Review grid mirrors the Product tab's columns (read-only), plus the
                      review-only checkbox + Status. Unit Cost is the one editable column
                      (Edit mode) and shares editableProducts with the Product tab. */}
                  <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                    <table className="table" style={{ ...styles.erpTable, tableLayout: "auto" }}>
                      <thead>
                        <tr>
                          <th style={styles.erpTh}>
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                modalData.length > 0 &&
                                modalData.some(row => !["APPROVED", "REJECTED"].includes(String(row.review || "").toUpperCase())) &&
                                modalData.every((row, idx) => {
                                  const s = String(row.review || "").toUpperCase();
                                  return ["APPROVED", "REJECTED"].includes(s) ? true : selectedRows.includes(idx);
                                })
                              }
                            />
                          </th>
                          <th style={{ ...styles.erpTh, width: '40px', textAlign: 'center' }}>Item</th>
                          <th style={{ ...styles.erpTh, minWidth: '180px' }}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Code" : "Product Code"}
                          </th>
                          <th style={{ ...styles.erpTh, width: '100px', textAlign: 'center' }}>Product Class</th>
                          <th style={{ ...styles.erpTh, minWidth: '160px' }}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Description" : "Description"}
                          </th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Qty</th>
                          <th style={{ ...styles.erpTh, width: '110px', textAlign: 'center' }}>Over Tolerance (%)</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '130px' }}>Range</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '60px' }}>UOM</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '80px' }}>Currency</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '120px' }}>HSN Code</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '90px' }}>Unit Cost</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '70px' }}>Disc (%)</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Total Cost</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Net Price</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col1 || "—"}</th>
                          {gstColumnLabels.col2 && <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col2}</th>}
                          {gstColumnLabels.col3 && <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col3}</th>}
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '110px' }}>Grand Total</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '110px', whiteSpace: 'nowrap' }}>Delivery Date</th>
                          {poOrderType === "import" && (
                            <th style={{ ...styles.erpTh, textAlign: 'center', width: '120px' }}>Country Of Origin</th>
                          )}
                          <th style={styles.erpTh}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalData.map((row, idx) => {
                          const rowStatusUpper = String(row.review || "").toUpperCase();
                          const isDecided = ["APPROVED", "REJECTED"].includes(rowStatusUpper);
                          const isDisabled = isPOFinalized || isDecided;
                          const calc = calculateLineTotals(row, idx);
                          const masterProduct = allProducts.find(p => p.productCode === row.product_code);

                          const statusLabel = (() => {
                            if (rowStatusUpper === "PENDING") return "Pending for Approval";
                            if (rowStatusUpper === "APPROVED") return "Approved";
                            if (rowStatusUpper === "REJECTED") return "Rejected";
                            if (rowStatusUpper === "QUERY") return "Query";
                            return "Pending for Approval";
                          })();

                          return (
                            <tr key={idx}>
                              <td style={styles.erpTd}>
                                <input
                                  type="checkbox"
                                  disabled={isDisabled}
                                  checked={selectedRows.includes(idx)}
                                  onChange={(e) => handleCheckboxChange(e, idx)}
                                />
                              </td>
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>{idx + 1}</td>

                              {/* Product Code — "CODE - Description", same label as the Product tab */}
                              <td style={styles.erpTd}>
                                {!isFieldVisible("product_code") ? <MaskedValue height={20} /> : (
                                  row.product_code
                                    ? (masterProduct
                                        ? `${masterProduct.productCode} - ${masterProduct.description || masterProduct.productDescription || ""}`
                                        : row.product_code)
                                    : ""
                                )}
                              </td>

                              {/* Product Class */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px' }}>
                                {!isFieldVisible("product_class") ? <MaskedValue height={20} /> : (
                                  row.product_class || masterProduct?.productClass || ""
                                )}
                              </td>

                              {/* Description */}
                              <td style={{ ...styles.erpTd, minWidth: '160px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>
                                {isFieldVisible("description")
                                  ? (row.description || row.product_description)
                                  : <MaskedValue height={20} />}
                              </td>

                              {/* Qty */}
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                {isFieldVisible("requirement_qty")
                                  ? (editableProducts[idx]?.requirement_qty ?? row.requirement_qty)
                                  : <MaskedValue height={20} />}
                              </td>

                              {/* Over Tolerance (%) */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px' }}>
                                {!isFieldVisible("purchase_class") ? <MaskedValue height={20} /> : (
                                  getOverTolerance(row) !== "" ? getOverTolerance(row) : "—"
                                )}
                              </td>

                              {/* Range */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {!isFieldVisible("range") ? <MaskedValue height={20} /> : (
                                  getToleranceRange(row, idx).rangeText ? (
                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>
                                      {getToleranceRange(row, idx).rangeText}
                                    </span>
                                  ) : "—"
                                )}
                              </td>

                              {/* UOM */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {isFieldVisible("uom") ? row.uom : <MaskedValue height={20} />}
                              </td>

                              {/* Currency */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {isFieldVisible("currency") ? (row.currency || "") : <MaskedValue height={20} />}
                              </td>

                              {/* HSN Code */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {isFieldVisible("hsn_code")
                                  ? (row.hsn_code || row.hsnCode || masterProduct?.hsnCode || "")
                                  : <MaskedValue height={20} />}
                              </td>

                              {/* Unit Cost — the only editable column on this tab, in Edit
                                  mode. Shares editableProducts with the Product tab's Unit
                                  Cost box, so a change here shows up there (and is saved) as-is. */}
                              <td style={styles.erpTd}>
                                {isFieldVisible("product_cost") ? (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px', minWidth: '90px' }}
                                    value={editableProducts[idx]?.product_cost ?? row.product_cost ?? ""}
                                    onChange={(e) => handleProductFieldChange(idx, "product_cost", e.target.value)}
                                    onBlur={() => handleProductCostBlur(idx)}
                                    disabled={!isEditMode}
                                  />
                                ) : (
                                  <MaskedValue height={24} />
                                )}
                              </td>

                              {/* Disc (%) */}
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                {isFieldVisible("discount") ? (discounts[idx] || "") : <MaskedValue height={20} />}
                              </td>

                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("total_cost") ? formatCurrency(calc.grossTotal) : <MaskedValue height={20} />}</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("net_price") ? formatCurrency(calc.netPrice) : <MaskedValue height={20} />}</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("cgst_percent") ? formatCurrency(calc.cgstAmt) : <MaskedValue height={20} />}</td>
                              {gstColumnLabels.col2 && (
                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("sgst_percent") ? formatCurrency(calc.sgstAmt) : <MaskedValue height={20} />}</td>
                              )}
                              {gstColumnLabels.col3 && (
                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("igst_percent") ? formatCurrency(calc.igstAmt) : <MaskedValue height={20} />}</td>
                              )}
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("grand_total") ? formatCurrency(calc.finalPrice) : <MaskedValue height={20} />}</td>

                              {/* Delivery Date */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {!isFieldVisible("delivery_date") ? <MaskedValue height={20} /> : (
                                  <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    {formatDateDMY(editableProducts[idx]?.delivery_date || row.delivery_date) || "—"}
                                  </span>
                                )}
                              </td>

                              {poOrderType === "import" && (
                                <td style={styles.erpTd}>
                                  <span style={{ fontSize: '12px' }}>{importSupplierCountry || "—"}</span>
                                </td>
                              )}

                              <td style={styles.erpTd}>
                                {statusLabel}
                              </td>
                            </tr>
                          );
                        })}

                        <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>
                          <td colSpan={13} style={{ ...styles.erpTd, textAlign: 'right' }}>TOTALS:</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.gross)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.net)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.cgst)}</td>
                          {gstColumnLabels.col2 && <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.sgst)}</td>}
                          {gstColumnLabels.col3 && <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.igst)}</td>}
                          <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px', whiteSpace: 'nowrap' }}>{currencySymbol} {formatCurrency(totals.grand)}</td>
                          <td style={styles.erpTd}></td>
                          {poOrderType === "import" && <td style={styles.erpTd}></td>}
                          <td style={styles.erpTd}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ================= CONFIRMATION TAB ================= */}
              {createActiveTab === "confirmation" && (() => {
                const overallStatus = (modalData?.[0]?.status || "").toUpperCase();
                const isApproved = overallStatus === "APPROVED" || overallStatus === "PARTIALLY APPROVED";
                return (
                  <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                      {isApproved && (
                        <button className="btn btn-dark btn-sm" onClick={handleSaveConfirmation}>
                          Save Confirmation
                        </button>
                      )}
                    </div>
                    <table className="table" style={styles.erpTable}>
                      <thead>
                        <tr>
                          <th style={styles.erpTh}>Item</th>
                          <th style={styles.erpTh}>Schedule Line</th>
                          <th style={styles.erpTh}>Product</th>
                          <th style={styles.erpTh}>Qty</th>
                          <th style={styles.erpTh}>Delivery Date</th>
                          <th style={styles.erpTh}>Confirm Qty</th>
                          <th style={styles.erpTh}>Confirm Date</th>
                          <th style={styles.erpTh}>Deliver Qty</th>
                          <th style={styles.erpTh}>Deliver Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalData
                          .map((row, originalIdx) => ({ row, originalIdx }))
                          .filter(({ row }) => String(row.review || "").toUpperCase() === "APPROVED")
                          .map(({ row, originalIdx: idx }) => {
                          const rowKey = row.id || `idx_${idx}`;
                          const lines = confirmationLines[rowKey] || [{ schedule_line: "", confirm_qty: "", confirm_date: "", deliver_qty: "", deliver_date: "" }];
                          const totalConfirmedAllLines = lines.reduce((sum, l) => sum + (parseFloat(l.confirm_qty) || 0), 0);
                          const remainingQtyAllLines = (parseFloat(row.requirement_qty) || 0) - totalConfirmedAllLines;
                          const canAddMoreLines = remainingQtyAllLines > 0;
                          return lines.map((line, lineIdx) => (
                            <tr key={`${idx}-${lineIdx}`}>
                              <td style={styles.erpTd}>{lineIdx === 0 ? idx + 1 : ""}</td>
                              {/* Schedule Line - auto-numbered, not manually editable */}
                              <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                {lineIdx + 1}
                              </td>
                              <td style={styles.erpTd}>{lineIdx === 0 ? row.product_code || "" : ""}</td>
                              {/* Qty: original qty shown bold on line 1; subsequent schedule lines show
                                  the remaining unconfirmed balance below, prefixed with "-" */}
                              <td style={styles.erpTd}>
                                {lineIdx === 0 ? (
                                  <span style={{ fontWeight: "bold" }}>{row.requirement_qty || ""}</span>
                                ) : (
                                  (() => {
                                    const originalQty = parseFloat(row.requirement_qty) || 0;
                                    const confirmedSoFar = lines
                                      .slice(0, lineIdx)
                                      .reduce((sum, l) => sum + (parseFloat(l.confirm_qty) || 0), 0);
                                    const balanceQty = originalQty - confirmedSoFar;
                                    return (
                                      <span style={{ color: "#dc2626" }}>
                                        - {balanceQty.toFixed(4)}
                                      </span>
                                    );
                                  })()
                                )}
                              </td>
                              <td style={styles.erpTd}>{lineIdx === 0 ? formatDateDMY(row.delivery_date) || "" : ""}</td>
                              {/* Confirm Qty */}
                              <td style={styles.erpTd}>
                                <input
                                  className="form-control form-control-sm"
                                  style={{ fontSize: "12px", width: "80px" }}
                                  value={line.confirm_qty || ""}
                                  disabled={!isApproved}
                                  onChange={(e) => {
                                    const updated = { ...confirmationLines };
                                    updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, confirm_qty: e.target.value } : l);
                                    setConfirmationLines(updated);
                                  }}
                                />
                              </td>
                              {/* Confirm Date */}
                              <td style={styles.erpTd}>
                                <DateInputDDMMYYYY
                                  className="form-control form-control-sm"
                                  style={{ fontSize: "12px" }}
                                  value={line.confirm_date || ""}
                                  disabled={!isApproved}
                                  onChange={(val) => {
                                    const updated = { ...confirmationLines };
                                    updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, confirm_date: val } : l);
                                    setConfirmationLines(updated);
                                  }}
                                />
                              </td>
                              {/* Deliver Qty */}
                              <td style={styles.erpTd}>
                                <input
                                  className="form-control form-control-sm"
                                  style={{ fontSize: "12px", width: "80px" }}
                                  value={line.deliver_qty || ""}
                                  disabled={!isApproved}
                                  onChange={(e) => {
                                    const updated = { ...confirmationLines };
                                    updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, deliver_qty: e.target.value } : l);
                                    setConfirmationLines(updated);
                                  }}
                                />
                              </td>
                              {/* Deliver Date + + button */}
                              <td style={{ ...styles.erpTd, whiteSpace: "nowrap" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <DateInputDDMMYYYY
                                    className="form-control form-control-sm"
                                    style={{ fontSize: "12px" }}
                                    value={line.deliver_date || ""}
                                    disabled={!isApproved}
                                    onChange={(val) => {
                                      const updated = { ...confirmationLines };
                                      updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, deliver_date: val } : l);
                                      setConfirmationLines(updated);
                                    }}
                                  />
                                  {isApproved && lineIdx === lines.length - 1 && canAddMoreLines && (
                                    <button
                                      className="btn btn-dark btn-sm"
                                      style={{ padding: "0 8px", fontSize: 16, lineHeight: "24px" }}
                                      onClick={() => {
                                        const updated = { ...confirmationLines };
                                        updated[rowKey] = [...lines, { schedule_line: String(lines.length + 1), confirm_qty: "", confirm_date: "", deliver_qty: "", deliver_date: "" }];
                                        setConfirmationLines(updated);
                                      }}
                                    >+</button>
                                  )}
                                  {isApproved && lineIdx > 0 && (
                                    <button
                                      className="btn btn-outline-danger btn-sm"
                                      style={{ fontSize: 12 }}
                                      onClick={() => {
                                        const updated = { ...confirmationLines };
                                        updated[rowKey] = lines
                                          .filter((_, i) => i !== lineIdx)
                                          .map((l, i) => ({ ...l, schedule_line: String(i + 1) }));
                                        setConfirmationLines(updated);
                                      }}
                                    >✕</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              

              {createActiveTab === "docs" && (
                <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>

                  {/* ── PO DOCUMENT CARD (RFQ style) ── */}
                  {isFieldVisible("purchase_order_pdf") && modalData?.[0]?.purchase_order_id && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        padding: "12px 16px",
                        background: "#fff",
                      }}>

                        {/* Filename on top */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "7px",
                          fontWeight: 600,
                          fontSize: 13,
                          color: "#1a1a2e",
                          marginBottom: "10px",
                        }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          {modalData[0].purchase_order_id}_PurchaseOrder.pdf
                        </div>

                        {/* View / Download / Print buttons below filename */}
                        <div style={{ display: "flex", gap: "8px" }}>

                          {/* View — opens popup */}
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() => setShowPODocInModal(true)}
                          >
                            View
                          </button>

                          {/* Download */}
                          <button
                            className="btn btn-dark btn-sm"
                            disabled={!(modalData?.length > 0 && modalData.every((r) => String(r.review || "").toUpperCase() === "APPROVED"))}
                            title="Available after approval"
                            onClick={() => {
                              // Reuses the exact-view download from PODocsView.js
                              // (same page the View/Print buttons already use)
                              // instead of the old DB-rebuilt PDF, so this always
                              // matches what's on screen.
                              window.open(
                                `/po-docs/${modalData[0].purchase_order_id}?autoDownload=1`,
                                "_blank"
                              );
                            }}
                          >
                            Download
                          </button>

                          {/* Print */}
                          <button
                            className="btn btn-outline-dark btn-sm"
                            disabled={!(modalData?.length > 0 && modalData.every((r) => String(r.review || "").toUpperCase() === "APPROVED"))}
                            title="Available after approval"
                            onClick={() => {
                              // Let /po-docs load its own data and trigger
                              // window.print() itself once poData is ready
                              // (see the autoPrint effect in PODocsView.js).
                              // Calling printWindow.print() from here in
                              // onload used to fire before the async PO
                              // fetch resolved, printing an empty (0-row)
                              // page.
                              window.open(
                                `/po-docs/${modalData[0].purchase_order_id}?autoPrint=1`,
                                "_blank"
                              );
                            }}
                          >
                            Print
                          </button>

                        </div>
                      </div>
                    </div>
                  )}

                  {/* File upload — same styled drop zone as Purchase Request's Docs tab */}
                  {isFieldVisible("uploaded_files") && (
                  <div style={{ marginBottom: 16 }}>
                    <label
                      htmlFor="poDocFileInput"
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        justifyContent: "center", border: "2px dashed #cbd5e1", borderRadius: 8,
                        padding: "18px 12px", cursor: "pointer",
                        background: "#f8fafc"
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <span style={{ fontSize: 22 }}>📁</span>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 500, marginTop: 4 }}>
                        {docsUploading ? "Uploading…" : "Click to attach documents"}
                      </span>
                      <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                        PDF, DOCX, XLSX (max {maxPoDocSizeKb} KB per file)
                      </span>
                      <input
                        id="poDocFileInput"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleDocUpload}
                        disabled={docsUploading}
                      />
                    </label>
                  </div>
                  )}

                  {/* Uploaded files list — pulled from the server (Google Drive-backed) */}
                  {isFieldVisible("uploaded_files") && (
                  <div>
                    {uploadedDocs.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "20px 0", color: "#94a3b8", fontSize: 13 }}>
                        No documents attached yet.
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={styles.erpTh}>#</th>
                            <th style={styles.erpTh}>File Name</th>
                            <th style={styles.erpTh}>Size</th>
                            <th style={styles.erpTh}>Uploaded At</th>
                            <th style={styles.erpTh}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadedDocs.map((doc, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <td style={styles.erpTd}>{idx + 1}</td>
                              <td style={styles.erpTd}>
                                <span style={{ color: "#1e293b", fontWeight: 500 }}>{doc.originalname || doc.filename}</span>
                              </td>
                              <td style={styles.erpTd}>
                                {doc.size
                                  ? doc.size > 1024 * 1024
                                    ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB`
                                    : `${(doc.size / 1024).toFixed(1)} KB`
                                  : '—'}
                              </td>
                              <td style={styles.erpTd}>{doc.uploadedAt ? formatChangeLogDateTime(doc.uploadedAt) : '—'}</td>
                              <td style={styles.erpTd}>
                                <button
                                  type="button"
                                  onClick={() => handleDocDownload(doc)}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    color: '#2563eb', fontWeight: 600, fontSize: 12,
                                    textDecoration: 'none', padding: '3px 10px',
                                    border: '1px solid #bfdbfe', borderRadius: 5,
                                    background: '#eff6ff', cursor: 'pointer'
                                  }}
                                >
                                  ⬇ Download
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  )}

                </div>
              )}

                  {/* ── VIEW POPUP MODAL — iframe loads /po-docs/:poId ── */}
                  {showPODocInModal && modalData?.[0]?.purchase_order_id && (
                    <div style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "rgba(0,0,0,0.55)",
                      zIndex: 99999,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}>
                      <div style={{
                        width: "90%",
                        height: "90%",
                        background: "#fff",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
                      }}>

                        {/* Modal header */}
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 20px",
                          borderBottom: "1px solid #e2e8f0",
                          background: "#f8fafc",
                          flexShrink: 0,
                        }}>
                          <span style={{
                            fontWeight: 600,
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            color: "#1a1a2e",
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            {modalData[0].purchase_order_id}_PurchaseOrder.pdf
                          </span>

                          <div style={{ display: "flex", gap: 8 }}>





                            {/* Close */}
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setShowPODocInModal(false)}
                            >
                              ✕ Close
                            </button>

                          </div>
                        </div>

                        {/* iframe loads /po-docs/:poId */}
                        <iframe
                          id="po-doc-iframe"
                          src={`/po-docs/${modalData[0].purchase_order_id}`}
                          title="PO Document"
                          style={{
                            flex: 1,
                            width: "100%",
                            border: "none",
                            background: "#fff",
                          }}
                        />

                      </div>
                    </div>
                  )}


              {/* ================= CHANGE LOG TAB ================= */}
              {createActiveTab === "change_log" && (
                <div style={styles.tableContainer}>
                  <table className="table" style={styles.erpTable}>
                    <thead>
                      <tr>
                        <th style={styles.erpTh}>Date & Time</th>
                        <th style={styles.erpTh}>User</th>
                        <th style={styles.erpTh}>Action</th>
                        <th style={styles.erpTh}>Field</th>
                        <th style={styles.erpTh}>Old Value</th>
                        <th style={styles.erpTh}>New Value</th>
                        <th style={styles.erpTh}>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeLogs.length > 0 ? (
                        changeLogs.map((log, idx) => {
                          const dateVal = log.created_at || log.updated_at;
                          return (
                            <tr key={idx}>
                              <td style={styles.erpTd}>
                                {formatChangeLogDateTime(dateVal)}
                              </td>
                              <td style={styles.erpTd}>{log.created_by || log.updated_by || "System"}</td>
                              <td style={styles.erpTd}>{log.action_type || "Update"}</td>
                              <td style={styles.erpTd}>
                                {log.field_name
                                  ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                  : '-'}
                              </td>
                              <td style={{ ...styles.erpTd, maxWidth: "150px" }}>
                                {log.old_value || "-"}
                              </td>
                              <td style={{ ...styles.erpTd, maxWidth: "150px" }}>
                                {log.new_value || "-"}
                              </td>
                              <td style={{ ...styles.erpTd, maxWidth: "200px", wordWrap: "break-word" }}>
                                {log.comment || "-"}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          {/* Updated colSpan to 7 to match new column count */}
                          <td colSpan="7" style={{ ...styles.erpTd, textAlign: "center", padding: 20 }}>
                            No changes recorded
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {createActiveTab === "payment_history" && (
                <div style={styles.tableContainer}>

                  <div style={{ position: 'relative', background: '#f8fafc', padding: '16px', paddingTop: '44px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>

                    {(() => {
                      const _totalPaid = advancePayments.reduce((sum, p) => {
                        if (editingPaymentId && p.id === editingPaymentId) return sum;
                        return sum + Number(p.advance || p.amount || 0);
                      }, 0);
                      const _remaining = approvedTotals.grand - _totalPaid;

                      // 🛠️ FIX: approvedTotals.grand is 0 whenever no line item
                      // has been marked APPROVED yet. Previously "_remaining <= 0"
                      // was true in that case too (0 - 0 = 0), so the button showed
                      // "✓ Fully Paid" even though nothing was approved or paid.
                      // We now distinguish "nothing to pay because nothing is
                      // approved" from "fully paid" so the label isn't misleading.
                      const _hasApprovedTotal = approvedTotals.grand > 0;
                      const _isFullyPaid = _hasApprovedTotal && _remaining <= 0;
                      const _disabled = _remaining <= 0; // still disable in both cases

                      let _buttonLabel = '+ Add Payment';
                      if (editingPaymentId) {
                        _buttonLabel = '✓ Update Payment';
                      } else if (_isFullyPaid) {
                        _buttonLabel = '✓ Fully Paid';
                      } else if (!_hasApprovedTotal) {
                        _buttonLabel = 'No Approved Products';
                      }

                      return (
                        <div className="d-flex" style={{ gap: '6px', position: 'absolute', top: '10px', right: '16px' }}>
                          {editingPaymentId && (
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              style={{ height: '32px', whiteSpace: 'nowrap' }}
                              onClick={handleCancelEditPayment}
                              type="button"
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            className="btn btn-dark btn-sm"
                            style={{ height: '32px', whiteSpace: 'nowrap', opacity: _disabled ? 0.5 : 1, cursor: _disabled ? 'not-allowed' : 'pointer' }}
                            onClick={handleAddPaymentRow}
                            disabled={_disabled}
                          >
                            {_buttonLabel}
                          </button>
                        </div>
                      );
                    })()}

                    <div className="row g-3 align-items-start">
                      <div className="col-md">
                        <label style={styles.compactLabel}>Payment Type</label>
                        <select
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabType}
                          onChange={(e) => setPaymentHistoryTabType(e.target.value)}
                        >
                          <option value="Advance">Advance</option>
                          <option value="Paid">Paid</option>
                        </select>
                        {/* 🆕 Shows the total of only APPROVED product lines, matching Sales Order */}
                        <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>
                          Total Price: <strong>{currencySymbol}{formatCurrency(approvedTotals.grand)}</strong>
                        </div>
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Date</label>
                        <DateInputDDMMYYYY
                          style={styles.compactInput}
                          value={paymentHistoryTabDate}
                          onChange={(val) => setPaymentHistoryTabDate(val)}
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Amount ({currencySymbol})</label>
                        <input
                          type="number"
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabAmount}
                          onChange={(e) => setPaymentHistoryTabAmount(e.target.value)}
                          placeholder="0.00"
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Payment Description</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabDescription}
                          onChange={(e) => setPaymentHistoryTabDescription(e.target.value)}
                          placeholder="e.g. NEFT advance against PO"
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Reference Number</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabReference}
                          onChange={(e) => setPaymentHistoryTabReference(e.target.value)}
                          placeholder="e.g. UTR / Cheque No."
                        />
                      </div>
                    </div>
                  </div>

                  <table className="table mb-0" style={styles.erpTable}>
                    <thead>
                      <tr>
                        <th style={styles.erpTh}>Date</th>
                        <th style={styles.erpTh}>Payment Type</th>
                        <th style={styles.erpTh}>Payment Description</th>
                        <th style={styles.erpTh}>Reference Number</th>
                        <th style={{ ...styles.erpTh, textAlign: 'right' }}>Amount</th>
                        <th style={{ ...styles.erpTh, textAlign: 'right' }}>Balance Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advancePayments.length > 0 ? (
                        advancePayments.map((row, idx) => (
                          <tr
                            key={row.id || idx}
                            style={editingPaymentId === row.id ? { background: '#fff8e1' } : undefined}
                          >
                            <td style={styles.erpTd}>
                              {formatDateDMY(row.payment_date)}
                            </td>
                            <td style={styles.erpTd}>
                              {row.payment_type || row.paymentType || "Advance"}
                            </td>
                            <td style={styles.erpTd}>
                              {row.payment_description || row.paymentDescription || "-"}
                            </td>
                            <td style={styles.erpTd}>
                              {row.reference_number || row.referenceNumber || "-"}
                            </td>
                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                              {formatCurrency(row.advance || row.amount)}
                            </td>
                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 600 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                {formatCurrency(row.pending)}
                                <button
                                  type="button"
                                  onClick={() => handleEditPaymentClick(row)}
                                  title="Edit payment"
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    padding: 0,
                                    lineHeight: 1,
                                    fontSize: '14px',
                                    color: editingPaymentId === row.id ? '#b8860b' : '#6c757d',
                                  }}
                                >
                                  ✎
                                </button>
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted p-3">
                            No payments recorded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ================= ACKNOWLEDGEMENT TAB ================= */}
              {createActiveTab === "acknowledgement" && (
                <div style={styles.tableContainer}>

                  <div style={{ position: 'relative', background: '#f8fafc', padding: '16px', paddingTop: '44px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>

                    <div className="d-flex" style={{ gap: '6px', position: 'absolute', top: '10px', right: '16px' }}>
                      {editingAckId && (
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          style={{ height: '32px', whiteSpace: 'nowrap' }}
                          onClick={handleCancelEditAcknowledgement}
                          type="button"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        className="btn btn-dark btn-sm"
                        style={{ height: '32px', whiteSpace: 'nowrap' }}
                        onClick={handleSaveAcknowledgement}
                      >
                        {editingAckId ? '✓ Update Acknowledgement' : 'Save Acknowledgement'}
                      </button>
                    </div>

                    <div className="row g-3 align-items-start">
                      <div className="col-md">
                        <label style={styles.compactLabel}>Acknowledgement No</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={ackNoInput}
                          onChange={(e) => setAckNoInput(e.target.value)}
                          placeholder="e.g. ACK-00123"
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Date</label>
                        <DateInputDDMMYYYY
                          style={styles.compactInput}
                          value={ackDateInput}
                          onChange={(val) => setAckDateInput(val)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* ================= FOOTER ================= */}
            <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
              {/* Short Close — for Pending for Approval and Approved, not already short closed */}
              {modalData?.[0]?.purchase_order_id && (() => {
                const s = (modalData?.[0]?.status || "").toUpperCase().trim();
                const ps = (modalData?.[0]?.po_status || "").toUpperCase().trim();
                return (ps !== "SHORT_CLOSED" && (s === "APPROVED" || s === "PENDING FOR APPROVAL")) ? (
                  <button className="btn btn-outline-danger btn-sm" onClick={handleShortClose}>
                    Short Close
                  </button>
                ) : null;
              })()}

              {/* Short Close confirmation — in-app dialog, replaces window.confirm */}
              {showShortCloseConfirm && (
                <div style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,0.55)",
                  zIndex: 100001,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <div style={{
                    width: 380,
                    maxWidth: "90%",
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "16px 20px",
                      borderBottom: "1px solid #f1c9c9",
                      background: "#fef2f2",
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <span style={{ fontWeight: 600, fontSize: 15, color: "#7f1d1d" }}>
                        Short Close Purchase Order
                      </span>
                    </div>

                    <div style={{ padding: "18px 20px", fontSize: 13.5, color: "#334155", lineHeight: 1.5 }}>
                      Are you sure you want to Short Close <b>{modalData?.[0]?.purchase_order_id}</b>?
                      It will not proceed to GRN.
                    </div>

                    <div style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      padding: "12px 20px",
                      borderTop: "1px solid #e2e8f0",
                      background: "#f8fafc",
                    }}>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowShortCloseConfirm(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={confirmShortClose}
                      >
                        Short Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalData?.[0]?.purchase_order_id ? (
                (() => {
                  const rawStatus = modalData?.[0]?.status || modalData?.[0]?.po_status || "";
                  const poStatus = rawStatus.toUpperCase().trim();

                  // CREATED = converted from a PR but not yet submitted.
                  // Not reviewable until Submit moves it to
                  // "Pending for Approval" (see submitPurchaseOrder).
                  if (poStatus === "CREATED") {
                    // CREATED PO: fields open directly editable (see
                    // setIsEditMode(poStatus === "CREATED") on load) — no
                    // separate Edit/Cancel step. Submit saves whatever was
                    // just typed/selected (Basic tab, products, etc.) and
                    // then moves the PO to Pending for Approval.
                    return (
                      <button
                        className="btn btn-dark btn-sm"
                        disabled={isSubmittingPO}
                        onClick={async () => {
                          if (isSubmittingPO) return; // guard against double-click double-submit
                          setIsSubmittingPO(true);
                          try {
                            await handleSave(() => handleSubmitPO());
                          } finally {
                            setIsSubmittingPO(false);
                          }
                        }}
                        title={!selectedVendor ? "Assign supplier to save PO" : ""}
                      >
                        {isSubmittingPO ? "Submitting..." : "Submit"}
                      </button>
                    );
                  }

                  // PENDING FOR APPROVAL = submitted, waiting for review
                  if (poStatus === "PENDING FOR APPROVAL") {
                    return isEditMode ? (
                      <>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => setIsEditMode(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={handleSave}
                          title={!selectedVendor ? "Assign supplier to save PO" : ""}
                        >
                          Save
                        </button>
                      </>
                    ) : (
                      canEditPO && (
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() => setIsEditMode(true)}
                        >
                          Edit
                        </button>
                      )
                    );
                  }

                  // APPROVED / REJECTED / others — show Edit as before
                  return isEditMode ? (
                    <>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setIsEditMode(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={handleSave}
                        title={!selectedVendor ? "Assign supplier to save PO" : ""}
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    canEditPO && (
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={() => {
                          const currentStatus = modalData?.[0]?.status || "";
                          if (currentStatus === "APPROVED" || currentStatus === "Approved") {
                            toast.info("Note: Editing an Approved PO will reset its status to 'Pending for Approval' for re-review.");
                          }
                          setIsEditMode(true);
                        }}
                      >
                        Edit
                      </button>
                    )
                  );
                })()
              ) : (
                /* New (create) PO: always show Save */
                <button
                  className="btn btn-dark btn-sm"
                  onClick={handleSave}
                  title={!selectedVendor ? "Assign supplier to save PO" : ""}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showProductModal && productDetails && (
        <div style={styles.modalBackdrop}>
          <div
            style={{
              ...styles.modal,
              maxWidth: "950px",
              width: "95%",
              maxHeight: "85vh",
              padding: "14px",
              fontSize: "13px"
            }}
          >


            {/* ================= BODY ================= */}
            <div style={{ padding: "12px" }}>

              {/* ===== TOP ROW (LIKE PIC) ===== */}
              <table className="table table-bordered table-sm mb-3">
                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                  <tr>
                    <th>{String(poProductType).trim().toLowerCase() === "asset" ? "Asset Code" : "Product Code"}</th>
                    <th>HSN Code</th>
                    <th>Quantity</th>
                    <th>Unit Cost</th>
                    <th>Discount (%)</th>
                    <th>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{productDetails.product_code}</td>
                    <td>{productDetails.hsnCode}</td>
                    <td>{productDetails.requirement_qty}</td>
                    <td>{currencySymbol} {formatCurrency(productDetails.product_cost)}</td>
                    <td>{productDetails.discount}</td>
                    <td style={{ fontWeight: 600 }}>
                      {currencySymbol} {formatCurrency(productDetails.totalCost)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* ===== NET PRICE LINE ===== */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 10px",
                  borderTop: "1px solid #999",
                  borderBottom: "1px solid #999",
                  marginBottom: "10px",
                  fontWeight: 600
                }}
              >
                <span>Net Price :</span>
                <span>{currencySymbol} {formatCurrency(productDetails.netPriceAfterDiscount)}</span>
              </div>

              {/* ===== GST BREAKUP (dynamic — one row per actual tax type) ===== */}
              <table
                className="table table-sm"
                style={{ width: "50%", marginLeft: "auto" }}
              >
                <tbody>
                  {(productDetails.taxes && productDetails.taxes.length
                    ? productDetails.taxes
                    : [{ type: "CGST", percent: 0, value: 0 }]
                  ).map((t, i) => (
                    <tr key={i}>
                      <td>{t.type} @ {t.percent}%</td>
                      <td style={{ textAlign: "right" }}>
                        {currencySymbol} {formatCurrency(t.value)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                    <td>Total GST</td>
                    <td style={{ textAlign: "right" }}>
                      {currencySymbol} {formatCurrency(productDetails.totalGST)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* ===== GRAND TOTAL ===== */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "12px",
                  marginTop: "8px",
                  background: "#f1f5f9",
                  fontWeight: 700,
                  fontSize: "15px",
                  borderRadius: "4px"
                }}
              >
                <span>Grand Total :</span>
                <span>{currencySymbol} {formatCurrency(productDetails.finalPriceAfterGST)}</span>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowProductModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showPaymentModal && (
        <div style={styles.modalBackdrop}>
          <div
            style={{
              ...styles.modal,
              maxWidth: "700px"
            }}
          >

            <div style={styles.modalHeader}>
              <strong>Payment Terms</strong>

              <button
                style={styles.iconBtn}
                onClick={() => setShowPaymentModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>

              <textarea
                className="form-control"
                rows={6}
                value={paymentTerms}
                readOnly
              />

            </div>

            <div style={styles.modalFooter}>

              <button
                className="btn btn-dark"
                onClick={() => setShowPaymentModal(false)}
              >
                Close
              </button>

            </div>

          </div>
        </div>
      )}
      {/* Terms & Conditions modal — edits local state only; persisted to
          the terms_and_conditions column via the main PO Save button,
          same as every other Basic-tab field. */}
      {showTermsModal && (
        <div style={styles.modalBackdrop}>
          <div
            style={{
              ...styles.modal,
              maxWidth: "700px"
            }}
          >

            <div style={styles.modalHeader}>
              <strong>Terms &amp; Conditions</strong>

              <button
                style={styles.iconBtn}
                onClick={() => setShowTermsModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>

              <textarea
                className="form-control"
                rows={12}
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
              />

            </div>

            <div style={styles.modalFooter}>

              <button
                className="btn btn-secondary"
                onClick={() => setShowTermsModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-dark"
                onClick={() => setShowTermsModal(false)}
              >
                Save
              </button>

            </div>

          </div>
        </div>
      )}
      {/* ================= CHARACTERISTICS MODAL ================= */}
      <CharacteristicsModal
        modalVisible={modalVisible}
        selectedCharacteristics={selectedCharacteristics}
        handleClose={handleSaveCharModal}
        isEditing={isEditMode}
        handleChange={onModalCharChange}
      />

      {/* ================= COMMENT MODAL ================= */}
      {commentModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: "500px" }}>
            <div style={styles.modalHeader}>
              <strong>{actionType} Purchase Order</strong>

              <button
                style={styles.iconBtn}
                onClick={() => setCommentModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>

              {actionType === "APPROVED" && (
                <div className="row g-2 mb-3">



                </div>
              )}

              <label style={styles.compactLabel}>
                Comment / Remarks
              </label>

              <textarea
                className="form-control"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter your comment here"
              />

            </div>

            <div style={styles.modalFooter}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setCommentModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-dark btn-sm"
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Supplier conflict resolution modal — shown instead of the old
          toast.warning()/hard toast.error() block whenever products with
          different default (PIR) suppliers end up on the same PO. Lets the
          user pick one of the detected suppliers, or any other supplier
          entirely, and applies it to every product row already added. */}
      {vendorConflictModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0,0,0,0.55)",
          zIndex: 100002,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
          <div style={{
            width: 440,
            maxWidth: "92%",
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
            overflow: "hidden",
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "16px 20px",
              borderBottom: "1px solid #fde68a",
              background: "#fffbeb",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span style={{ fontWeight: 600, fontSize: 15, color: "#78350f" }}>
                Different Suppliers Detected
              </span>
            </div>

            <div style={{ padding: "18px 20px", fontSize: 13.5, color: "#334155", lineHeight: 1.5 }}>
              <p style={{ marginBottom: 14 }}>
                {vendorConflictModal.productCode
                  ? <>Product <b>{vendorConflictModal.productCode}</b> defaults to a different supplier than what's already on this Purchase Order.</>
                  : <>Products on this Purchase Order have different default suppliers.</>
                }{" "}A Purchase Order can only have one supplier — pick which one to use below.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {vendorConflictModal.options.map((opt) => (
                  <button
                    key={opt.code || opt.name}
                    className="btn btn-outline-dark btn-sm"
                    style={{ textAlign: "left" }}
                    onClick={() => resolveVendorConflict(opt.code, opt.name)}
                  >
                    Use "{opt.name}"
                  </button>
                ))}
              </div>

              <label style={styles.compactLabel}>Or choose a different supplier</label>
              <Select
                {...rsMenuProps}
                styles={{
                  ...rsCompact,
                  // 🔧 FIX: rsCompact.menu hard-codes position:"absolute", which
                  // fights menuPosition="fixed" above (used so the list escapes
                  // this modal's bounds). With both set, react-select computes
                  // the dropdown's coordinates as viewport-relative (fixed) but
                  // then renders it as page-relative (absolute) — so if the
                  // background page is scrolled down at all, the list appears
                  // shifted downward by the scroll offset, detached from the
                  // input. Leave position alone here so it stays whatever
                  // menuPosition="fixed" actually set it to.
                  menu: (base) => ({ ...base, zIndex: 100003, fontSize: "13px" }),
                  menuPortal: (base) => ({ ...base, zIndex: 100003 }),
                }}
                menuShouldScrollIntoView={false}
                isClearable
                placeholder="Select a supplier"
                options={vendors.map((v) => ({ value: v.id, label: `${v.name} - ${v.id}` }))}
                value={null}
                onChange={(opt) => {
                  if (!opt) return;
                  const v = vendors.find(vv => vv.id === opt.value);
                  resolveVendorConflict(opt.value, v?.name || opt.label);
                }}
              />
            </div>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              padding: "12px 20px",
              borderTop: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setVendorConflictModal(null)}
              >
                Decide Later
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />


    </Layout >
  );
};

const styles = {
  compactLabel: {
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "2px",
    color: "#334155"
  },
  compactInput: {
    fontSize: "14px",
    height: "32px",
    padding: "4px 8px"
  },
  compactTabBtn: {
    fontSize: "14px",
    padding: "4px 10px"
  }
  ,
  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },

  erpTable: {
    width: "100%",
    minWidth: "600px",
    tableLayout: "fixed",
    borderCollapse: "collapse"
  },

  erpTh: {
    background: "#f8fafc",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
    whiteSpace: "nowrap"
  },

  erpTd: {
    fontSize: 13,
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155",
    verticalAlign: "middle"
  }
  ,
  filterCard: {
    background: '#ffffff',
    borderRadius: 8,
    padding: 14,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    marginBottom: 18
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 4,
    color: '#334155'
  },
  filterSelect: {
    height: 30,
    fontSize: 12
  }
  ,
  pageTitleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: '10px',
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: 2
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#64748b'
  },
  primaryBtn: { paddingLeft: 14, paddingRight: 14 },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '20px 12px',
    overflowY: 'auto',
  },
  modal: {
    background: '#fff',
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    padding: '15px 20px',
    borderBottom: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
  },
  modalBody: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  modalFooter: {
    padding: '15px 20px',
    borderTop: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  tabs: {
    display: 'flex',
    gap: 8,
    margin: '14px 0',
  },
};

export default PurchaseOrder;